import React, { useEffect, useState, useRef, useCallback, Suspense, lazy, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Avatar, Box, Grid, Typography, FormControl, Select, MenuItem } from '@mui/material';
import Fade from "react-reveal/Fade";
import Button from "@mui/material/Button";
import CommunityPostSummary from './CommunityPostSummary';
import CommunityPostModal from './CommunityPostModal';
import CommunityActiveThreads from "./CommunityActiveThreads";
import CommunityGuidelines from "./CommunityGuidelines";
import CommunityMobileTabs from './CommunityMobileTabs';
import SnackbarAlert from '../../common/SnackbarAlert';
import JoinCommunity from "./JoinCommunity";
import CreatorContentCard from '../../common/CreatorContentCard';
import axios from "axios";
import { motion } from 'framer-motion';
import { ReactSortable } from 'react-sortablejs';
// Influencer Modals
import { FaCrown } from 'react-icons/fa6'
import { FaLock, FaUsers} from 'react-icons/fa'
import { IoClose } from 'react-icons/io5'
import { TbChevronUp, TbUsersPlus, TbShare, TbPencil, TbGripVertical, TbGripHorizontal, TbPlus, TbSparkles, TbRosette, TbHeartHandshake, TbSchool, TbPackage, TbSeeding, TbMessageCircleDollar, TbUsers, TbLink, TbMessageDots, TbCopy} from 'react-icons/tb'
import CommunityUpcomingEvents from './CommunityUpcomingEvents';
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import InfiniteScroll from "react-infinite-scroll-component";
import Modal from "@mui/material/Modal";
// import {BiSolidBank} from "react-icons/bi";
import { BiInfoCircle } from "react-icons/bi";
import LinearProgress from "@mui/material/LinearProgress";
import { decode } from 'html-entities';
import CommunityPreview from './CommunityPreview';
import { AnimatePresence } from 'framer-motion';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CommunityComposer from './CommunityComposer';
import Library from './Library';
import ShareEarnButton from '../../Affiliate/ShareEarnButton';
import getImageUrl from '../../../utils/getImageUrl';
import ContentFilters from '../InfluencerSubComponents/ContentFilters';
import SearchBarInfeed from '../../Navigation/SearchBarInfeed';
import Async from '../Async/Async';
import PaidDMs from '../PaidDms/PaidDMs';
import { usePreviewReadOnly } from '../PreviewReadOnlyContext';
import { formatDateTimeYMD } from '../../../i18n/getIntlLocale';
import goToBilling from '../../../utils/goToBilling';
import { getBackendUrl } from '../../../utils/getBackendUrl';
import { buildShortProductLink } from '../../../utils/shortProductLink';
import sanitizeDescriptionHtml from '../../../utils/sanitizeDescriptionHtml';

const TextEditor = lazy(() => import('../../common/TextEditor'));

const scroll = (id) => {
   const section = document.querySelector('#post' + id);
   section.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const paths = require('../../../paths.json');

const DarkTooltip = styled(({ className, ...props }) => (
   <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
   [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#222222 !important',
      color: '#ffffff',
      boxShadow: theme.shadows[1],
      fontSize: 11,
      margin: '0px'
   },
}));

const StyledPagination = styled(Pagination)(({ theme }) => ({
   "& .MuiPaginationItem-root": {
      fontWeight: 500,
      fontFamily: "Roboto",
      color: "black",
      backgroundColor: "var(--rebrandind-background-color)",
      borderRadius: "8px",
      "&:hover": {
         backgroundColor: "#e3e7fc",
         color: "var(--brand-color)",
      },
   },
   "& .Mui-selected": {
      backgroundColor: "#e3e7fc",
      color: "var(--brand-color)",
      borderRadius: "8px",
      fontWeight: 500,
      fontFamily: "Roboto",
   },
   "& .Mui-selected:hover": {
      backgroundColor: "#d7ddfc",
      color: "var(--brand-color)",
      borderRadius: "8px",
      fontWeight: 500,
      fontFamily: "Roboto",
   },
}));

const modalStyle = {
   display: 'flex',
   height: 'auto',
   // bgcolor: 'rgba(0,0,0,1)',
   minHeight: '100vh',
   minWidth: '100vw',
   overflowY: 'hidden',
   outline: 'none',
   zIndex: '9999',
};

const containerVariants = {
   hidden: { opacity: 0, y: 20 },
   visible: {
     opacity: 1,
     y: 0,
     transition: {
       duration: 0.6,
       staggerChildren: 0.1
     }
   }
 };

 const itemVariants = {
   hidden: { opacity: 0, y: 10 },
   visible: { opacity: 1, y: 0 }
 };

// Helper function to capitalize category names
const capitalizeCategory = (category) => {
   if (!category || category === 'all') return category;
   return category.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
   ).join(' ');
};

// Helper function to generate mock data for preview
const generateMockPreviewData = (influencerName, influencerIcon, displayName, t) => {
   const mockTags = [
      `👋 ${t('community.exampleTags.startHere')}`,
      `📣 ${t('community.exampleTags.announcements')}`,
      `🏆 ${t('community.exampleTags.wins')}`,
      `❓ ${t('community.exampleTags.qAndA')}`,
      `📚 ${t('community.exampleTags.resources')}`
   ];
   const mockPosts = [
      {
         post_id: 'mock-1',
         username: influencerName || 'influencer',
         display_name: displayName || influencerName || 'Community Creator',
         user_icon: influencerIcon || '',
         time: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
         subscription: null,
         text: t('community.examplePosts.post1'),
         content_type: 'img',
         file: '',
         tier: 0,
         n_threads: 3,
         n_comments: 8,
         like_count: 12,
         user_like_value: 0,
         threads: [],
         tags_list: [`📣 ${t('community.exampleTags.announcements')}`],
         link: '',
         pinned: true,
         community_post_title: '',
         event_time: '',
         is_example: true
      },
      {
         post_id: 'mock-2',
         username: influencerName || 'influencer',
         display_name: displayName || influencerName || 'Community Creator',
         user_icon: influencerIcon || '',
         time: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
         subscription: null,
         text: t('community.examplePosts.post2'),
         content_type: 'vid',
         file: '',
         tier: 0,
         n_threads: 2,
         n_comments: 5,
         like_count: 8,
         user_like_value: 0,
         threads: [],
         tags_list: [`🏆 ${t('community.exampleTags.wins')}`],
         link: '',
         pinned: false,
         community_post_title: '',
         event_time: '',
         is_example: true
      },
      {
         post_id: 'mock-3',
         username: influencerName || 'influencer',
         display_name: displayName || influencerName || 'Community Creator',
         user_icon: influencerIcon || '',
         time: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
         subscription: null,
         text: t('community.examplePosts.post3'),
         content_type: 'mzm',
         file: '',
         tier: 0,
         n_threads: 4,
         n_comments: 12,
         like_count: 15,
         user_like_value: 0,
         threads: [],
         tags_list: [`❓ ${t('community.exampleTags.qAndA')}`],
         link: 'https://zoom.us/j/example',
         pinned: false,
         community_post_title: t('community.examplePosts.event1Title'),
         event_time: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(),
         is_example: true
      },
      {
         post_id: 'mock-4',
         username: influencerName || 'influencer',
         display_name: displayName || influencerName || 'Community Creator',
         user_icon: influencerIcon || '',
         time: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
         subscription: null,
         text: t('community.examplePosts.post4'),
         content_type: 'aud',
         file: '',
         tier: 0,
         n_threads: 5,
         n_comments: 18,
         like_count: 22,
         user_like_value: 0,
         threads: [],
         tags_list: [`📚 ${t('community.exampleTags.resources')}`],
         link: '',
         pinned: false,
         community_post_title: '',
         event_time: '',
         is_example: true
      },
      {
         post_id: 'mock-5',
         username: influencerName || 'influencer',
         display_name: displayName || influencerName || 'Community Creator',
         user_icon: influencerIcon || '',
         time: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
         subscription: null,
         text: t('community.examplePosts.post5'),
         content_type: 'pdf',
         file: '',
         tier: 0,
         n_threads: 1,
         n_comments: 3,
         like_count: 6,
         user_like_value: 0,
         threads: [],
         tags_list: [`📚 ${t('community.exampleTags.resources')}`],
         link: '',
         pinned: false,
         community_post_title: '',
         event_time: '',
         is_example: true
      },
      {
         post_id: 'mock-6',
         username: influencerName || 'influencer',
         display_name: displayName || influencerName || 'Community Creator',
         user_icon: influencerIcon || '',
         time: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
         subscription: null,
         text: t('community.examplePosts.post6'),
         content_type: 'txt',
         file: '',
         tier: 0,
         n_threads: 7,
         n_comments: 25,
         like_count: 30,
         user_like_value: 0,
         threads: [],
         tags_list: [`📣 ${t('community.exampleTags.announcements')}`],
         link: '',
         pinned: false,
         community_post_title: '',
         event_time: '',
         is_example: true
      },
      {
         post_id: 'mock-7',
         username: influencerName || 'influencer',
         display_name: displayName || influencerName || 'Community Creator',
         user_icon: influencerIcon || '',
         time: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
         subscription: null,
         text: t('community.examplePosts.post7'),
         content_type: 'mgm',
         file: '',
         tier: 0,
         n_threads: 3,
         n_comments: 10,
         like_count: 14,
         user_like_value: 0,
         threads: [],
         tags_list: [`👋 ${t('community.exampleTags.startHere')}`],
         link: 'https://meet.google.com/example',
         pinned: false,
         community_post_title: t('community.examplePosts.event2Title'),
         event_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
         is_example: true
      },
      {
         post_id: 'mock-8',
         username: influencerName || 'influencer',
         display_name: displayName || influencerName || 'Community Creator',
         user_icon: influencerIcon || '',
         time: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
         subscription: null,
         text: t('community.examplePosts.post8'),
         content_type: 'img',
         file: '',
         tier: 0,
         n_threads: 2,
         n_comments: 7,
         like_count: 19,
         user_like_value: 0,
         threads: [],
         tags_list: [`❓ ${t('community.exampleTags.qAndA')}`],
         link: '',
         pinned: false,
         community_post_title: '',
         event_time: '',
         is_example: true
      }
   ];
   
   return { mockTags, mockPosts };
};

const CommunityDescription = ({ description }) => {
   const { t } = useTranslation();
   const [expanded, setExpanded] = useState(false);
   const maxLength = 120;
   const plainText = description?.replace(/<[^>]*>/g, '') || '';
   const needsTruncation = plainText.length > maxLength;

   if (!needsTruncation) {
      return (
         <div
            style={{ fontSize: '14px', color: '#6C757D', lineHeight: '1.5', margin: '0 0 16px 0', fontFamily: 'Roboto, sans-serif' }}
            dangerouslySetInnerHTML={{ __html: sanitizeDescriptionHtml(description) }}
         />
      );
   }

   if (expanded) {
      return (
         <div style={{ margin: '0 0 16px 0' }}>
            <div
               style={{ fontSize: '14px', color: '#6C757D', lineHeight: '1.5', margin: '0', fontFamily: 'Roboto, sans-serif' }}
               dangerouslySetInnerHTML={{ __html: sanitizeDescriptionHtml(description) }}
            />
            <span
               onClick={() => setExpanded(false)}
               style={{ fontSize: '13px', color: 'var(--brand-color)', cursor: 'pointer', fontWeight: '600', fontFamily: 'Roboto, sans-serif' }}
            >
               {t('community.library.hide', 'Hide')}
            </span>
         </div>
      );
   }

   const truncated = plainText.substring(0, maxLength) + '...';
   return (
      <div style={{ margin: '0 0 16px 0' }}>
         <p style={{ fontSize: '14px', color: '#6C757D', lineHeight: '1.5', margin: '0', fontFamily: 'Roboto, sans-serif' }}>
            {truncated}
         </p>
         <span
            onClick={() => setExpanded(true)}
            style={{ fontSize: '13px', color: 'var(--brand-color)', cursor: 'pointer', fontWeight: '600', fontFamily: 'Roboto, sans-serif' }}
         >
            {t('community.library.readMore', 'Read more')}
         </span>
      </div>
   );
};

function Community(props) {
   const { t } = useTranslation();
   const isCustomerPreview = props.previewRolloutOwner && props.isCustomerPreview;
   const previewReadOnly = usePreviewReadOnly();

   // A preview session simulates the member experience, so it must never write.
   // The guard sits on the network entry points rather than only on the
   // controls: an un-disabled button, a keyboard shortcut or a future call site
   // then still cannot post, like, comment, or email real community members.
   const blockPreviewWrite = () => {
      if (!previewReadOnly) return false;
      handleSnackbar(
         'var(--brand-color)',
         'info',
         t('elements.previewBar.readOnlyAction', 'This is a preview — nothing you do here is saved, and your customers will not see this message.')
      );
      return true;
   };

   // const placeholder = "Hi " + TruncateWithEllipsis(props.display_name, 16) + ", start typing to join the conversation....️";
   const placeholder = t('community.composer.placeholder');

   // Get brand color from designProperties
   const getBrandColor = () => {
      const customBrandColor = props.designProperties?.brandColorUpdated || props.designProperties?.brandColor;
      if (customBrandColor && customBrandColor !== '#FDB62B' && customBrandColor !== null && customBrandColor !== '') {
         return customBrandColor;
      }
      return 'var(--brand-color)';
   };

   const [isLoading, setIsloading] = useState(true);
   const [newPostText, setNewPostText] = useState('');
   const [newPostTextLength, setNewPostTextLength] = useState(0);
   const [postScheduleDate, setPostScheduleDate] = useState(null);
   const [postTier, setPostTier] = useState(null)
   const [postData, setPostData] = useState([]);
   const [nPosts, setNPosts] = useState(0);
   const [hasRealPosts, setHasRealPosts] = useState(false);
   const [submitPostIsLoading, setSubmitPostIsLoading] = useState(false)
   // Post
   const [isMediaAttached, setIsMediaAttached] = useState(false);
   const [selectedFile, setSelectedFile] = useState('');
   const [selectedPreviewUrl, setSelectedPreviewUrl] = useState('');
   const [fileType, setFileType] = useState('txt');
   const [showPostButton, setShowPostButton] = useState(true);
   // Thread
   const [isMediaAttachedThread, setIsMediaAttachedThread] = useState(false);
   const [selectedFileThread, setSelectedFileThread] = useState('');
   const [selectedPreviewUrlThread, setSelectedPreviewUrlThread] = useState('');
   const [fileTypeThread, setFileTypeThread] = useState('txt');
   const [showPostButtonThread, setShowPostButtonThread] = useState(true);
   const [selectedPreviewIdThread, setSelectedPreviewIdThread] = useState('');
   // Comment
   const [isMediaAttachedComment, setIsMediaAttachedComment] = useState(false);
   const [selectedFileComment, setSelectedFileComment] = useState('');
   const [selectedPreviewUrlComment, setSelectedPreviewUrlComment] = useState('');
   const [fileTypeComment, setFileTypeComment] = useState('txt');
   const [showPostButtonComment, setShowPostButtonComment] = useState(true);
   const [selectedPreviewIdComment, setSelectedPreviewIdComment] = useState('');
   // Edit
   const [isMediaAttachedEdit, setIsMediaAttachedEdit] = useState(false);
   const [selectedFileEdit, setSelectedFileEdit] = useState('');
   const [selectedPreviewUrlEdit, setSelectedPreviewUrlEdit] = useState('');
   const [fileTypeEdit, setFileTypeEdit] = useState('txt');
   const [postMeetingTime, setPostMeetingTime] = useState('');
   const [editCommentModal, setEditCommentModal] = useState(false);

   // const [mobileTabsSelected, setMobileTabsSelected] = React.useState('timeline');
   const [selectedFilter, setSelectedFilter] = React.useState('posts');

   const [initialLoaded, setInitialLoaded] = useState(false);
   const [scrollLoading, setScrollLoading] = useState(false);
   const [scrollHasMore, setScrollHasMore] = useState(true);
   const postsContainerRef = useRef(null);
   const scrollSentinelRef = useRef(null);
   const postDataRef = useRef(postData);
   
   // Keep ref in sync with postData
   useEffect(() => {
      postDataRef.current = postData;
   }, [postData]);

   // Error Handling
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMsg, setSnackbarMsg] = useState('')
   const [snackbarType, setSnackbarType] = useState('false')
   const [snackbarColor, setSnackbarColor] = useState('false');
   const [snackbarOpenClick, setSnackbarOpenClick] = useState(false);
   const [snackbarPreMsgClick, setSnackbarPreMsgClick] = useState('')
   const [snackbarPostMsgClick, setSnackbarPostMsgClick] = useState('')
   const [snackbarTextClick, setSnackbarTextClick] = useState('')
   const [snackbarColorClick, setSnackbarColorClick] = useState('var(--brand-color)')
   const [movedToPostId, setMovedToPostId] = useState(false);
   const [snackbarOpenMeeting, setSnackbarOpenMeeting] = useState(false);
   const [snackbarMsgMeeting, setSnackbarMsgMeeting] = useState('')
   const [snackbarColorMeeting, setSnackbarColorMeeting] = useState('var(--brand-color)')
   const [snackbarSeverityMeeting, setSnackbarSeverityMeeting] = useState('error');
   const [currencySymbol, setCurrencySymbol] = useState('£');
   const [snackbarEmbed, setSnackbarEmbed] = useState(false);
   const [snackbarMsgEmbed, setSnackbarMsgEmbed] = useState('')
   const [snackbarOpenDoc, setSnackbarOpenDoc] = useState(false);
   const [snackbarMsgDoc, setSnackbarMsgDoc] = useState('')
   const [resetCommentTextId, setResetCommentTextId] = useState(null);
   const [resetThreadTextId, setResetThreadTextId] = useState(null);
   const [resetPostEditTextId, setResetPostEditTextId] = useState(null);
   const [resetThreadEditTextId, setResetThreadEditTextId] = useState(null);
   const [resetCommentEditTextId, setResetCommentEditTextId] = useState(null);
   const { currentMonth, previousMonth } = getCurrentAndPreviousMonth();
   // console.log(postData)
   const [editingContentModal, setEditingContentModal] = useState(false);
   const [editingContentText, setEditingContentText] = useState(null);
   const [editingContentTextLength, setEditingContentTextLength] = useState(0);
   const [editingContentTextId, setEditingContentTextId] = useState(null);
   const [editingContentTextIndex, setEditingContentTextIndex] = useState(null);
   // upload vars
   const [timeRemaining, setTimeRemaining] = useState(null);
   const [uploadPercent, setUploadPercent] = useState(null);
   const [uploadMsg, setUploadMsg] = useState(null);
   const [uploadStep, setUploadStep] = useState(null);
   const [uploadSpeeds, setUploadSpeeds] = useState(null);
   const [isUploading, setIsUploading] = useState(false);
   const [fileSize, setFileSize] = useState(0);
   const [progressFileSize, setProgressFileSize] = useState(0);
   const [uploadError, setUploadError] = useState(false);
   const [snackbarSeverity, setSnackbarSeverity] = useState('error');
   const [snackbarSeverityClick, setSnackbarSeverityClick] = useState('error');
   // recording
   const mediaStream = useRef();
   const mediaRecorder = useRef();
   const mediaDevices = useRef();
   const [aDevID, setADevID] = useState("");
   const [vDevID, setVDevID] = useState("");
   const [recordingStatus, setRecordingStatus] = useState('stopped')
   const [recordingDurationMins, setRecordingDurationMins] = useState('00')
   const [recordingDurationSeconds, setRecordingDurationSeconds] = useState('00')
   const [changingDevice, setChangingDevice] = useState(false);
   const [communityContentType, setCommunityContentType] = useState('');
   const [videoInitialised, setVideoInitialised] = useState(true);
   const [localStreamInitialised, setLocalStreamInitialised] = useState(0);
   const [localisedStreamCounter, setLocalisedStreamCounter] = useState('');
   const localStream = useRef();
   const [recordingDevices, setRecordingDevices] = useState({
      audioin: null,
      audioout: null,
      videoin: null,
   });

   const audio_constraints = {
      audio: { autoplay: true, deviceId: aDevID, sampleRate: 44100 },
   };
   const video_constraints = {
      audio: { autoplay: true, deviceId: aDevID, sampleRate: 44100 },
      video: { autoplay: true, deviceId: vDevID }, height: { ideal: 1920 },
   };
   const [videoBitsPerSecond, setVideoBitsPerSecond] = useState();
   const [recordedFileFormat, setRecordedFileFormat] = useState('')
   const [downloadUrl, setDownloadUrl] = useState('')
   const [openCommentThreadId, setOpenCommentThreadId] = useState(-1)
   const [openCommentPostId, setOpenCommentPostId] = useState(-1)
   // meeting
   const [meetingModalOpen, setMeetingModalOpen] = useState(false);
   const [meetingType, setMeetingType] = useState('');
   const [meetingLink, setMeetingLink] = useState('');
   const [meetingTime, setMeetingTime] = useState('');
   const [meetingLinkThread, setMeetingLinkThread] = useState('');
   const [meetingTimeThread, setMeetingTimeThread] = useState('');
   const [meetingLinkComment, setMeetingLinkComment] = useState('');
   const [meetingTimeComment, setMeetingTimeComment] = useState('');
   const [meetingTimeEditPost, setMeetingTimeEditPost] = useState('');
   const [meetingTimeEditComment, setMeetingTimeEditComment] = useState('');
   const [meetingTimeEditThread, setMeetingTimeEditThread] = useState('');
   const [meetingLinkEdit, setMeetingLinkEdit] = useState('');
   const [meetingImageEdit, setMeetingImageEdit] = useState('');
   const [meetingImageFileNameEdit, setMeetingImageFileNameEdit] = useState('');
   const [updateEditPostData, setUpdateEditPostData] = useState(null);
   const [embedUrlKey, setEmbedUrlKey] = useState('');
   const [embedUrlType, setEmbedUrlType] = useState('');
   const [embedUrlIsOpen, setEmbedUrlIsOpen] = useState(false)
   const [embedVideoUrl, setEmbedVideoUrl] = useState('')
   const [embedVideoUrlThread, setEmbedVideoUrlThread] = useState('')
   const [embedUrlKeyThread, setEmbedUrlKeyThread] = useState('');
   const [embedVideoUrlComment, setEmbedVideoUrlComment] = useState('')
   const [embedUrlKeyComment, setEmbedUrlKeyComment] = useState('');
   const [embedVideoUrlEdit, setEmbedVideoUrlEdit] = useState('')
   const [embedUrlKeyEdit, setEmbedUrlKeyEdit] = useState('');
   const [docModalOpen, setDocModalOpen] = useState(false);
   const [docType, setDocType] = useState('');
   const [activeDocFileType, setActiveDocFileType] = useState('');
   const [docLink, setDocLink] = useState('');
   const [docLinkThread, setDocLinkThread] = useState('');
   const [docLinkComment, setDocLinkComment] = useState('');
   const [docLinkEdit, setDocLinkEdit] = useState('');
   const [replyIndexCurrent, setReplyIndexCurrent] = useState(null);

   const [uploadStartTime, setUploadStartTime] = useState(0);
   const networkCheckInterval = useRef()
   const progressIntervalRef = useRef()
   const [controller, setController] = useState('');
   const controllerRef = useRef()
   const [timeoutTimer, setTimeOutTimer] = useState('');
   const initInterval = useRef(false)
   const [uploadingTitle, setUploadingTitle] = useState('post...');
   const [decodedGoalsValue, setDecodedGoalsValue] = useState('');
   const [anchorEl, setAnchorEl] = useState(null);
   const [anchorElAudio, setAnchorElAudio] = useState(null);
   const [anchorElVideo, setAnchorElVideo] = useState(null);
   const [anchorElDoc, setAnchorElDoc] = useState(null);
   // Comment handlers
   const [anchorElCommentVideo, setAnchorElCommentVideo] = useState(null);
   const [anchorElComment, setAnchorElComment] = useState(null);
   const [anchorElCommentDoc, setAnchorElCommentDoc] = useState(null);
   const [anchorElCommentMeeting, setAnchorElCommentMeeting] = useState(null);
   // Thread handlers
   const [anchorElThreadVideo, setAnchorElThreadVideo] = useState(null);
   const [anchorElThread, setAnchorElThread] = useState(null);
   const [anchorElThreadDoc, setAnchorElThreadDoc] = useState(null);
   const [anchorElThreadMeeting, setAnchorElThreadMeeting] = useState(null);
   // Edit handlers
   const [anchorElEditVideo, setAnchorElEditVideo] = useState(null);
   const [anchorElEdit, setAnchorElEdit] = useState(null);
   const [anchorElEditDoc, setAnchorElEditDoc] = useState(null);
   const [anchorElEditMeeting, setAnchorElEditMeeting] = useState(null);
   const [rerenderKey, setRerenderKey] = useState(0);
   const [backend_url, setBackendUrl] = useState(getBackendUrl());
   const [tabs, setTabs] = useState([]);
   const [tabId, setTabId] = useState(null);
   const [tabName, setTabName] = useState('');
   // Path link (/slug/commc_7), so a link-preview bot gets this community's own card; hub link until a community is selected.
   const communityShareUrl = buildShortProductLink(paths.URL, props.influencer, 'commc', tabId) || `${paths.URL}${props.influencer}`;

   const [initialLoading, setInitialLoading] = useState(false);
   const [accessToCurrentTab, setAccessToCurrentTab] = useState(false);
   const [currentTabInfo, setCurrentTabInfo] = useState({});
  
   const [showPostsOrEvents, setShowPostsOrEvents] = useState('posts');
   const [editingTabLabel, setEditingTabLabel] = useState(null); // 'posts' or 'library' when editing
   const [editTabLabelValue, setEditTabLabelValue] = useState('');
   const [shareModalOpen, setShareModalOpen] = useState(false);
   const [shareLinkCopied, setShareLinkCopied] = useState(false);
   const [libraryProductView, setLibraryProductView] = useState(null);
   const [libraryProductLoading, setLibraryProductLoading] = useState(false);
   const [libraryCourseView, setLibraryCourseView] = useState(null);
   const [libraryCourseLoading, setLibraryCourseLoading] = useState(false);
  const [libraryMembershipView, setLibraryMembershipView] = useState(null);
  const [libraryMembershipLoading, setLibraryMembershipLoading] = useState(false);
  const [libraryMembershipTier, setLibraryMembershipTier] = useState(-1);
  const [libraryMembershipPageN, setLibraryMembershipPageN] = useState(1);
  const [libraryMembershipContentList, setLibraryMembershipContentList] = useState([]);
  const [libraryMembershipContentListLength, setLibraryMembershipContentListLength] = useState(0);
  const [libraryMembershipPagePosition, setLibraryMembershipPagePosition] = useState(0);
  const [libraryMembershipInfScroll, setLibraryMembershipInfScroll] = useState(true);
  const [libraryMembershipInfScrollLoading, setLibraryMembershipInfScrollLoading] = useState(false);
  const [libraryMembershipContentFilter, setLibraryMembershipContentFilter] = useState('all');
  const [libraryMembershipCategoryFilter, setLibraryMembershipCategoryFilter] = useState('all');
  const [libraryMembershipSortFilter, setLibraryMembershipSortFilter] = useState('latest');
  const [libraryMembershipSearchQuery, setLibraryMembershipSearchQuery] = useState('');
  const [libraryMembershipContentTypes, setLibraryMembershipContentTypes] = useState([]);
  const [libraryMembershipCategories, setLibraryMembershipCategories] = useState([]);
  const [libraryMembershipFilterUi, setLibraryMembershipFilterUi] = useState({
    anchorEl: null,
    content_menu_open: false,
    category_menu_open: false,
    tier_menu_open: false,
    sort_menu_open: false,
  });
  const [libraryCoachingView, setLibraryCoachingView] = useState(null);
  const [libraryCoachingLoading, setLibraryCoachingLoading] = useState(false);
  const [libraryCommunityView, setLibraryCommunityView] = useState(null);
  const [libraryMessagesView, setLibraryMessagesView] = useState(null);
  const [libraryCoachingTier, setLibraryCoachingTier] = useState(-1);
  const [libraryCoachingContentList, setLibraryCoachingContentList] = useState([]);
  const [libraryCoachingContentListLength, setLibraryCoachingContentListLength] = useState(0);
  const [libraryCoachingPagePosition, setLibraryCoachingPagePosition] = useState(0);
  const [libraryCoachingPageN, setLibraryCoachingPageN] = useState(1);
  const [libraryCoachingInfScroll, setLibraryCoachingInfScroll] = useState(true);
  const [libraryCoachingInfScrollLoading, setLibraryCoachingInfScrollLoading] = useState(false);
  const [libraryCoachingContentFilter, setLibraryCoachingContentFilter] = useState('all');
  const [libraryCoachingCategoryFilter, setLibraryCoachingCategoryFilter] = useState('all');
  const [libraryCoachingSortFilter, setLibraryCoachingSortFilter] = useState('latest');
  const [libraryCoachingSearchQuery, setLibraryCoachingSearchQuery] = useState('');
  const [libraryCoachingContentTypes, setLibraryCoachingContentTypes] = useState([]);
  const [libraryCoachingCategories, setLibraryCoachingCategories] = useState([]);
  const [libraryCoachingFilterUi, setLibraryCoachingFilterUi] = useState({
    anchorEl: null,
    content_menu_open: false,
    category_menu_open: false,
    sort_menu_open: false,
  });
   const [libraryCourseChapter, setLibraryCourseChapter] = useState(null);
   const [libraryCoursePageN, setLibraryCoursePageN] = useState(1);
   const [libraryCourseContentList, setLibraryCourseContentList] = useState([]);
   const [libraryCourseContentListLength, setLibraryCourseContentListLength] = useState(0);
   const [libraryCoursePagePosition, setLibraryCoursePagePosition] = useState(0);
   const [libraryCourseInfScroll, setLibraryCourseInfScroll] = useState(true);
   const [libraryCourseInfScrollLoading, setLibraryCourseInfScrollLoading] = useState(false);

   const openLibraryProduct = (itemId) => {
      setLibraryProductLoading(true);
      setLibraryProductView(null);
      setLibraryCourseView(null);
      setLibraryMembershipView(null);
      const form_data = new FormData();
      form_data.append('currency', props.currency || 'gbp');
      axios({
         method: 'post',
         url: getBackendUrl() + 'api/influencer/community/library_product/' + itemId,
         data: form_data,
         withCredentials: true,
      }).then(res => {
         if (res.data.content_item) {
            setLibraryProductView(res.data.content_item);
         }
         setLibraryProductLoading(false);
      }).catch(err => { setLibraryProductLoading(false); });
   };

   const loadLibraryCourseContent = (courseName, chapterName, pageN) => {
      // Map community library chapter names to the existing content API format
      const apiChapter = chapterName === '_uncategorised_' ? '_-_-other_-_-' : (chapterName || '');
      const form_data = new FormData();
      form_data.append('influencer_str', props.influencer);
      form_data.append('category', '');
      form_data.append('sort', 'latest');
      form_data.append('content_type', '');
      form_data.append('tab', 'paid_collections');
      form_data.append('collection', courseName);
      form_data.append('post_id', 'null');
      form_data.append('page_n', pageN);
      form_data.append('currency', props.currency || 'gbp');
      form_data.append('tier', -1);
      form_data.append('chapter', apiChapter);
      axios({
         method: 'post',
         url: getBackendUrl() + 'api/influencer_content_data_latest_v8',
         data: form_data,
         withCredentials: true,
      }).then(res => {
         const newList = res.data.content_list || [];
         setLibraryCourseContentList(newList);
         setLibraryCourseContentListLength(res.data.content_list_length || 0);
         setLibraryCoursePagePosition(newList.length);
         setLibraryCoursePageN(pageN);
         if (newList.length >= (res.data.content_list_length || 0) || newList.length >= 20) {
            setLibraryCourseInfScroll(false);
         } else {
            setLibraryCourseInfScroll(true);
         }
         if (res.data.course_chapters) {
            setLibraryCourseView(prev => prev ? { ...prev, course_chapters: res.data.course_chapters } : prev);
         }
         setLibraryCourseLoading(false);
      }).catch(err => { setLibraryCourseLoading(false); });
   };

   const handleLibraryCourseInfScroll = () => {
      if (libraryCourseInfScrollLoading) return;
      setLibraryCourseInfScrollLoading(true);
      const courseName = libraryCourseView?.course?.name || '';
      const apiChapter = libraryCourseChapter === '_uncategorised_' ? '_-_-other_-_-' : (libraryCourseChapter || '');
      const form_data = new FormData();
      form_data.append('influencer_str', props.influencer);
      form_data.append('category', '');
      form_data.append('content_type', '');
      form_data.append('start_index', libraryCoursePagePosition);
      form_data.append('sort', 'latest');
      form_data.append('tab', 'paid_collections');
      form_data.append('collection', courseName);
      form_data.append('page_n', libraryCoursePageN);
      form_data.append('currency', props.currency || 'gbp');
      form_data.append('tier', -1);
      form_data.append('chapter', apiChapter);
      form_data.append('post_id', 'null');
      axios({
         method: 'post',
         url: getBackendUrl() + 'api/influencer_content_data_inf_scroll_v6',
         data: form_data,
         withCredentials: true,
      }).then(res => {
         const newItems = res.data.content_list || [];
         const updatedList = [...libraryCourseContentList, ...newItems];
         setLibraryCourseContentList(updatedList);
         setLibraryCoursePagePosition(prev => prev + newItems.length);
         setLibraryCourseInfScrollLoading(false);
         if (newItems.length === 0 || updatedList.length >= libraryCourseContentListLength || updatedList.length >= 20) {
            setLibraryCourseInfScroll(false);
         }
      }).catch(() => { setLibraryCourseInfScrollLoading(false); });
   };

   const handleLibraryCoursePageChange = (pageN) => {
      const courseName = libraryCourseView?.course?.name || '';
      setLibraryCoursePageN(pageN);
      setLibraryCourseInfScroll(true);
      loadLibraryCourseContent(courseName, libraryCourseChapter, pageN);
   };

   const openLibraryCourse = (courseId) => {
      setLibraryCourseLoading(true);
      setLibraryCourseView(null);
      setLibraryProductView(null);
      setLibraryMembershipView(null);
      setLibraryCourseChapter(null);
      setLibraryCourseContentList([]);
      setLibraryCourseContentListLength(0);
      setLibraryCoursePagePosition(0);
      setLibraryCoursePageN(1);
      setLibraryCourseInfScroll(true);
      const form_data = new FormData();
      form_data.append('currency', props.currency || 'gbp');
      axios({
         method: 'post',
         url: getBackendUrl() + 'api/influencer/community/library_course/' + courseId,
         data: form_data,
         withCredentials: true,
      }).then(res => {
         if (res.data.course) {
            setLibraryCourseView(res.data);
            const firstChapter = res.data.chapters && res.data.chapters.length > 0
               ? res.data.chapters[0].chapter_name : null;
            setLibraryCourseChapter(firstChapter);
            // Load initial content via the existing content API
            loadLibraryCourseContent(res.data.course.name, firstChapter, 1);
         } else {
            setLibraryCourseLoading(false);
         }
      }).catch(err => { setLibraryCourseLoading(false); });
   };

   const loadLibraryMembershipContent = (tierNumber, pageN, overrides = {}) => {
      const contentFilter = overrides.content_filter !== undefined ? overrides.content_filter : libraryMembershipContentFilter;
      const categoryFilter = overrides.category_filter !== undefined ? overrides.category_filter : libraryMembershipCategoryFilter;
      const sortFilter = overrides.sort_filter !== undefined ? overrides.sort_filter : libraryMembershipSortFilter;
      const form_data = new FormData();
      form_data.append('influencer_str', props.influencer);
      form_data.append('category', categoryFilter);
      form_data.append('sort', sortFilter);
      form_data.append('content_type', contentFilter);
      form_data.append('tab', 'sub_posts');
      form_data.append('collection', '');
      form_data.append('digital_product', '');
      form_data.append('post_id', 'null');
      form_data.append('page_n', pageN);
      form_data.append('currency', props.currency || 'gbp');
      form_data.append('tier', tierNumber);
      axios({
         method: 'post',
         url: getBackendUrl() + 'api/influencer_content_data_tab_change_v8',
         data: form_data,
         withCredentials: true,
      }).then(res => {
         const newList = res.data.content_list || [];
         setLibraryMembershipContentList(newList);
         setLibraryMembershipContentTypes(res.data.content_types || []);
         setLibraryMembershipCategories(res.data.categories_filter || []);
         setLibraryMembershipContentListLength(res.data.content_list_length || 0);
         setLibraryMembershipPagePosition(newList.length);
         setLibraryMembershipPageN(pageN);
         if (newList.length >= (res.data.content_list_length || 0)) {
            setLibraryMembershipInfScroll(false);
         } else {
            setLibraryMembershipInfScroll(true);
         }
         setLibraryMembershipLoading(false);
      }).catch(() => {
         setLibraryMembershipContentList([]);
         setLibraryMembershipContentListLength(0);
         setLibraryMembershipPagePosition(0);
         setLibraryMembershipInfScroll(false);
         setLibraryMembershipLoading(false);
      });
   };

   const handleLibraryMembershipInfScroll = () => {
      if (libraryMembershipInfScrollLoading || libraryMembershipLoading) return;
      setLibraryMembershipInfScrollLoading(true);
      const form_data = new FormData();
      form_data.append('influencer_str', props.influencer);
      form_data.append('category', libraryMembershipCategoryFilter);
      form_data.append('sort', libraryMembershipSortFilter);
      form_data.append('content_type', libraryMembershipContentFilter);
      form_data.append('tab', 'sub_posts');
      form_data.append('collection', '');
      form_data.append('digital_product', '');
      form_data.append('page_n', libraryMembershipPageN);
      form_data.append('start_index', libraryMembershipPagePosition);
      form_data.append('currency', props.currency || 'gbp');
      form_data.append('tier', libraryMembershipTier);
      form_data.append('post_id', 'null');
      axios({
         method: 'post',
         url: getBackendUrl() + 'api/influencer_content_data_inf_scroll_v6',
         data: form_data,
         withCredentials: true,
      }).then(res => {
         const newItems = res.data.content_list || [];
         const updatedList = [...libraryMembershipContentList, ...newItems];
         setLibraryMembershipContentList(updatedList);
         setLibraryMembershipPagePosition(prev => prev + newItems.length);
         setLibraryMembershipInfScrollLoading(false);
         if (newItems.length === 0 || updatedList.length >= libraryMembershipContentListLength) {
            setLibraryMembershipInfScroll(false);
         } else {
            setLibraryMembershipInfScroll(true);
         }
      }).catch(() => { setLibraryMembershipInfScrollLoading(false); });
   };

   const handleLibraryMembershipPageChange = (pageN) => {
      setLibraryMembershipPageN(pageN);
      setLibraryMembershipInfScroll(true);
      loadLibraryMembershipContent(libraryMembershipTier, pageN);
   };

   const openLibraryMembership = (membershipItem) => {
      const membershipId = membershipItem?.id;
      if (!membershipId && membershipId !== 0) return;
      setLibraryMembershipLoading(true);
      setLibraryMembershipView(membershipItem || null);
      setLibraryProductView(null);
      setLibraryCourseView(null);
      setLibraryMembershipContentList([]);
      setLibraryMembershipContentListLength(0);
      setLibraryMembershipPagePosition(0);
      setLibraryMembershipPageN(1);
      setLibraryMembershipInfScroll(true);
      setLibraryMembershipContentFilter('all');
      setLibraryMembershipCategoryFilter('all');
      setLibraryMembershipSortFilter('latest');
      setLibraryMembershipSearchQuery('');
      const initialTier = Number.isFinite(Number(membershipItem?.tier)) ? Number(membershipItem.tier) : -1;
      setLibraryMembershipTier(initialTier);
      // Load feed immediately so UI doesn't block on tier lookup.
      loadLibraryMembershipContent(initialTier, 1, { content_filter: 'all', category_filter: 'all', sort_filter: 'latest' });
      const form_data = new FormData();
      form_data.append("currency", props.currency);
      const influencerStr = props.influencer || props.influencer_info?.influencer || props.influencer_name;

      const resolveAndLoadMembership = (tiers = []) => {
         const matchingTier = tiers.find(t => String(t.id) === String(membershipId) || String(t.tier) === String(membershipId));
         const fallbackTier = Number.isFinite(Number(membershipItem?.tier))
            ? Number(membershipItem.tier)
            : -1;
         const tierFilter = matchingTier && Number.isFinite(Number(matchingTier.tier))
            ? Number(matchingTier.tier)
            : fallbackTier;

         setLibraryMembershipView(matchingTier || membershipItem || null);
         if (tierFilter !== libraryMembershipTier) {
            setLibraryMembershipTier(tierFilter);
            loadLibraryMembershipContent(tierFilter, 1);
         }
         window.scrollTo({ top: 0, behavior: 'instant' });
      };

      axios({
         method: "post",
         url: getBackendUrl() + 'api/influencer/storefront_initial_data/' + influencerStr,
         data: form_data,
         headers: {
            "Content-type": "application/json",
         },
         withCredentials: true,
      }).then((res) => {
         const subscriptions = res.data?.data?.subscriptions;
         const tiers = Array.isArray(subscriptions) ? subscriptions : (subscriptions?.tiers || []);
         if (tiers.length > 0) {
            resolveAndLoadMembership(tiers);
            return;
         }
         axios({
            method: "get",
            url: getBackendUrl() + 'api/influencer/' + influencerStr,
            withCredentials: true,
         }).then((fallbackRes) => {
            const fallbackTiers = fallbackRes.data?.data?.subscriptions || fallbackRes.data?.data?.tiers || [];
            resolveAndLoadMembership(Array.isArray(fallbackTiers) ? fallbackTiers : []);
         }).catch(() => {
            resolveAndLoadMembership([]);
         });
      }).catch(() => {
         axios({
            method: "get",
            url: getBackendUrl() + 'api/influencer/' + influencerStr,
            withCredentials: true,
         }).then((fallbackRes) => {
            const fallbackTiers = fallbackRes.data?.data?.subscriptions || fallbackRes.data?.data?.tiers || [];
            resolveAndLoadMembership(Array.isArray(fallbackTiers) ? fallbackTiers : []);
         }).catch(() => {
            resolveAndLoadMembership([]);
         });
      });
   };

   const handleLibraryMembershipFilterChange = (type, value) => {
      const nextContent = type === 'content' ? value : libraryMembershipContentFilter;
      const nextCategory = type === 'category' ? value : libraryMembershipCategoryFilter;
      const nextSort = type === 'sort' ? value : libraryMembershipSortFilter;
      if (type === 'content') setLibraryMembershipContentFilter(value);
      if (type === 'category') setLibraryMembershipCategoryFilter(value);
      if (type === 'sort') setLibraryMembershipSortFilter(value);
      setLibraryMembershipContentList([]);
      setLibraryMembershipContentListLength(0);
      setLibraryMembershipPagePosition(0);
      setLibraryMembershipPageN(1);
      setLibraryMembershipInfScroll(true);
      setLibraryMembershipLoading(true);
      loadLibraryMembershipContent(libraryMembershipTier, 1, {
         content_filter: nextContent,
         category_filter: nextCategory,
         sort_filter: nextSort,
      });
   };

   const membershipCategories = libraryMembershipCategories;
   const membershipSortOptions = ['latest', 'saved', 'trending', 'shuffle', 'most views', 'most likes', 'purchased'];
   const membershipTierOptions = [[libraryMembershipTier, libraryMembershipView?.name || t('community.library.memberships', 'Membership')]];
   const handleMembershipSearchResult = (contentId) => {
      const element = document.getElementById(`membership-content-${contentId}`);
      if (element) {
         element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
   };

   const coachingSortOptions = ['latest', 'saved', 'trending', 'shuffle', 'most views', 'most likes'];

   const loadLibraryCoachingContent = (tierNumber, pageN, overrides = {}) => {
      const contentFilter = overrides.content_filter !== undefined ? overrides.content_filter : libraryCoachingContentFilter;
      const categoryFilter = overrides.category_filter !== undefined ? overrides.category_filter : libraryCoachingCategoryFilter;
      const sortFilter = overrides.sort_filter !== undefined ? overrides.sort_filter : libraryCoachingSortFilter;
      const form_data = new FormData();
      form_data.append('influencer_str', props.influencer);
      form_data.append('category', categoryFilter);
      form_data.append('sort', sortFilter);
      form_data.append('content_type', contentFilter);
      form_data.append('tab', 'sub_posts');
      form_data.append('collection', '');
      form_data.append('digital_product', '');
      form_data.append('post_id', 'null');
      form_data.append('page_n', pageN);
      form_data.append('currency', props.currency || 'gbp');
      form_data.append('tier', tierNumber);
      axios({
         method: 'post',
         url: getBackendUrl() + 'api/influencer_content_data_tab_change_v8',
         data: form_data,
         withCredentials: true,
      }).then(res => {
         const newList = res.data.content_list || [];
         setLibraryCoachingContentList(newList);
         setLibraryCoachingContentTypes(res.data.content_types || []);
         setLibraryCoachingCategories(res.data.categories_filter || []);
         setLibraryCoachingContentListLength(res.data.content_list_length || 0);
         setLibraryCoachingPagePosition(newList.length);
         setLibraryCoachingPageN(pageN);
         if (newList.length >= (res.data.content_list_length || 0)) {
            setLibraryCoachingInfScroll(false);
         } else {
            setLibraryCoachingInfScroll(true);
         }
         setLibraryCoachingLoading(false);
      }).catch(() => {
         setLibraryCoachingContentList([]);
         setLibraryCoachingContentListLength(0);
         setLibraryCoachingPagePosition(0);
         setLibraryCoachingInfScroll(false);
         setLibraryCoachingLoading(false);
      });
   };

   const handleLibraryCoachingInfScroll = () => {
      if (libraryCoachingInfScrollLoading || libraryCoachingLoading) return;
      setLibraryCoachingInfScrollLoading(true);
      const form_data = new FormData();
      form_data.append('influencer_str', props.influencer);
      form_data.append('category', libraryCoachingCategoryFilter);
      form_data.append('sort', libraryCoachingSortFilter);
      form_data.append('content_type', libraryCoachingContentFilter);
      form_data.append('tab', 'sub_posts');
      form_data.append('collection', '');
      form_data.append('digital_product', '');
      form_data.append('page_n', libraryCoachingPageN);
      form_data.append('start_index', libraryCoachingPagePosition);
      form_data.append('currency', props.currency || 'gbp');
      form_data.append('tier', libraryCoachingTier);
      form_data.append('post_id', 'null');
      axios({
         method: 'post',
         url: getBackendUrl() + 'api/influencer_content_data_inf_scroll_v6',
         data: form_data,
         withCredentials: true,
      }).then(res => {
         const newItems = res.data.content_list || [];
         const updatedList = [...libraryCoachingContentList, ...newItems];
         setLibraryCoachingContentList(updatedList);
         setLibraryCoachingPagePosition(prev => prev + newItems.length);
         setLibraryCoachingInfScrollLoading(false);
         if (newItems.length === 0 || updatedList.length >= libraryCoachingContentListLength) {
            setLibraryCoachingInfScroll(false);
         } else {
            setLibraryCoachingInfScroll(true);
         }
      }).catch(() => { setLibraryCoachingInfScrollLoading(false); });
   };

   const handleLibraryCoachingFilterChange = (type, value) => {
      const nextContent = type === 'content' ? value : libraryCoachingContentFilter;
      const nextCategory = type === 'category' ? value : libraryCoachingCategoryFilter;
      const nextSort = type === 'sort' ? value : libraryCoachingSortFilter;
      if (type === 'content') setLibraryCoachingContentFilter(value);
      if (type === 'category') setLibraryCoachingCategoryFilter(value);
      if (type === 'sort') setLibraryCoachingSortFilter(value);
      setLibraryCoachingContentList([]);
      setLibraryCoachingContentListLength(0);
      setLibraryCoachingPagePosition(0);
      setLibraryCoachingPageN(1);
      setLibraryCoachingInfScroll(true);
      setLibraryCoachingLoading(true);
      loadLibraryCoachingContent(libraryCoachingTier, 1, { content_filter: nextContent, category_filter: nextCategory, sort_filter: nextSort });
   };

   const handleCoachingSearchResult = (contentId) => {
      const element = document.getElementById(`coaching-content-${contentId}`);
      if (element) {
         element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
   };

   const openLibraryCoaching = (coachingItem) => {
      setLibraryCoachingView(coachingItem || null);
      setLibraryProductView(null);
      setLibraryCourseView(null);
      setLibraryMembershipView(null);
      window.scrollTo({ top: 0, behavior: 'instant' });
   };

   const openLibraryCommunity = (communityItem) => {
      setLibraryCommunityView(communityItem || null);
      setLibraryProductView(null);
      setLibraryCourseView(null);
      setLibraryMembershipView(null);
      setLibraryCoachingView(null);
      setLibraryMessagesView(null);
      window.scrollTo({ top: 0, behavior: 'instant' });
   };

   const openLibraryMessages = (messagesItem) => {
      setLibraryMessagesView(messagesItem || null);
      setLibraryProductView(null);
      setLibraryCourseView(null);
      setLibraryMembershipView(null);
      setLibraryCoachingView(null);
      setLibraryCommunityView(null);
      window.scrollTo({ top: 0, behavior: 'instant' });
   };

   const [influencerTotalMembers, setInfluencerTotalMembers] = useState(0);
   const [openCheckoutModal, setOpenCheckoutModal] = useState(false);
   const [promoCode, setPromoCode] = useState('');
   const [editedPromoCode, setEditedPromoCode] = useState('');
   const [promoError, setPromoError] = useState('');
   const [selectedDuration, setSelectedDuration] = useState('month');
   const [checkoutPercentOff, setCheckoutPercentOff] = useState(0);
   
   const [fullscreenViewOpen, setFullscreenViewOpen] = useState(false);
   const [selectedContentFullscreen, setSelectedContentFullscreen] = useState('');
   const [selectedContentFullscreenType, setSelectedContentFullscreenType] = useState('');

   const [uploadPostTags, setUploadPostTags] = useState(['all']);
   const [pendingCategories, setPendingCategories] = useState([]); // Track categories to be created on post submission
   const [editPostTags, setEditPostTags] = useState(['all']);
   const [pendingEditCategories, setPendingEditCategories] = useState([]); // Track categories to be created on post edit submission
   const [selectedCategory, setSelectedCategory] = useState('all');
   const [categorySearch, setCategorySearch] = useState('');
   const [searchResults, setSearchResults] = useState(null);
   const [searchLoading, setSearchLoading] = useState(false);
   const searchTimeoutRef = useRef(null);
   const [communityEvents, setCommunityEvents] = useState([]);
   const [libraryItems, setLibraryItems] = useState([]);
   const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(false);
   const [editCategoryModalAnimating, setEditCategoryModalAnimating] = useState(false);
   const [editingCategory, setEditingCategory] = useState(null);
   const [editCategoryName, setEditCategoryName] = useState('');
   const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
   const [dismissedExampleTabs, setDismissedExampleTabs] = useState(() => {
      try {
         const stored = localStorage.getItem('dismissedExampleTabs');
         return stored ? JSON.parse(stored) : [];
      } catch { return []; }
   });
   const showExamplePosts = !dismissedExampleTabs.includes(tabId);
   const dismissExamplePosts = () => {
      const updated = [...dismissedExampleTabs, tabId];
      setDismissedExampleTabs(updated);
      localStorage.setItem('dismissedExampleTabs', JSON.stringify(updated));
   };
   // Re-show examples if all posts have been removed
   useEffect(() => {
      if (!hasRealPosts && postData.length === 0 && dismissedExampleTabs.includes(tabId)) {
         const updated = dismissedExampleTabs.filter(id => id !== tabId);
         setDismissedExampleTabs(updated);
         localStorage.setItem('dismissedExampleTabs', JSON.stringify(updated));
      }
   }, [hasRealPosts, postData.length, tabId]);
   const [hiddenExamplePosts, setHiddenExamplePosts] = useState([]);
   const [expandedLibrarySections, setExpandedLibrarySections] = useState({});
   const [customizeCategories, setCustomizeCategories] = useState(false);
   const [inlineEditCategory, setInlineEditCategory] = useState(null);
   const [inlineEditValue, setInlineEditValue] = useState('');
   const [categoryToDelete, setCategoryToDelete] = useState('');
   const [deleteCategoryPostCount, setDeleteCategoryPostCount] = useState(0);
   const [moveTargetCategory, setMoveTargetCategory] = useState('');

   // Handler to open edit category modal
   const handleEditCategoryClick = (category, e) => {
      e.stopPropagation(); // Prevent triggering the category button click
      setEditingCategory(category);
      setEditCategoryName(category);
      setEditCategoryModalOpen(true);
      setTimeout(() => {
         setEditCategoryModalAnimating(true);
      }, 10);
   };

   // Get ordered categories based on category_order
   const getOrderedCategories = useMemo(() => {
      if (!currentTabInfo || !Array.isArray(currentTabInfo.tags)) {
         return [];
      }
      
      const allCategories = currentTabInfo.tags.filter(cat => cat && typeof cat === 'string' && cat.trim() !== '');
      const categoryOrder = currentTabInfo.category_order || [];
      
      // If no order exists, return categories as-is
      if (!Array.isArray(categoryOrder) || categoryOrder.length === 0) {
         return allCategories;
      }
      
      // Create a map of ordered categories (normalized to lowercase)
      const orderedMap = new Map();
      categoryOrder.forEach((cat, index) => {
         if (typeof cat === 'string') {
            orderedMap.set(cat.toLowerCase(), { cat, index });
         }
      });
      
      // Separate into ordered and unordered
      const ordered = [];
      const unordered = [];
      const seen = new Set();
      
      // Add categories in order
      categoryOrder.forEach(cat => {
         const catLower = typeof cat === 'string' ? cat.toLowerCase() : '';
         const matching = allCategories.find(c => c.toLowerCase() === catLower);
         if (matching && !seen.has(matching.toLowerCase())) {
            ordered.push(matching);
            seen.add(matching.toLowerCase());
         }
      });
      
      // Add any remaining categories that weren't in the order
      allCategories.forEach(cat => {
         if (!seen.has(cat.toLowerCase())) {
            unordered.push(cat);
         }
      });
      
      return [...ordered, ...unordered];
   }, [currentTabInfo?.tags, currentTabInfo?.category_order]);

   // State for drag-and-drop reordering
   const [orderedCategories, setOrderedCategories] = useState([]);
   const saveOrderTimeoutRef = useRef(null);
   const scrollContainerRef = useRef(null);
   
   // Update orderedCategories when getOrderedCategories changes
   useEffect(() => {
      setOrderedCategories(getOrderedCategories.map(cat => ({ id: cat, name: cat })));
   }, [getOrderedCategories]);

   // Debounced search
   useEffect(() => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (!categorySearch || categorySearch.trim().length < 2) {
         setSearchResults(null);
         setSearchLoading(false);
         return;
      }
      setSearchLoading(true);
      searchTimeoutRef.current = setTimeout(() => {
         const form_data = new FormData();
         form_data.append('query', categorySearch.trim());
         axios({
            method: 'post',
            url: getBackendUrl() + 'api/influencer/community/search/' + props.influencer + '/' + tabId,
            data: form_data,
            headers: { 'Content-type': 'application/json' },
            withCredentials: true,
         }).then(res => {
            setSearchResults(res.data.results || []);
            setSearchLoading(false);
         }).catch((err) => {
            console.error('Community search error:', err);
            setSearchResults([]);
            setSearchLoading(false);
         });
      }, 400);
      return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
   }, [categorySearch, tabId]); // eslint-disable-line react-hooks/exhaustive-deps

   // Save category order to backend (with debounce)
   const saveCategoryOrder = useCallback(async (newOrder) => {
      // Clear existing timeout
      if (saveOrderTimeoutRef.current) {
         clearTimeout(saveOrderTimeoutRef.current);
      }
      
      // Set new timeout
      saveOrderTimeoutRef.current = setTimeout(async () => {
         const categoryNames = newOrder.map(item => typeof item === 'string' ? item : item.name || item.id);
         
         const form_data = new FormData();
         form_data.append('tab_id', tabId);
         form_data.append('category_order', JSON.stringify(categoryNames));
         
         try {
            const response = await axios({
               method: 'POST',
               url: backend_url + 'api/influencer/community/set_category_order',
               data: form_data,
               headers: {
                  'Content-type': 'application/json',
               },
               withCredentials: true
            });
            
            if (response.data.success) {
               // Update currentTabInfo with new order
               if (currentTabInfo) {
                  setCurrentTabInfo({
                     ...currentTabInfo,
                     category_order: response.data.category_order || categoryNames
                  });
               }
               // Update tabs array
               setTabs(prevTabs => 
                  prevTabs.map(tab => 
                     tab.id === tabId 
                        ? { ...tab, category_order: response.data.category_order || categoryNames }
                        : tab
                  )
               );
            }
         } catch (error) {
            console.error('Error saving category order:', error);
         }
      }, 500);
   }, [tabId, currentTabInfo, backend_url]);

   const canAccessCommunity = props.isUnlockedCommunityPreview ? true : (props.mobilePreview || (props.current_page_view === 'preview' && props.view_type === 'user')) ? false : true;

   function addCommunityCategory(category) {
      let form_data = new FormData();
      form_data.append('tag', category);
      form_data.append('tab_id', tabId);
      return axios({
         method: 'POST',   
         url: backend_url + 'api/influencer/community/add_tag',
         data: form_data,
         headers: {
            'Content-type': 'application/json',
         },
         withCredentials: true
      }).then(res => {
         if (res.data.success){
            // console.log('Category added: ', res.data.tag);
            // Update tabs array to include the new tag for the current tab
            setTabs(prevTabs => 
               prevTabs.map(tab => 
                  tab.id === tabId && Array.isArray(tab.tags) 
                     ? { ...tab, tags: tab.tags.includes(res.data.tag) ? tab.tags : [...tab.tags, res.data.tag] }
                     : tab
               )
            );
            // Update currentTabInfo to include the new tag
            if (currentTabInfo && Array.isArray(currentTabInfo.tags)) {
               if (!currentTabInfo.tags.includes(res.data.tag)) {
                  setCurrentTabInfo({
                     ...currentTabInfo,
                     tags: [...currentTabInfo.tags, res.data.tag]
                  });
               }
            } else if (currentTabInfo) {
               setCurrentTabInfo({
                  ...currentTabInfo,
                  tags: [res.data.tag]
               });
            }
            return res.data.tag; // Return the tag so it can be used in the composer
         }
         throw new Error('Failed to add category');
      }).catch(error => {
         console.log(error);
         throw error;
      });
   }

   function editCommunityCategory(orig_category, new_category) {
      let form_data = new FormData();
      form_data.append('orig_tag_name', orig_category);
      form_data.append('tag', new_category);
      form_data.append('tab_id', tabId);
      axios({
         method: 'POST',
         url: backend_url + 'api/influencer/community/edit_tag',
         data: form_data,
         headers: {
            'Content-type': 'application/json',
         },
         withCredentials: true
      }).then(res => {
         if (res.data.success){
            const updatedTag = res.data.tag;
            // Update orderedCategories state to preserve position while updating the name
            setOrderedCategories(prevOrdered => 
               prevOrdered.map(item => {
                  const catName = typeof item === 'string' ? item : item.name || item.id;
                  if (catName && catName.toLowerCase() === orig_category.toLowerCase()) {
                     return typeof item === 'string' ? updatedTag : { ...item, id: updatedTag, name: updatedTag };
                  }
                  return item;
               })
            );
            // Update tabs array to reflect the edited tag
            setTabs(prevTabs => 
               prevTabs.map(tab => 
                  tab.id === tabId && Array.isArray(tab.tags) 
                     ? { 
                        ...tab, 
                        tags: tab.tags.map(tag => tag === orig_category ? updatedTag : tag)
                     }
                     : tab
               )
            );
            // Update currentTabInfo to reflect the edited tag
            if (currentTabInfo && Array.isArray(currentTabInfo.tags)) {
               const idx = currentTabInfo.tags.findIndex(tag => tag === orig_category);
               if (idx !== -1) {
                  let updatedTags = [...currentTabInfo.tags];
                  updatedTags[idx] = updatedTag;
                  
                  // Also update category_order if it exists
                  let updatedCategoryOrder = currentTabInfo.category_order;
                  if (Array.isArray(updatedCategoryOrder)) {
                     updatedCategoryOrder = updatedCategoryOrder.map(cat => {
                        if (typeof cat === 'string' && cat.toLowerCase() === orig_category.toLowerCase()) {
                           return updatedTag;
                        }
                        return cat;
                     });
                  }
                  
                  setCurrentTabInfo({
                     ...currentTabInfo,
                     tags: updatedTags,
                     category_order: updatedCategoryOrder
                  });
               }
            }
            // If the edited category is currently selected, update selectedCategory and reload posts
            if (selectedCategory === orig_category) {
               setSelectedCategory(updatedTag);
               setUploadPostTags([updatedTag]);
               // Reload posts with the new category name
               changeCategory(updatedTag);
            } else if (selectedCategory === 'all') {
               // If viewing "all" posts, reload to show updated category names in posts
               changeCategory('all');
            }
            // console.log('Category edited: ', updatedTag);
         }
      }).catch(error => {
         console.log(error);
      });
   }

   const deleteCommunityCategory = async (category) => {
      try {
         const form_data = new FormData();
         form_data.append('tag', category);
         form_data.append('tab_id', tabId);
         form_data.append('check_only', 'true');
         const res = await axios({
            method: 'post',
            url: backend_url + 'api/influencer/community/delete_tag',
            data: form_data,
            headers: { 'Content-type': 'application/json' },
            withCredentials: true,
         });
         if (res.data.post_count > 0) {
            setCategoryToDelete(category);
            setDeleteCategoryPostCount(res.data.post_count);
            setMoveTargetCategory('');
            setDeleteConfirmModalOpen(true);
         } else {
            // No posts, delete directly
            const delete_data = new FormData();
            delete_data.append('tag', category);
            delete_data.append('tab_id', tabId);
            await axios({
               method: 'post',
               url: backend_url + 'api/influencer/community/delete_tag',
               data: delete_data,
               headers: { 'Content-type': 'application/json' },
               withCredentials: true,
            });
            // Update UI
            const updated = (currentTabInfo?.tags || []).filter(t => t !== category);
            setCurrentTabInfo(prev => ({ ...prev, tags: updated }));
            const updatedOrder = (orderedCategories || []).filter(c => c.name?.toLowerCase() !== category.toLowerCase());
            setOrderedCategories(updatedOrder);
            if (selectedCategory === category) {
               setSelectedCategory('all');
            }
            handleSnackbar('var(--brand-color)', 'success', t('community.category.deleteSuccess'));
         }
      } catch (err) {
         handleSnackbar('var(--danger-color)', 'error', t('community.category.deleteError') || 'Error deleting category');
      }
   };

 
   function processPinnedPost(post_id, post_index, action) {
      let form_data = new FormData();
      form_data.append('post_id', post_id);
      form_data.append('post_index', post_index);
      form_data.append('action', action);

      axios({
         method: 'POST',
         url: backend_url + 'api/influencer/community/pinned_post',
         data: form_data,
         headers: {
            'Content-type': 'application/json',
         },
         withCredentials: true,
      })
         .then(res => {
            let new_post_data = [...postData];

            if (action === 'remove') {
               new_post_data.splice(post_index, 0, new_post_data.splice(res.data.post_index, 1)[0]);
               new_post_data[res.data.post_index]['pinned'] = false;
               // sort by time newest first atm (TODO: sort by index to optimise)
               new_post_data.sort((a, b) => new Date(b.time) - new Date(a.time));
            } else {
               new_post_data.unshift(new_post_data.splice(res.data.post_index, 1)[0]);
               new_post_data[0]['pinned'] = true;
            }
            setPostData(new_post_data);
         });
   }


   const handleUpdatePinnedPost = (post_id, post_index) => {
      processPinnedPost(post_id, post_index, 'add');
   }

   const handleRemovePinnedPost = (post_id, post_index) => {
      processPinnedPost(post_id, post_index, 'remove');
   }

   const [isPinnedPost, setIsPinnedPost] = useState(null);

   useEffect(() => {
      // console.log(postData)
      const hasPinnedPost = postData.some(post => post.pinned === true);
      setIsPinnedPost(hasPinnedPost);
      // console.log('has pinned post: ', hasPinnedPost);
   }, [postData]);
   

   const handleFullscreenOpen = (preview_src, preview_type) => {
      if (props.windowWidth < 900 && (preview_type === 'gos' || preview_type === 'god')) {
         const id = preview_src.split('/')[5];
         let url = preview_src;
         if (preview_type === 'gos') {
            url = `https://docs.google.com/spreadsheets/d/${id}/edit`;
         } else if (preview_type === 'god') {
            url = `https://docs.google.com/document/d/${id}/edit`;
         }
         window.open(url, '_blank');
      } else {
         setSelectedContentFullscreen(preview_src);
         setSelectedContentFullscreenType(preview_type);
         setFullscreenViewOpen(true);
      }
   }

   const handleFullscreenClose = () => {
      setFullscreenViewOpen(false);
   }
   // Test Guidelines
   const guidelines = [
      'Be respectful to others',
      'No spamming or self-promotion',
      'Avoid offensive language and content',
      'Respect the privacy of others',
      'Stay on-topic and relevant',
   ];


   const handleClick = e => {
      setAnchorEl(e.currentTarget);
   };
   const handleClose = () => {
      setAnchorEl(null);
   };
   
   // Function to scroll to composer and open meeting menu
   const scrollToComposerAndOpenMeeting = () => {
      // Scroll to composer
      const composerElement = document.getElementById('community-composer');
      if (composerElement) {
         composerElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
         // Wait for scroll, then find and click the calendar button
         setTimeout(() => {
            const calendarButton = composerElement.querySelector(`[aria-label="${t('community.addMeeting')}"], .calendar-meeting-button`);
            if (calendarButton) {
               calendarButton.click();
            } else {
               // Fallback: try to find by icon or parent element
               const calendarIcon = composerElement.querySelector('svg[style*="color"][style*="666"]');
               if (calendarIcon && calendarIcon.closest('span')) {
                  calendarIcon.closest('span').click();
               }
            }
         }, 300);
      }
   };
   const handleClickDoc = e => {
      setAnchorElDoc(e.currentTarget);
   };
   const handleCloseDoc = () => {
      setAnchorElDoc(null);
   };
   const handleClickVideo = e => {
      setAnchorElVideo(e.currentTarget);
   };

   const handleCloseVideo = () => {
      setAnchorElVideo(null);
   };


   const handleClickAudio = e => {
      setAnchorElAudio(e.currentTarget);
   };
   const handleCloseAudio = () => {
      setAnchorElAudio(null);
   };


   const processEmbedUrlSet = () => {
      let url_string = ''
      if (embedUrlType === 'post') {
         // check if valid youtube url
         if (embedVideoUrl.includes('youtube.com/')) {
            url_string = embedVideoUrl.split('youtube.com/')[1].split('v=')[1].split('&')[0]
            setEmbedUrlKey(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrl('youtube');
            setIsMediaAttached(true);
            setFileType('vey');
            setEmbedVideoUrl('');
            setShowPostButton(true);
         } else if (embedVideoUrl.includes('youtu.be/')) {
            url_string = embedVideoUrl.split('youtu.be/')[1].split('?')[0]
            setEmbedUrlKey(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrl('youtube');
            setIsMediaAttached(true);
            setFileType('vey');
            setEmbedVideoUrl('');
            setShowPostButton(true);
         } else if (embedVideoUrl.includes('loom.com/share/')) {
            url_string = embedVideoUrl.split('loom.com/share/')[1].split('?')[0]
            setEmbedUrlKey(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrl('loom');
            setIsMediaAttached(true);
            setFileType('vel');
            setEmbedVideoUrl('');
            setShowPostButton(true);
         } else if (embedVideoUrl.includes('vimeo.com/')) {
            const _vm = embedVideoUrl.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/)
            const _vh = embedVideoUrl.match(/[?&]h=([a-zA-Z0-9]+)/)
            if (_vm && _vm[1]) {
               const _vHash = _vm[2] || (_vh ? _vh[1] : null)
               url_string = _vHash ? _vm[1] + '?h=' + _vHash : _vm[1]
            } else { url_string = '' }
            if (url_string) {
               setEmbedUrlKey(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrl('vimeo');
               setIsMediaAttached(true);
               setFileType('vev');
               setEmbedVideoUrl('');
            setShowPostButton(true);
            }
         } else if (embedVideoUrl.includes('twitch.tv/') || embedVideoUrl.includes('clips.twitch.tv/')) {
            const _tv = embedVideoUrl.match(/twitch\.tv\/videos\/(\d+)/)
            const _tc = embedVideoUrl.match(/clips\.twitch\.tv\/([A-Za-z0-9_-]+)/)
            const _tch = !_tv && !_tc ? embedVideoUrl.match(/twitch\.tv\/([A-Za-z0-9_]+)$/) : null
            if (_tv) url_string = 'v' + _tv[1]
            else if (_tc) url_string = 'c' + _tc[1]
            else if (_tch) url_string = 'ch' + _tch[1]
            if (url_string) {
               setEmbedUrlKey(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrl('twitch');
               setIsMediaAttached(true);
               setFileType('vtw');
               setEmbedVideoUrl('');
            setShowPostButton(true);
            }
         } else if (embedVideoUrl.includes('wistia.com/')) {
            const _wi = embedVideoUrl.match(/wistia\.(?:com|net)\/(?:medias|embed\/iframe)\/([A-Za-z0-9]+)/)
            url_string = _wi ? _wi[1] : ''
            if (url_string) {
               setEmbedUrlKey(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrl('wistia');
               setIsMediaAttached(true);
               setFileType('vwi');
               setEmbedVideoUrl('');
            setShowPostButton(true);
            }
         } else {
            setSnackbarMsgEmbed(t('community.errors.invalidYouTubeLoomUrl'))
            setSnackbarEmbed(true);
         }
      } else if (embedUrlType === 'thread') {
         // check if valid youtube url
         if (embedVideoUrlThread.includes('youtube.com/')) {
            url_string = embedVideoUrlThread.split('youtube.com/')[1].split('v=')[1].split('&')[0]
            setEmbedUrlKeyThread(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrlThread('youtube');
            setIsMediaAttachedThread(true);
            setFileTypeThread('vey');
            setEmbedVideoUrlThread('');
            setShowPostButtonThread(true);
         } else if (embedVideoUrlThread.includes('youtu.be/')) {
            url_string = embedVideoUrlThread.split('youtu.be/')[1].split('?')[0]
            setEmbedUrlKeyThread(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrlThread('youtube');
            setIsMediaAttachedThread(true);
            setFileTypeThread('vey');
            setEmbedVideoUrlThread('');
            setShowPostButtonThread(true);
         } else if (embedVideoUrlThread.includes('loom.com/share/')) {
            url_string = embedVideoUrlThread.split('loom.com/share/')[1].split('?')[0]
            setEmbedUrlKeyThread(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrlThread('loom');
            setIsMediaAttachedThread(true);
            setFileTypeThread('vel');
            setEmbedVideoUrlThread('');
            setShowPostButtonThread(true);
         } else if (embedVideoUrlThread.includes('vimeo.com/')) {
            const _vm = embedVideoUrlThread.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/)
            const _vh = embedVideoUrlThread.match(/[?&]h=([a-zA-Z0-9]+)/)
            if (_vm && _vm[1]) {
               const _vHash = _vm[2] || (_vh ? _vh[1] : null)
               url_string = _vHash ? _vm[1] + '?h=' + _vHash : _vm[1]
            } else { url_string = '' }
            if (url_string) {
               setEmbedUrlKeyThread(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrlThread('vimeo');
               setIsMediaAttachedThread(true);
               setFileTypeThread('vev');
               setEmbedVideoUrlThread('');
            setShowPostButtonThread(true);
            }
         } else if (embedVideoUrlThread.includes('twitch.tv/') || embedVideoUrlThread.includes('clips.twitch.tv/')) {
            const _tv = embedVideoUrlThread.match(/twitch\.tv\/videos\/(\d+)/)
            const _tc = embedVideoUrlThread.match(/clips\.twitch\.tv\/([A-Za-z0-9_-]+)/)
            const _tch = !_tv && !_tc ? embedVideoUrlThread.match(/twitch\.tv\/([A-Za-z0-9_]+)$/) : null
            if (_tv) url_string = 'v' + _tv[1]
            else if (_tc) url_string = 'c' + _tc[1]
            else if (_tch) url_string = 'ch' + _tch[1]
            if (url_string) {
               setEmbedUrlKeyThread(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrlThread('twitch');
               setIsMediaAttachedThread(true);
               setFileTypeThread('vtw');
               setEmbedVideoUrlThread('');
            setShowPostButtonThread(true);
            }
         } else if (embedVideoUrlThread.includes('wistia.com/')) {
            const _wi = embedVideoUrlThread.match(/wistia\.(?:com|net)\/(?:medias|embed\/iframe)\/([A-Za-z0-9]+)/)
            url_string = _wi ? _wi[1] : ''
            if (url_string) {
               setEmbedUrlKeyThread(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrlThread('wistia');
               setIsMediaAttachedThread(true);
               setFileTypeThread('vwi');
               setEmbedVideoUrlThread('');
            setShowPostButtonThread(true);
            }
         } else {
            setSnackbarMsgEmbed(t('community.errors.invalidYouTubeLoomUrl'))
            setSnackbarEmbed(true);
         }
      } else if (embedUrlType === 'comment') {
         // check if valid youtube url
         if (embedVideoUrlComment.includes('youtube.com/')) {
            url_string = embedVideoUrlComment.split('youtube.com/')[1].split('v=')[1].split('&')[0]
            setEmbedUrlKeyComment(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrlComment('youtube');
            setIsMediaAttachedComment(true);
            setFileTypeComment('vey');
            setEmbedVideoUrlComment('');
            setShowPostButtonComment(true);
         } else if (embedVideoUrlComment.includes('youtu.be/')) {
            url_string = embedVideoUrlComment.split('youtu.be/')[1].split('?')[0]
            setEmbedUrlKeyComment(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrlComment('youtube');
            setIsMediaAttachedComment(true);
            setFileTypeComment('vey');
            setEmbedVideoUrlComment('');
            setShowPostButtonComment(true);
         } else if (embedVideoUrlComment.includes('loom.com/share/')) {
            url_string = embedVideoUrlComment.split('loom.com/share/')[1].split('?')[0]
            setEmbedUrlKeyComment(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrlComment('loom');
            setIsMediaAttachedComment(true);
            setFileTypeComment('vel');
            setEmbedVideoUrlComment('');
            setShowPostButtonComment(true);
         } else if (embedVideoUrlComment.includes('vimeo.com/')) {
            const _vm = embedVideoUrlComment.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/)
            const _vh = embedVideoUrlComment.match(/[?&]h=([a-zA-Z0-9]+)/)
            if (_vm && _vm[1]) {
               const _vHash = _vm[2] || (_vh ? _vh[1] : null)
               url_string = _vHash ? _vm[1] + '?h=' + _vHash : _vm[1]
            } else { url_string = '' }
            if (url_string) {
               setEmbedUrlKeyComment(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrlComment('vimeo');
               setIsMediaAttachedComment(true);
               setFileTypeComment('vev');
               setEmbedVideoUrlComment('');
            setShowPostButtonComment(true);
            }
         } else if (embedVideoUrlComment.includes('twitch.tv/') || embedVideoUrlComment.includes('clips.twitch.tv/')) {
            const _tv = embedVideoUrlComment.match(/twitch\.tv\/videos\/(\d+)/)
            const _tc = embedVideoUrlComment.match(/clips\.twitch\.tv\/([A-Za-z0-9_-]+)/)
            const _tch = !_tv && !_tc ? embedVideoUrlComment.match(/twitch\.tv\/([A-Za-z0-9_]+)$/) : null
            if (_tv) url_string = 'v' + _tv[1]
            else if (_tc) url_string = 'c' + _tc[1]
            else if (_tch) url_string = 'ch' + _tch[1]
            if (url_string) {
               setEmbedUrlKeyComment(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrlComment('twitch');
               setIsMediaAttachedComment(true);
               setFileTypeComment('vtw');
               setEmbedVideoUrlComment('');
            setShowPostButtonComment(true);
            }
         } else if (embedVideoUrlComment.includes('wistia.com/')) {
            const _wi = embedVideoUrlComment.match(/wistia\.(?:com|net)\/(?:medias|embed\/iframe)\/([A-Za-z0-9]+)/)
            url_string = _wi ? _wi[1] : ''
            if (url_string) {
               setEmbedUrlKeyComment(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrlComment('wistia');
               setIsMediaAttachedComment(true);
               setFileTypeComment('vwi');
               setEmbedVideoUrlComment('');
            setShowPostButtonComment(true);
            }
         } else {
            setSnackbarMsgEmbed(t('community.errors.invalidYouTubeLoomUrl'))
            setSnackbarEmbed(true);
         }
      } else if (embedUrlType === 'edit') {
         // check if valid youtube url
         if (embedVideoUrlEdit.includes('youtube.com/')) {
            url_string = embedVideoUrlEdit.split('youtube.com/')[1].split('v=')[1].split('&')[0]
            setEmbedUrlKeyEdit(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrlEdit(url_string);
            setIsMediaAttachedEdit(true);
            setFileTypeEdit('vey');
            setEmbedVideoUrlEdit('');
         } else if (embedVideoUrlEdit.includes('youtu.be/')) {
            url_string = embedVideoUrlEdit.split('youtu.be/')[1].split('?')[0]
            setEmbedUrlKeyEdit(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrlEdit(url_string);
            setIsMediaAttachedEdit(true);
            setFileTypeEdit('vey');
            setEmbedVideoUrlEdit('');
         } else if (embedVideoUrlEdit.includes('loom.com/share/')) {
            url_string = embedVideoUrlEdit.split('loom.com/share/')[1].split('?')[0]
            setEmbedUrlKeyEdit(url_string);
            setEmbedUrlIsOpen(false);
            setSelectedPreviewUrlEdit(url_string);
            setIsMediaAttachedEdit(true);
            setFileTypeEdit('vel');
            setEmbedVideoUrlEdit('');
         } else if (embedVideoUrlEdit.includes('vimeo.com/')) {
            const _vm = embedVideoUrlEdit.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/)
            const _vh = embedVideoUrlEdit.match(/[?&]h=([a-zA-Z0-9]+)/)
            if (_vm && _vm[1]) {
               const _vHash = _vm[2] || (_vh ? _vh[1] : null)
               url_string = _vHash ? _vm[1] + '?h=' + _vHash : _vm[1]
            } else { url_string = '' }
            if (url_string) {
               setEmbedUrlKeyEdit(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrlEdit(url_string);
               setIsMediaAttachedEdit(true);
               setFileTypeEdit('vev');
               setEmbedVideoUrlEdit('');
            }
         } else if (embedVideoUrlEdit.includes('twitch.tv/') || embedVideoUrlEdit.includes('clips.twitch.tv/')) {
            const _tv = embedVideoUrlEdit.match(/twitch\.tv\/videos\/(\d+)/)
            const _tc = embedVideoUrlEdit.match(/clips\.twitch\.tv\/([A-Za-z0-9_-]+)/)
            const _tch = !_tv && !_tc ? embedVideoUrlEdit.match(/twitch\.tv\/([A-Za-z0-9_]+)$/) : null
            if (_tv) url_string = 'v' + _tv[1]
            else if (_tc) url_string = 'c' + _tc[1]
            else if (_tch) url_string = 'ch' + _tch[1]
            if (url_string) {
               setEmbedUrlKeyEdit(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrlEdit(url_string);
               setIsMediaAttachedEdit(true);
               setFileTypeEdit('vtw');
               setEmbedVideoUrlEdit('');
            }
         } else if (embedVideoUrlEdit.includes('wistia.com/')) {
            const _wi = embedVideoUrlEdit.match(/wistia\.(?:com|net)\/(?:medias|embed\/iframe)\/([A-Za-z0-9]+)/)
            url_string = _wi ? _wi[1] : ''
            if (url_string) {
               setEmbedUrlKeyEdit(url_string);
               setEmbedUrlIsOpen(false);
               setSelectedPreviewUrlEdit(url_string);
               setIsMediaAttachedEdit(true);
               setFileTypeEdit('vwi');
               setEmbedVideoUrlEdit('');
            }
         } else {
            setSnackbarMsgEmbed(t('community.errors.invalidYouTubeLoomUrl'))
            setSnackbarEmbed(true);
         }
      }
   }

   const getMeetingTypeName = (fileType) => {
      switch (fileType) {
         case 'mzm': return 'Zoom Meeting';
         case 'mgm': return 'Google Meet Meeting';
         case 'mmt': return 'Microsoft Teams Meeting';
         case 'mot': return 'Meeting';
         default: return 'Meeting';
      }
   }

   const formatMeetingTime = (dateTimeString) => {

      if (postMeetingTime !== '') {

         const date = new Date(dateTimeString);
         const now = new Date();

         const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
         const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

         // Use local time methods instead of UTC
         const dayOfWeek = days[date.getDay()];
         const dayOfMonth = date.getDate();
         const month = months[date.getMonth()];

         let hours = date.getHours();
         const minutes = date.getMinutes().toString().padStart(2, '0');
         const ampm = hours >= 12 ? 'pm' : 'am';
         hours = hours % 12;
         hours = hours ? hours : 12; // the hour '0' should be '12'

         const timeString = `${hours}:${minutes}${ampm}`;

         // Check if the meeting is within the next 7 days
         const diffTime = Math.abs(date - now);
         const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

         if (diffDays <= 7) {
            return `Look forward to meeting with you on ${dayOfWeek} at ${timeString}!`;
         } else {
            return `Look forward to meeting you on the ${dayOfMonth}${getDaySuffix(dayOfMonth)} of ${month} at ${timeString}! `;
         }
      } else {
         return 'Look forward to meeting you there!';
      }
   }

   const getDaySuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
         case 1: return "st";
         case 2: return "nd";
         case 3: return "rd";
         default: return "th";
      }
   }

   const checkMeetingLink = () => {
      let meetingLinkValid = false;
      
      if (meetingType === 'post') {
         if (fileType === 'mgm') {
            if (!meetingLink.includes('meet.google.com/')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidGoogleMeetLink')
               );
               return;
            } else {
               setSelectedPreviewUrl(meetingLink);
               setIsMediaAttached(true);
               setShowPostButton(true);
               setMeetingModalOpen(false);
               setMeetingLink('');
               meetingLinkValid = true;
            }
         } else if (fileType === 'mzm') {
            if (!meetingLink.includes('web.zoom.us/')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidZoomLink')
               );
               return;
            } else {
               setSelectedPreviewUrl(meetingLink);
               setIsMediaAttached(true);
               setShowPostButton(true);
               setMeetingModalOpen(false);
               setMeetingLink('');
               meetingLinkValid = true;
            }
         } else if (fileType === 'mmt') {
            if (!meetingLink.includes('teams.microsoft.com/l/meetup-join')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidMicrosoftTeamsLink'),
               );
               return;
            } else {
               setSelectedPreviewUrl(meetingLink);
               setIsMediaAttached(true);
               setShowPostButton(true);
               setMeetingModalOpen(false);
               setMeetingLink('');
               meetingLinkValid = true;
            }
         } else if (fileType === 'mot') {
            setSelectedPreviewUrl(meetingLink);
            setIsMediaAttached(true);
            setShowPostButton(true);
            setMeetingModalOpen(false);
            setMeetingLink('');
            meetingLinkValid = true;
         }
      } else if (meetingType === 'thread') {
         if (fileTypeThread === 'mgm') {
            if (!meetingLinkThread.includes('meet.google.com/')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidGoogleMeetLink')
               );
               return;
            } else {
               setSelectedPreviewUrlThread(meetingLinkThread);
               setIsMediaAttachedThread(true);
               setShowPostButtonThread(true);
               setMeetingModalOpen(false);
               setMeetingLinkThread('');
               meetingLinkValid = true;
            }
         } else if (fileTypeThread === 'mzm') {
            if (!meetingLinkThread.includes('web.zoom.us/')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidZoomLink')
               );
               return;
            } else {
               setSelectedPreviewUrlThread(meetingLinkThread);
               setIsMediaAttachedThread(true);
               setShowPostButtonThread(true);
               setMeetingModalOpen(false);
               setMeetingLinkThread('');
               meetingLinkValid = true;
            }
         } else if (fileTypeThread === 'mmt') {
            if (!meetingLinkThread.includes('teams.microsoft.com/l/meetup-join')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidMicrosoftTeamsLink'),
               );
               return;
            } else {
               setSelectedPreviewUrlThread(meetingLinkThread);
               setIsMediaAttachedThread(true);
               setShowPostButtonThread(true);
               setMeetingModalOpen(false);
               setMeetingLinkThread('');
               meetingLinkValid = true;
            }
         } else if (fileTypeThread === 'mot') {
            setSelectedPreviewUrlThread(meetingLinkThread);
            setIsMediaAttachedThread(true);
            setShowPostButtonThread(true);
            setMeetingModalOpen(false);
            setMeetingLinkThread('');
            meetingLinkValid = true;
         }
      } else if (meetingType === 'comment') {
         if (fileTypeComment === 'mgm') {
            if (!meetingLinkComment.includes('meet.google.com/')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidGoogleMeetLink')
               );
               return;
            } else {
               setSelectedPreviewUrlComment(meetingLinkComment);
               setIsMediaAttachedComment(true);
               setShowPostButtonComment(true);
               setMeetingModalOpen(false);
               setMeetingLinkComment('');
               meetingLinkValid = true;
            }
         } else if (fileTypeComment === 'mzm') {
            if (!meetingLinkComment.includes('web.zoom.us/')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidZoomLink')
               );
               return;
            } else {
               setSelectedPreviewUrlComment(meetingLinkComment);
               setIsMediaAttachedComment(true);
               setShowPostButtonComment(true);
               setMeetingModalOpen(false);
               setMeetingLinkComment('');
               meetingLinkValid = true;
            }
         } else if (fileTypeComment === 'mmt') {
            if (!meetingLinkComment.includes('teams.microsoft.com/l/meetup-join')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidMicrosoftTeamsLink'),
               );
               return;
            } else {
               setSelectedPreviewUrlComment(meetingLinkComment);
               setIsMediaAttachedComment(true);
               setShowPostButtonComment(true);
               setMeetingModalOpen(false);
               setMeetingLinkComment('');
               meetingLinkValid = true;
            }
         } else if (fileTypeComment === 'mot') {
            setSelectedPreviewUrlComment(meetingLinkComment);
            setIsMediaAttachedComment(true);
            setShowPostButtonComment(true);
            setMeetingModalOpen(false);
            setMeetingLinkComment('');
            meetingLinkValid = true;
         }
      } else if (meetingType === 'edit') {
         if (fileTypeEdit === 'mgm') {
            if (!meetingLinkEdit.includes('meet.google.com/')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidGoogleMeetLink')
               );
               return;
            } else {
               setSelectedPreviewUrlEdit(meetingLinkEdit);
               setIsMediaAttachedEdit(true);
               setMeetingModalOpen(false);
               setMeetingLinkEdit('');
               meetingLinkValid = true;
            }
         } else if (fileTypeEdit === 'mzm') {
            if (!meetingLinkEdit.includes('web.zoom.us/')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidZoomLink')
               );
               return;
            } else {
               setSelectedPreviewUrlEdit(meetingLinkEdit);
               setIsMediaAttachedEdit(true);
               setMeetingModalOpen(false);
               setMeetingLinkEdit('');
               meetingLinkValid = true;
            }
         } else if (fileTypeEdit === 'mmt') {
            if (!meetingLinkEdit.includes('teams.microsoft.com/l/meetup-join')) {
               handleSnackbarMeeting(
                  'var(--danger-color)',
                  'error',
                  t('community.errors.invalidMicrosoftTeamsLink'),
               );
               return;
            } else {
               setSelectedPreviewUrlEdit(meetingLinkEdit);
               setIsMediaAttachedEdit(true);
               setMeetingModalOpen(false);
               setMeetingLinkEdit('');
               meetingLinkValid = true;
            }
         } else if (fileTypeEdit === 'mot') {
            setSelectedPreviewUrlEdit(meetingLinkEdit);
            setIsMediaAttachedEdit(true);
            setMeetingModalOpen(false);
            setMeetingLinkEdit('');
            meetingLinkValid = true;
         }
      }

      // Add default meeting description text with meeting type and time (only if meeting link is valid)
      if (!meetingLinkValid) {
         return;
      }
      
      // Get the current fileType based on meetingType
      const currentFileType = meetingType === 'post' ? fileType :
         meetingType === 'thread' ? fileTypeThread :
            meetingType === 'comment' ? fileTypeComment :
               meetingType === 'edit' ? fileTypeEdit : '';
      
      const meetingTypeName = getMeetingTypeName(currentFileType);
      let meetingDescription = '';
      
      if (postMeetingTime !== '') {
         // Extract time information from postMeetingTime (which is in UTC)
         const date = new Date(postMeetingTime);
         const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
         const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
         const dayOfWeek = days[date.getDay()];
         const dayOfMonth = date.getDate();
         const month = months[date.getMonth()];
         let hours = date.getHours();
         const minutes = date.getMinutes().toString().padStart(2, '0');
         const ampm = hours >= 12 ? 'pm' : 'am';
         hours = hours % 12;
         hours = hours ? hours : 12;
         const timeString = `${hours}:${minutes}${ampm}`;
         
         const now = new Date();
         const diffTime = Math.abs(date - now);
         const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
         
         if (diffDays <= 7) {
            meetingDescription = `${meetingTypeName} scheduled for ${dayOfWeek} at ${timeString}`;
         } else {
            meetingDescription = `${meetingTypeName} scheduled for the ${dayOfMonth}${getDaySuffix(dayOfMonth)} of ${month} at ${timeString}`;
         }
      } else {
         // If no meeting time, still add meeting type
         meetingDescription = `${meetingTypeName} - Look forward to meeting you there!`;
      }
      
      // Add the description to the appropriate text field
      if (meetingType === 'post') {
         setNewPostText(prevText => prevText !== '' ? prevText + '\n\n' + meetingDescription : meetingDescription);
      } else if (meetingType === 'thread') {
         setMeetingTimeThread(meetingDescription);
      } else if (meetingType === 'comment') {
         setMeetingTimeComment(meetingDescription);
      } else if (meetingType === 'edit') {
         // Set the actual meeting time, not the description
         setMeetingTimeEditPost(postMeetingTime || '');
         // Also update the edit post text with the meeting description
         if (updateEditPostData && typeof updateEditPostData === 'function') {
            updateEditPostData(prevText => prevText !== '' ? prevText + '\n\n' + meetingDescription : meetingDescription);
         }
      } 

      // setPostMeetingTime('');

      // setPostMeetingTime('');
      // setMeetingTimeThread('');
      // setMeetingTimeComment('');
      // setMeetingTimeEdit('');

   }

   const _getDocLinkError = (link, expectedType) => {
      const l = link.trim().toLowerCase();
      if (expectedType === 'gos') {
         if (l.includes('drive.google.com')) return t('community.errors.googleDriveLinkSheets', 'This is a Google Drive link. Please open the spreadsheet in Google Sheets and share that link instead (docs.google.com/spreadsheets/...)');
         if (l.includes('docs.google.com/document/')) return t('community.errors.googleDocsNotSheets', 'This is a Google Docs link. Please share a Google Sheets link instead (docs.google.com/spreadsheets/...)');
         if (l.includes('docs.google.com/presentation/') || l.includes('docs.google.com/forms/')) return t('community.errors.wrongGoogleType', 'Please share a Google Sheets link (docs.google.com/spreadsheets/...)');
         if (!l.includes('docs.google.com/spreadsheets/')) return t('community.errors.invalidGoogleSheetsLink');
      } else if (expectedType === 'god') {
         if (l.includes('drive.google.com')) return t('community.errors.googleDriveLinkDocs', 'This is a Google Drive link. Please open the document in Google Docs and share that link instead (docs.google.com/document/...)');
         if (l.includes('docs.google.com/spreadsheets/')) return t('community.errors.googleSheetsNotDocs', 'This is a Google Sheets link. Please share a Google Docs link instead (docs.google.com/document/...)');
         if (l.includes('docs.google.com/presentation/') || l.includes('docs.google.com/forms/')) return t('community.errors.wrongGoogleType', 'Please share a Google Docs link (docs.google.com/document/...)');
         if (!l.includes('docs.google.com/document/')) return t('community.errors.invalidGoogleDocsLink');
      }
      return null;
   };

   const checkDocLink = () => {
      if (docType === 'post') {
         if (fileType === 'gos') {
            const err = _getDocLinkError(docLink, 'gos');
            if (err) {
               setSnackbarMsgDoc(err);
               setSnackbarOpenDoc(true);
            } else {
               setSelectedPreviewUrl(docLink);
               setIsMediaAttached(true);
               setShowPostButton(true);
               setDocModalOpen(false);
               setDocLink('');
            }
         } else if (fileType === 'god') {
            const err = _getDocLinkError(docLink, 'god');
            if (err) {
               setSnackbarMsgDoc(err);
               setSnackbarOpenDoc(true);
            } else {
               setSelectedPreviewUrl(docLink);
               setIsMediaAttached(true);
               setShowPostButton(true);
               setDocModalOpen(false);
               setDocLink('');
            }
         }
      } else if (docType === 'thread') {
         if (fileTypeThread === 'gos') {
            const err = _getDocLinkError(docLinkThread, 'gos');
            if (err) {
               setSnackbarMsgDoc(err);
               setSnackbarOpenDoc(true);
            } else {
               setSelectedPreviewUrlThread(docLinkThread);
               setIsMediaAttachedThread(true);
               setShowPostButtonThread(true);
               setDocModalOpen(false);
               setDocLinkThread('');
            }
         } else if (fileTypeThread === 'god') {
            const err = _getDocLinkError(docLinkThread, 'god');
            if (err) {
               setSnackbarMsgDoc(err);
               setSnackbarOpenDoc(true);
            } else {
               setSelectedPreviewUrlThread(docLinkThread);
               setIsMediaAttachedThread(true);
               setShowPostButtonThread(true);
               setDocModalOpen(false);
               setDocLinkThread('');
            }
         }
      } else if (docType === 'comment') {
         if (fileTypeComment === 'gos') {
            const err = _getDocLinkError(docLinkComment, 'gos');
            if (err) {
               setSnackbarMsgDoc(err);
               setSnackbarOpenDoc(true);
            } else {
               setSelectedPreviewUrlComment(docLinkComment);
               setIsMediaAttachedComment(true);
               setShowPostButtonComment(true);
               setDocModalOpen(false);
               setDocLinkComment('');
            }
         } else if (fileTypeComment === 'god') {
            const err = _getDocLinkError(docLinkComment, 'god');
            if (err) {
               setSnackbarMsgDoc(err);
               setSnackbarOpenDoc(true);
            } else {
               setSelectedPreviewUrlComment(docLinkComment);
               setIsMediaAttachedComment(true);
               setShowPostButtonComment(true);
               setDocModalOpen(false);
               setDocLinkComment('');
            }
         }
      } else if (docType === 'edit') {
         if (fileTypeEdit === 'gos') {
            const err = _getDocLinkError(docLinkEdit, 'gos');
            if (err) {
               setSnackbarMsgDoc(err);
               setSnackbarOpenDoc(true);
            } else {
               setSelectedPreviewUrlEdit(docLinkEdit);
               setIsMediaAttachedEdit(true);
               setDocModalOpen(false);
               setDocLinkEdit('');
            }
         } else if (fileTypeEdit === 'god') {
            const err = _getDocLinkError(docLinkEdit, 'god');
            if (err) {
               setSnackbarMsgDoc(err);
               setSnackbarOpenDoc(true);
            } else {
               setSelectedPreviewUrlEdit(docLinkEdit);
               setIsMediaAttachedEdit(true);
               setDocModalOpen(false);
               setDocLinkEdit('');
            }
         }
      }
   }


   const openFreeCommunity = (community_id) => {
      let form_data = new FormData();
      form_data.append('influencer', props.influencer)
      axios({
         method: 'post',
         url: backend_url + 'api/influencer/check_late_payment',
         data: form_data,
         headers: {
            'Content-type': 'application/json'
         },
         withCredentials: true
      }).then(res => {
         if (res.data && typeof res.data.late_payment === 'boolean') {
            if (res.data.late_payment === true) {
               props.setState({ openCheckoutError: true })
            } else {
               let data = new FormData();
               data.append('influencer', props.influencer)
               data.append('community_tab_id', community_id)
               axios(
                  {
                     method: 'post',
                     url: backend_url + 'api/influencer/unlock_free_community',
                     headers: {
                        'Content-type': 'application/json',
                     },
                     data: data,
                     withCredentials: true
                  }
               ).then(
                  res => {
                     if (!res.data.error) {
                        try { const _pid = sessionStorage.getItem('creator_meta_pixel_id'); if (_pid && window.fbq) { const _u = sessionStorage.getItem('creator_meta_pixel_uid') || 'anon'; window.fbq('trackSingle', _pid, 'Purchase', {}, {eventID: 'purchase_' + _u + '_' + Math.floor(Date.now() / 60000)}); } } catch (e) {}
                        // Update access prop for correct tab in tabs
                        setTabs(prevTabs =>
                           prevTabs.map(tab =>
                              tab.id === community_id ? { ...tab, access: true } : tab
                           )
                        );
                        // If the currently selected tab is the unlocked one, update access state for current tab object/info
                        if (tabId === community_id) {
                           setAccessToCurrentTab(true);
                           setCurrentTabInfo(prev => ({
                              ...prev,
                              access: true,
                              tags: Array.isArray(prev?.tags) ? prev.tags : []
                           }));

                           // Reload community posts data now that user has access
                           setIsloading(true);
                           const postsData = new FormData();
                           postsData.append('post_id', null);
                           postsData.append('tag', 'all');
                           axios(
                              {
                                 method: 'POST',
                                 url: backend_url + "api/influencer/community/initial_load_posts/" + props.influencer + '/' + community_id,
                                 data: postsData,
                                 headers: {
                                    'Content-type': 'application/json',
                                 },
                                 withCredentials: true
                              }
                           ).then(
                              postsRes => {
                                 setCommunityEvents(postsRes.data.events);
                                 setPostData(postsRes.data.post_info);
                                 setNPosts(postsRes.data.n_posts);
                                 if (postsRes.data.n_members !== undefined) setInfluencerTotalMembers(postsRes.data.n_members);
                                 setHasRealPosts(postsRes.data.n_posts > 0 || postsRes.data.post_info?.length > 0);

                                 if (postsRes.data.n_posts < 6) {
                                    setScrollHasMore(false);
                                 } else {
                                    setScrollHasMore(true);
                                 }
                                 setInitialLoaded(true);
                                 setIsloading(false);
                              }
                           ).catch(error => {
                              setIsloading(false);
                           });
                        }
                     }
                  }
               )
            }
         } else {
            let data = new FormData();
            data.append('influencer', props.influencer)
            data.append('community_tab_id', community_id)
            axios(
               {
                  method: 'post',
                  url: backend_url + 'api/influencer/unlock_free_community',
                  headers: {
                     'Content-type': 'application/json',
                  },
                  data: data,
                  withCredentials: true
               }
            ).then(
               res => {
                  if (!res.data.error) {
                     try { const _pid = sessionStorage.getItem('creator_meta_pixel_id'); if (_pid && window.fbq) { const _u = sessionStorage.getItem('creator_meta_pixel_uid') || 'anon'; window.fbq('trackSingle', _pid, 'Purchase', {}, {eventID: 'purchase_' + _u + '_' + Math.floor(Date.now() / 60000)}); } } catch (e) {}
                     // Update access prop for correct tab in tabs
                     setTabs(prevTabs =>
                        prevTabs.map(tab =>
                           tab.id === community_id ? { ...tab, access: true } : tab
                        )
                     );
                     // If the currently selected tab is the unlocked one, update access state for current tab object/info
                     if (tabId === community_id) {
                        setAccessToCurrentTab(true);
                        setCurrentTabInfo(prev => ({
                           ...prev,
                           access: true,
                           tags: Array.isArray(prev?.tags) ? prev.tags : []
                        }));

                        // Reload community posts data now that user has access
                        setIsloading(true);
                        const postsData = new FormData();
                        postsData.append('post_id', null);
                        postsData.append('tag', 'all');
                        axios(
                           {
                              method: 'POST',
                              url: backend_url + "api/influencer/community/initial_load_posts/" + props.influencer + '/' + community_id,
                              data: postsData,
                              headers: {
                                 'Content-type': 'application/json',
                              },
                              withCredentials: true
                           }
                        ).then(
                           postsRes => {
                              setCommunityEvents(postsRes.data.events);
                              setPostData(postsRes.data.post_info);
                              setNPosts(postsRes.data.n_posts);
                              if (postsRes.data.n_members !== undefined) setInfluencerTotalMembers(postsRes.data.n_members);
                              if (postsRes.data.n_posts > 0 || postsRes.data.post_info?.length > 0) setHasRealPosts(true);

                              if (postsRes.data.n_posts < 6) {
                                 setScrollHasMore(false);
                              } else {
                                 setScrollHasMore(true);
                              }
                              setInitialLoaded(true);
                              setIsloading(false);
                           }
                        ).catch(error => {
                           setIsloading(false);
                        });
                     }
                  }
               }
            )
         }
      })
   }
   // initial data load
   useEffect(() => {
      setIsloading(true);
      setInitialLoading(true);
      // load the initial data

      // State was initialised with getBackendUrl() at mount; only push an
      // update if it has somehow diverged.
      const new_backend_url = getBackendUrl();
      if (new_backend_url !== backend_url) {
         setBackendUrl(new_backend_url);
      }
      const data = new FormData()
      data.append('currency', props.currency);
      data.append('post_id', props.post_id);
     // Determine effective tab id: if storefront layout, prefer tab_id from URL
     const searchParamsInitial = new URLSearchParams(window.location.search);
     let effectiveTabId = props.tab_id;
   //   if ((props.pageLayoutType === 'storefront' || props.pageLayoutTypeUpdated === 'storefront')) {
      const urlTabId = searchParamsInitial.get('tab_id');
      if (urlTabId && !props.hide_tab_switcher) {
         effectiveTabId = urlTabId;
         setTabId(urlTabId);
      }
   //   }
       
      axios(
         {
            method: 'post',
           url: new_backend_url + "api/influencer/community/initial_load2/" + props.influencer + '/' + effectiveTabId,
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               // Check if this is an unlocked community preview with preview data
               if (props.isUnlockedCommunityPreview && props.previewCommunityData && props.my_platform_page !== 'edit_community') {
                  // Create preview tab from passed data
                  const previewTabTags = Array.isArray(props.previewCommunityData.tags) ? props.previewCommunityData.tags : [];
                  const previewTab = {
                     ...props.previewCommunityData,
                     tags: previewTabTags
                  };
                  
                  // Generate mock data if no tags or posts exist, or if posts exist but none have tags
                  let finalTags = previewTabTags;
                  let finalPosts = [];
                  
                  const existingPosts = res.data.post_info || [];
                  const postsHaveTags = existingPosts.some(post => 
                     post.tags_list && Array.isArray(post.tags_list) && post.tags_list.length > 0
                  );
                  
                  if (previewTabTags.length === 0 || existingPosts.length === 0 || !postsHaveTags) {
                     const { mockTags, mockPosts } = generateMockPreviewData(
                        props.influencer_name || props.influencer,
                        props.influencer_icon,
                        props.display_name || props.influencer_name,
                        t
                     );
                     
                     // Use mock tags if no tags exist
                     if (previewTabTags.length === 0) {
                        finalTags = mockTags;
                     }
                     
                     // Use mock posts if no posts exist or if posts exist but none have tags
                     if (existingPosts.length === 0 || !postsHaveTags) {
                        finalPosts = mockPosts;
                     } else {
                        finalPosts = existingPosts;
                     }
                  } else {
                     finalPosts = existingPosts;
                  }
                  
                  // Update preview tab with final tags
                  const updatedPreviewTab = {
                     ...previewTab,
                     tags: finalTags
                  };
                  
                  // Set tabs with preview data
                  setTabs([updatedPreviewTab, ...res.data.community_tab_info]);
                  setInitialLoading(false);
                  
                  // Set current tab info
                  setTabId(updatedPreviewTab.id);
                  setTabName(updatedPreviewTab.name);
                  setCurrentTabInfo(updatedPreviewTab);
                  
                  // Set post data (mock or real)
                  setPostData(finalPosts);
                  setNPosts(finalPosts.length);
                  setScrollHasMore(false);
                  setInitialLoaded(true);

                  setIsloading(false);
                  setAccessToCurrentTab(canAccessCommunity);
                  return;
               }
               const isPreviewMode = props.mobilePreview || (props.current_page_view === 'preview' && props.view_type === 'user');
               const communityTabs = Array.isArray(res.data.community_tab_info)
                  ? (isPreviewMode ? res.data.community_tab_info.filter(tab => !tab.hidden) : res.data.community_tab_info)
                  : [];
               setTabs(communityTabs);
               setInitialLoading(false);
               const searchParams = new URLSearchParams(window.location.search);
               const requestedTabId = searchParams.get('tab_id');
               let tab_access
               let n_posts
               const resolvedTabId = (
                  res.data.tab_overwrite ?? requestedTabId ?? effectiveTabId
               );
               let currentTab = null;
               if (resolvedTabId !== null && resolvedTabId !== undefined && String(resolvedTabId) !== '') {
                  currentTab = communityTabs.find(tab => String(tab.id) === String(resolvedTabId));
               }
               if (!currentTab && communityTabs.length > 0) {
                  currentTab = communityTabs[0];
               }
               if (currentTab) {
                  setTabId(currentTab.id)
                  setTabName(currentTab?.name)
                  // Ensure tags is always an array
                  const tabWithTags = currentTab ? {
                     ...currentTab,
                     tags: Array.isArray(currentTab.tags) ? currentTab.tags : []
                  } : {};
                  setCurrentTabInfo(tabWithTags)
                  setInfluencerTotalMembers(currentTab?.n_members ?? 0);
                  tab_access = currentTab?.access;
                  n_posts = currentTab?.n_posts;
                  // Free communities now work like free courses/digital products - show sales page first
                  // Removed automatic access - user must click "Get Access" button to unlock
               } else {
                  setCurrentTabInfo({});
                  setTabName('');
                  tab_access = false;
                  n_posts = 0;
               }
               setLibraryItems(res.data.library || []);
               if (tab_access === true || isCustomerPreview) {
                  setPostData(res.data.post_info)
                  setCommunityEvents(res.data.events);
                  if (n_posts === null || n_posts === undefined) {
                     n_posts = res.data.n_posts;
                  }
                  setNPosts(n_posts)
                  // Check total posts from tab model (unfiltered) OR from API response
                  const tabModelPosts = currentTab?.n_posts;
                  if ((tabModelPosts && tabModelPosts > 0) || n_posts > 0 || (res.data.post_info && res.data.post_info.length > 0)) setHasRealPosts(true);

                  if (n_posts < 6) {
                     setScrollHasMore(false);
                  } else {
                     setScrollHasMore(true);
                  }
                  setInitialLoaded(true);

                  setIsloading(false);
                  if (isCustomerPreview) {
                     setAccessToCurrentTab(true);
                  // Logged-out visitors see the sales experience.
                  } else if (props.isLoggedOutView) {
                     setAccessToCurrentTab(false);
                  } else {
                     // Use the actual access value from the API response
                     // For free communities, user must have access (subscription) to view content
                     setAccessToCurrentTab((props.mobilePreview || (props.current_page_view === 'preview' && props.view_type === 'user')) ? false : (tab_access === true));
                  }
               } else {
                  setPostData([]);
                  setNPosts(0);
                  setScrollHasMore(false);
                  setInitialLoaded(true);

                  setIsloading(false);
                  setAccessToCurrentTab(false);
               }
               // }
            })
         .catch(error => {
            setIsloading(false)
         })
   // Re-run when community tabs hidden state changes (publish/unpublish from management view)
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.activeProductData?.active_products?.[0]?.community?.tabs?.map(t => `${t.id}:${t.hidden}`).join(',')])

   function updateCommunitySubscriptionIntent(duration, promo_code) {
      // get the payment intent info
      const form_data = new FormData();
      form_data.append('influencer', props.influencer)
      form_data.append('currency', props.currency)
      form_data.append('duration', duration)
      form_data.append('community_tab_id', tabId)
      form_data.append('promo_code', promo_code)

      axios({
         method: 'post',
         url: backend_url + "api/checkout/update_community_payment_intent",
         data: form_data,
         headers: {
            'Content-type': 'application/json',
         },
         withCredentials: true
      }).then(res => {
         // Always process coupon info even if PI update failed
         if (res.data.coupon_percent_off !== undefined) {
            setCheckoutPercentOff(res.data.coupon_percent_off);
            setPromoCode(res.data.promo_code);
            setPromoError(res.data.promo_error);
            setEditedPromoCode(res.data.coupon_percent_off > 0 ? res.data.promo_code : '');
         }
         if (!res.data.error) {
            setSelectedDuration(duration);
         }
      })
   }

   useEffect(() => {
      if (initialLoaded === true) {
         if (movedToPostId === false) {
            if (String(props.post_id) !== 'null') {
               scroll(props.post_id);
            }
            setMovedToPostId(true);
         }
      }
   }, [initialLoaded])


   function getCurrentAndPreviousMonth() {
      const months = [
         "January", "February", "March", "April",
         "May", "June", "July", "August",
         "September", "October", "November", "December"
      ];

      const currentDate = new Date();
      const currentMonthIndex = currentDate.getMonth();

      let previousMonthIndex = currentMonthIndex - 1;
      let previousYear = currentDate.getFullYear();

      if (previousMonthIndex === -1) {
         previousMonthIndex = 11; // December
         previousYear--;
      }

      const currentMonth = months[currentMonthIndex];
      const previousMonth = months[previousMonthIndex];

      return {
         currentMonth,
         previousMonth
      };
   }


   const handlePostsScroll = useCallback(() => {
      if (initialLoaded === true) {
         if (scrollLoading === false) {
            setScrollLoading(true);
            const data = new FormData()
            var existing_ids = []
            const currentPostData = postDataRef.current;
            for (let i = 0; i < currentPostData.length; i++) {
               existing_ids.push(currentPostData[i].post_id)
            }
            data.append('existing_ids', existing_ids)
            data.append('tag', selectedCategory)
            axios(
               {
                  method: 'POST',
                  url: backend_url + "api/influencer/community/load_more2/" + props.influencer + '/' + tabId,
                  data: data,
                  headers: {
                     'Content-type': 'application/json',
                  },
                  withCredentials: true
               })
               .then(
                  res => {
                     setPostData(prevPostData => {
                        const newPosts = [...prevPostData, ...res.data.post_info];
                        if (res.data.n_posts <= (res.data.post_info.length + prevPostData.length)) {
                           setScrollHasMore(false);
                        }
                        return newPosts;
                     })
                     setNPosts(res.data.n_posts)
                     setIsloading(false)
                     setScrollLoading(false);
                  })
               .catch(error => {
                  setIsloading(false);
                  setScrollLoading(false);
               })
         }
      }
   }, [initialLoaded, scrollLoading, selectedCategory, tabId, props.influencer])

   // Set up Intersection Observer for infinite scroll on posts container
   useEffect(() => {
      const sentinel = scrollSentinelRef.current;
      if (!sentinel || !scrollHasMore || scrollLoading || !initialLoaded) {
         return;
      }

      const observer = new IntersectionObserver(
         (entries) => {
            const [entry] = entries;
            // When the sentinel is visible (or intersecting), load more posts
            if (entry.isIntersecting && scrollHasMore && !scrollLoading && initialLoaded) {
               handlePostsScroll();
            }
         },
         {
            // Trigger when sentinel is 200px from viewport
            rootMargin: '200px',
            threshold: 0.1,
         }
      );

      observer.observe(sentinel);

      return () => {
         if (sentinel) {
            observer.unobserve(sentinel);
         }
      };
   }, [scrollHasMore, scrollLoading, initialLoaded, handlePostsScroll]);


   const handleRemoveContent = () => {
      setSelectedFile('');
      setSelectedPreviewUrl('');
      setFileType('txt');
      setIsMediaAttached(false)
      setShowPostButton(false)
   };

   const loadAdditionalThreads = (post_id, post_index) => {
      const data = new FormData()
      data.append('post_id', post_id)
      var existing_thread_ids = []
      for (let i = 0; i < postData[post_index]['threads'].length; i++) {
         existing_thread_ids.push(postData[post_index]['threads'][i].id)
      }
      data.append('existing_thread_ids', existing_thread_ids)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/load_additional_threads",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               let new_post_data = [...postData];
               new_post_data[post_index]['threads'] = [...postData[post_index]['threads'], ...res.data.new_thread_info];
               // 5. Set the state to our new copy
               setPostData(new_post_data)
            })
         .catch(error => {

         })
   };

   const loadAdditionalComments = (thread_id, post_index, thread_index) => {
      const data = new FormData()
      data.append('thread_id', thread_id)
      var existing_comment_ids = []
      for (let i = 0; i < postData[post_index]['threads'][thread_index]['comments'].length; i++) {
         existing_comment_ids.push(postData[post_index]['threads'][thread_index]['comments'][i].id)
      }
      data.append('existing_comment_ids', existing_comment_ids)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/load_additional_comments",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               let new_post_data = [...postData];
               new_post_data[post_index]['threads'][thread_index]['comments'] = [...postData[post_index]['threads'][thread_index]['comments'], ...res.data.new_comment_info];
               // console.log(new_post_data[post_index]['threads'][thread_index]['comments'])
               // 5. Set the state to our new copy

               setPostData(new_post_data)
            })
         .catch(error => {
            // console.log(error)
         })
   };

   function extractFileExt(file_str) {
      const splitString = file_str.split('.')
      if (splitString.length === 1) {
         return '';
      }
      return splitString[splitString.length - 1]
   }

   useEffect(() => {
      controllerRef.current = controller
   }, [controller])

   async function uploadCommunityContent(
      text,
      file,
      content_type,
      actual_content_type,
      influencer_str,
      message_type,
      post_id,
      thread_id,
      comment_id,
      edit_flag,
      tab_id,
      link = null,
      meeting_time = null,
      post_tags = null,
      schedule_time = undefined,
   ) {
      async function abortMultipartUpload(initial) {
         try {
            if (initial) {
               controllerRef.current.abort()
               if (actual_content_type === 'aud-rec' || actual_content_type === 'vid-rec') {
                  handleSnackbarClick(
                     'var(--danger-color)',
                     'error',
                     t('community.upload.failedConnection'),
                     t('community.upload.andTryLater'),
                     t('community.upload.downloadRecording'),
                  );
                  if (message_type === 'post') {
                     setDownloadUrl(selectedPreviewUrl);
                  } else if (message_type === 'thread') {
                     setDownloadUrl(selectedPreviewUrlThread);
                  } else if (message_type === 'comment') {
                     setDownloadUrl(selectedPreviewUrlComment);
                  }
               } else {
                  handleSnackbar('var(--danger-color)', 'error', t('community.upload.failedConnection'));
               }
               clearInterval(networkCheckInterval.current)
               progressIntervalRef.current && clearInterval(progressIntervalRef.current)
               setIsUploading(false);
               setFileSize(0);
               setProgressFileSize(0)
               setUploadError(true);
               setSnackbarSeverity('error')
               setSnackbarOpen(true)
               setUploadSpeeds([])
               initInterval.current = false
            }
            let fd = new FormData()
            fd.append('key', fileKey)
            fd.append('uploadId', uploadId)
            const res2 = await axios({
               method: 'post',
               url: backend_url + 'api/abort_multipart_upload/',
               data: fd,
               withCredentials: true
            });
         }
         catch {
            setTimeout(() => {
               abortMultipartUpload(false)
            }, 2000);
         }
      }
      async function uploadPart(partNumber, partSize) {
         const start = partNumber * partSize;
         const end = Math.min(start + partSize, file.size);
         const chunk = file.slice(start, end);
         const url = presignedUrls[partNumber];
         const chunkSize = end - start;
         const maxRetries = 5;
         for (let retry = 0; retry < maxRetries; retry++) {
            try {
               const r = await axios.put(url, chunk, {
                  headers: {
                     'Content-Type': file.type,
                  },
                  onUploadProgress: progressEvent => {
                     partsProgress[partNumber] = (progressEvent.loaded / progressEvent.total) * chunkSize;
                  },
                  signal: controllerRef.current.signal
               });

               const etag = r.headers.etag;
               etags[partNumber] = etag;
               break; // Successful upload, exit the loop
            } catch (error) {
               if (retry >= maxRetries - 1) {
                  // Handle the failure, log, or take further action as needed
                  return Promise.reject(`Max retries reached for chunk ${partNumber + 1}`)
               }
               else {
                  // Wait for a moment before retrying
                  await new Promise(resolve => setTimeout(resolve, 1000));
               }
            }
         }
      }
      const form_data = new FormData();
      setController(new AbortController());
      // setup timeout check
      setTimeOutTimer(Date.now() / 1000);
      if (content_type === 'txt') {
         setUploadingTitle('Text...');
      } else if (content_type === 'img') {
         setUploadingTitle('Image...');
      } else if (content_type === 'aud') {
         setUploadingTitle('Audio...');
      } else if (content_type === 'vid') {
         setUploadingTitle('Video...');
      } else if (content_type === 'vey') {
         setUploadingTitle('Youtube...');
      } else if (content_type === 'vel') {
         setUploadingTitle('Loom...');
      } else if (content_type === 'vev') {
         setUploadingTitle('Vimeo...');
      } else if (content_type === 'vtw') {
         setUploadingTitle('Twitch...');
      } else if (content_type === 'vwi') {
         setUploadingTitle('Wistia...');
      } else if (content_type === 'mgm') {
         setUploadingTitle('Google Meet...');
      } else if (content_type === 'mot') {
         setUploadingTitle('Meeting...');
      } else if (content_type === 'mmt') {
         setUploadingTitle('Microsoft Teams...');
      } else if (content_type === 'mot') {
         setUploadingTitle('Meeting Other...');
      } else if (content_type === 'gos') {
         setUploadingTitle('Google Sheet...');
      } else if (content_type === 'god') {
         setUploadingTitle('Google Doc...');
      }
      //TODO: Sanatise the data and check it has everything needed for a succesful post
      setIsUploading(true);
      setUploadPercent(0);
      setUploadMsg('Preparing file...');
      setUploadStep('Step 1 of 3:');
      // Set upload type (multipart or single)
      let multipartUpload = false
      multipartUpload =
         !(window.location.host.includes('localhost')) && (content_type.toLowerCase() === 'vid' || content_type.toLowerCase() === 'aud') && file.size > 100 * Math.pow(1024, 2) // 100MB
      form_data.append('type', content_type.toLowerCase());
      // If a file is attached, always include its extension (including optional images for event posts).
      if (file && file.name && (content_type !== 'txt') && !['vel', 'vey', 'vev', 'vtw', 'vwi', 'gos', 'god'].includes(content_type)) {
         form_data.append('ext', extractFileExt(file.name));
      }
      if (multipartUpload) {
         form_data.append('upload_type', 'multipart')
      }
      else {
         form_data.append('upload_type', 'single')
      }
      if (message_type === 'post') {
         form_data.append('tab_id', tab_id)
         // JSON, not the array itself: FormData coerces an array with
         // Array.toString(), which comma-joins without quoting, so a tag whose
         // own name contains a comma ("meg, help!") arrived at the backend as
         // two tags that matched nothing and the post saved with no tags.
         form_data.append('post_tags', JSON.stringify(post_tags || []))
      }
      form_data.append('influencer_str', influencer_str)
      form_data.append('post_id', post_id)
      form_data.append('thread_id', thread_id)
      form_data.append('comment_id', comment_id)
      form_data.append('text', text)
      // Setup for Multipart uploads
      form_data.append('content_type', file ? file.type : '')
      if (link) {
         form_data.append('link', link)
      }
      // console.log(meeting_time)
      // console.log(content_type)
      if (['mgm', 'mot', 'mzm', 'mmt'].includes(content_type)) {
         form_data.append('event_time', meeting_time)
      }
      if (message_type === 'post' && schedule_time !== undefined) {
         form_data.append('schedule_date', schedule_time ? schedule_time.split('.')[0] : 'null')
      }
      let uploadId = ''
      let presignedUrls = []
      let fileKey = ''
      const partSize = 10 * 1024 * 1024; // 10MB part size
      const parts = file ? Math.ceil(file.size / partSize) : 0
      let partsProgress = new Array(parts).fill(0)
      let etags = new Array(parts)
      form_data.append('num_parts', parts)
      let url = ''

      if (message_type === 'post') {
         if (edit_flag === true) {
            url = backend_url + "api/influencer/community/edit_post_v2"
         } else {
            url = backend_url + "api/influencer/community/submit_post_v3"
         }
      } else if (message_type === 'thread') {
         if (edit_flag === true) {
            url = backend_url + "api/influencer/community/edit_thread_v2"
         } else {
            url = backend_url + "api/influencer/community/submit_thread_v2"
         }
      } else if (message_type === 'comment') {
         if (edit_flag === true) {
            url = backend_url + "api/influencer/community/edit_comment_v2"
         } else {
            url = backend_url + "api/influencer/community/submit_comment_v2"
         }
      }
      try {
         const response = await axios({
            method: 'post',
            url: url,
            data: form_data,
            headers: {
               'Content-type': 'application/json',
            },
            // signal: this.state.controller.signal,
            withCredentials: true
         });
         if (response.data.access_denied) {
            throw new Error('Access Denied');
         }
         // Only need this step for file based uploads.
         const shouldUploadToS3 = !!(file && file.name && (response.data.upload_uri || response.data.urls));
         if (shouldUploadToS3) {
            // Post to S3
            // const r = await putFile(response.data.upload_uri, file)
            setUploadPercent(0);
            setUploadMsg(t('community.upload.uploading'));
            setUploadStep('Step 2 of 3:');
            multipartUpload = response.data.multipart
            if (multipartUpload) {
               presignedUrls = response.data.urls
               uploadId = response.data.uploadId
               fileKey = response.data.fileKey
               const uploadPromises = [];
               for (let i = 0; i < parts; i++) {
                  uploadPromises.push(uploadPart(i, partSize));
               }
               const progressInterval = progressIntervalRef.current = setInterval(() => {
                  const progress = partsProgress.reduce((a, b) => a + b, 0)
                  const percent = 100 * progress / parts
                  if (!initInterval.current) {
                     // Only runs once
                     initInterval.current = true
                     setUploadStartTime(Date.now());
                     setProgressFileSize(file.size);
                  }

                  let uploadString = ''
                  if (file.size >= 1048576) {
                     const uploadMB = Math.round(progress / 1048576)
                     const totalSizeMB = Math.round(file.size / 1048576)
                     uploadString = `${uploadMB}MB of ${totalSizeMB}MB`
                  }
                  else if (file.size >= 1024) {
                     const uploadKB = Math.round(progress / 1024)
                     const totalSizeKB = Math.round(file.size / 1024)
                     uploadString = `${uploadKB}KB of ${totalSizeKB}KB`
                  }
                  else {
                     const uploadBytes = Math.round(progress)
                     const totalSizeBytes = Math.round(file.size)
                     uploadString = `${uploadBytes}Bytes of ${totalSizeBytes}Bytes`
                  }
                  setUploadPercent((progress / file.size) * 100)
                  setUploadMsg(`Uploading...(${uploadString})`)
                  if (percent === 100) {
                     clearInterval(progressInterval)
                  }
               }, 300);

               // Upload parts in parallel
               await Promise.all(uploadPromises);

               clearInterval(progressInterval)
               const partsInfo = etags.map(({ ETag }, i) => ({
                  ETag: etags[i],
                  PartNumber: i + 1,
               }))
               let data = new FormData()
               data.append('key', fileKey)
               data.append('uploadId', uploadId)
               data.append('partsInfo', partsInfo)
               data.append('etags', etags)
               const res2 = await axios({
                  method: 'post',
                  url: backend_url + 'api/complete_multipart_upload/',
                  data: data,
                  withCredentials: true
               });
            } else {
               const r =
                  await axios.put(
                     response.data.upload_uri,
                     file,
                     {
                        headers: {
                           'Content-Type': file.type
                        },
                        signal: controller.signal,
                        onUploadProgress: progressEvent => {
                           if (!initInterval.current) {
                              // Only runs once
                              initInterval.current = true
                              setUploadStartTime(Date.now());
                              setProgressFileSize(progressEvent.total);
                           }
                           let uploadString = ''
                           if (progressEvent.total >= 1048576) {
                              const uploadMB = Math.round(progressEvent.loaded / 1048576)
                              const totalSizeMB = Math.round(progressEvent.total / 1048576)
                              uploadString = `${uploadMB}MB of ${totalSizeMB}MB`
                           }
                           else if (progressEvent.total >= 1024) {
                              const uploadKB = Math.round(progressEvent.loaded / 1024)
                              const totalSizeKB = Math.round(progressEvent.total / 1024)
                              uploadString = `${uploadKB}KB of ${totalSizeKB}KB`
                           }
                           else {
                              const uploadBytes = Math.round(progressEvent.loaded)
                              const totalSizeBytes = Math.round(progressEvent.total)
                              uploadString = `${uploadBytes}Bytes of ${totalSizeBytes}Bytes`
                           }
                           setUploadPercent((progressEvent.loaded / progressEvent.total) * 100)
                           setUploadMsg(`Uploading...(${uploadString})`)
                        }
                     }
                  )
            }
            setUploadPercent(100);
            setUploadMsg('Processing...');
            setUploadStep('Step 3 of 3');
            setTimeRemaining(0);
            // Notify WithMe of completed upload
            if (edit_flag === true) {
               const response2 = await notifyUploadCompleteCommunityEdit(response.data.message_id, response.data.message_type, response.data.text, response.data.content_type)
               if (response2.data.access_denied) {
                  throw new Error('Access Denied');
               }
            } else {
               const response2 = await notifyUploadCompleteCommunity(response.data.message_id, response.data.message_type)
               if (response2.data.access_denied) {
                  throw new Error('Access Denied');
               }
            }
         } else {
            setUploadPercent(100);
            setUploadMsg('Finished');
            setTimeRemaining(0);
            // update the vars
         }
         setUploadMsg('Finished');
         setUploadStep('');
         setIsUploading(false)
         clearInterval(networkCheckInterval.current)
         progressIntervalRef.current && clearInterval(progressIntervalRef.current)
         setFileSize(0);
         setProgressFileSize(0)
         // setSnackbarMsg(`Done`);
         // setSnackbarSeverity('success')
         // setSnackbarOpen(true)
         setUploadSpeeds([])
         setTimeRemaining(null)
         initInterval.current = false
         return response.data.content
      } catch (err) {
         if (multipartUpload) {
            abortMultipartUpload(true)
         }
         clearInterval(networkCheckInterval.current)
         progressIntervalRef.current && clearInterval(progressIntervalRef.current)
         setIsUploading(false);
         setFileSize(0);
         setProgressFileSize(0)
         setUploadError(true);
         setSnackbarSeverity('error')
         setSnackbarOpen(true)
         setUploadSpeeds([])
         initInterval.current = false
         const error = err.response?.data?.error
         if (error === 'Invalid file type for video') {
            handleSnackbar('var(--danger-color)', 'error', `${error}. Please upload a video with one of the following file types: mp4, mov`);
         }
         else if (error === 'Invalid file type for audio') {
            handleSnackbar('var(--danger-color)', 'error', `${error}. Please upload audio with one of the following file types: mp3, mp4, wav, aac, m4a`);
         }
         else if (error === 'Invalid file type for image') {
            handleSnackbar('var(--danger-color)', 'error', `${error}. Please upload an image with one of the following file types: jpeg, jpg, png`);
         }
         else if (error === 'Invalid file type for pdf') {
            handleSnackbar('var(--danger-color)', 'error', `${error}. Invalid file type. Please upload a pdf file`);
         }
         else {
            if (actual_content_type === 'aud-rec' || actual_content_type === 'vid-rec') {
               handleSnackbarClick(
                  'var(--danger-color)',
                  'error',
                  t('community.upload.failedConnection'),
                  t('community.upload.andTryLater'),
                  t('community.upload.downloadRecording'),
               );
               if (message_type === 'post') {
                  setDownloadUrl(selectedPreviewUrl);
               } else if (message_type === 'thread') {
                  setDownloadUrl(selectedPreviewUrlThread);
               } else if (message_type === 'comment') {
                  setDownloadUrl(selectedPreviewUrlComment);
               }
            } else {
               handleSnackbar('var(--danger-color)', 'error', t('community.upload.failedConnection'));
            }
         }
         throw new Error('Server error')
      }
   }

   async function submitPost(tab_id) {
      if (blockPreviewWrite()) return;

      if (props.late_payment) { goToBilling(); return; }
      // console.log(post_tier)
      setSubmitPostIsLoading(true)
      
      // Create any pending categories before submitting the post
      if (pendingCategories.length > 0) {
         try {
            // Filter out categories that already exist
            const existingTags = currentTabInfo?.tags || [];
            const categoriesToCreate = pendingCategories.filter(cat => !existingTags.includes(cat));
            
            // Create all pending categories
            for (const categoryName of categoriesToCreate) {
               await addCommunityCategory(categoryName);
            }
            
            // Clear pending categories after successful creation
            setPendingCategories([]);
         } catch (error) {
            handleSnackbar('var(--danger-color)', 'error', 'Failed to create categories. Please try again.');
            setSubmitPostIsLoading(false);
            return;
         }
      }
      
      let content_info;
      content_info = await uploadCommunityContent(
         newPostText,
         selectedFile,
         fileType === 'aud-rec' ? 'aud' : fileType === 'vid-rec' ? 'vid' : fileType,
         fileType,
         props.influencer,
         'post',
         '',
         '',
         '',
         false,
         tab_id,
         ['gos', 'god', 'mot', 'mzm', 'mgm', 'mmt'].includes(fileType) ? selectedPreviewUrl : ['vey', 'vel', 'vev', 'vtw', 'vwi'].includes(fileType) ? embedUrlKey : null,
         ['mgm', 'mot', 'mzm', 'mmt'].includes(fileType) ? postMeetingTime : null,
         uploadPostTags,
         postScheduleDate,
      )
      setSubmitPostIsLoading(true);
      const data = new FormData()
      data.append('influencer_str', props.influencer)
      data.append('tab_id', currentTabInfo.id)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/load_latest_post",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         }
      ).then(
         res => {
            setCommunityEvents(res.data.events);
            setNewPostText('');
            setNewPostTextLength(0);
            setSubmitPostIsLoading(false)
            setSelectedFile('');
            setSelectedPreviewUrl('');
            setFileType('txt');
            setIsMediaAttached(false)
            setShowPostButton(false)
            setUploadPostTags(['all']); // Reset to 'all' after post submission
            setPendingCategories([]); // Clear pending categories after successful submission

            const isScheduled = !!(res.data.new_post_info?.[0]?.scheduled_date && new Date(res.data.new_post_info[0].scheduled_date) > new Date());
            setPostScheduleDate(null);

            // load the new post info
            let new_post_data = []
            if (postData.length === 0) {
               new_post_data = [res.data.new_post_info[0]]
               // } else if (postData[0]['pinned'] === true){
               // new_post_data = [...postData.slice(0,1), res.data.new_post_info[0], ...postData.slice(1)];
            } else {
               new_post_data = [res.data.new_post_info[0], ...postData];
            }
            if (new_post_data.length > 0) {
               new_post_data[0]['tags_list'] = Array.isArray(uploadPostTags) ? [...uploadPostTags] : [];
            }
            setPostData(new_post_data);
            setHasRealPosts(true);

            // Switch to the category that the post was submitted to
            // Get the tags before resetting uploadPostTags
            const postTags = Array.isArray(uploadPostTags) ? uploadPostTags.filter(tag => tag && tag !== 'all' && tag.trim() !== '') : [];
            if (postTags.length > 0) {
               // Switch to the first tag (category) that the post was assigned to
               changeCategory(postTags[0]);
            }

            handleSnackbar('var(--brand-color)', 'success', isScheduled ? t('community.composer.scheduledFor', { date: formatDateTimeYMD(new Date(res.data.new_post_info[0].scheduled_date)) }) : t('community.post.submitSuccess'))
            // If the post is an event (has event_time), scroll to top
            if (['mgm', 'mzm', 'mmt', 'mot'].includes(res.data.new_post_info[0]?.content_type) && res.data.new_post_info[0]?.event_time) {
               setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
               }, 100);
            }
            // Scheduled posts aren't live yet, so skip notifying subscribers.
            if (!isScheduled) {
               if ((props.influencer) === (res.data.new_post_info[0].username)) {
                  window.Intercom('update', {
                     "active_on_community_last_7_days": true,
                  });
               }
               const data2 = new FormData()
               data2.append('post_id', res.data.new_post_info[0].post_id)
               axios(
                  {
                     method: 'POST',
                     url: backend_url + "api/influencer/community/send_new_post_community_email",
                     data: data2,
                     headers: {
                        'Content-type': 'application/json',
                     },
                     withCredentials: true
                  })
            }
      })
   }

   // console.log(postData)

   const postLikeChange = (post_id, post_index) => {
      if (blockPreviewWrite()) return;
      const data = new FormData()
      data.append('post_id', post_id)
      data.append('post_index', post_index)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/post_like_update",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               let new_post_data = [...postData];
               new_post_data[res.data.post_index]['like_count'] = res.data.post_like_count
               new_post_data[res.data.post_index]['user_like_value'] = res.data.like_val
               setPostData(new_post_data)
            })
         .catch(error => {
         })
   };

   const threadLikeChange = (thread_id, post_index, thread_index) => {
      if (blockPreviewWrite()) return;
      // load the initial data
      const data = new FormData()
      data.append('thread_id', thread_id)
      data.append('post_index', post_index)
      data.append('thread_index', thread_index)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/thread_like_update",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               let new_post_data = [...postData];
               new_post_data[res.data.post_index]['threads'][res.data.thread_index]['like_count'] = res.data.thread_like_count
               new_post_data[res.data.post_index]['threads'][res.data.thread_index]['user_like_value'] = res.data.like_val
               setPostData(new_post_data)
            })
         .catch(error => {
         })
   };

   const commentLikeChange = (comment_id, post_index, thread_index, comment_index) => {
      // load the initial data
      const data = new FormData()
      data.append('comment_id', comment_id)
      data.append('post_index', post_index)
      data.append('thread_index', thread_index)
      data.append('comment_index', comment_index)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/comment_like_update",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               let new_post_data = [...postData];
               new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'][res.data.comment_index]['like_count'] = res.data.comment_like_count
               new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'][res.data.comment_index]['user_like_value'] = res.data.like_val
               setPostData(new_post_data)
            })
         .catch(error => {
         })
   };

   async function editPost(newText, post_id, post_index, link, scheduleDateToSend = undefined) {
      if (blockPreviewWrite()) return;
      // Create any pending edit category before submitting the post edit
      if (pendingEditCategories && pendingEditCategories.length > 0) {
         try {
            const pendingCategory = pendingEditCategories[0];
            // Check if category already exists
            const existingTags = currentTabInfo?.tags || [];
            
            if (!existingTags.includes(pendingCategory)) {
               // Create the pending category
               await addCommunityCategory(pendingCategory);
            }
            
            // Clear pending edit category after successful creation
            setPendingEditCategories([]);
         } catch (error) {
            handleSnackbar('var(--danger-color)', 'error', 'Failed to create category. Please try again.');
            return;
         }
      }
      
      // Capture the current editPostTags value to avoid closure issues
      // Filter out 'all' as it's not a real tag category
      const currentEditPostTags = Array.isArray(editPostTags) 
         ? editPostTags.filter(tag => tag && tag !== 'all' && tag.trim() !== '')
         : [];
      
      // Get existing event_time from post data as fallback if meetingTimeEditPost is empty
      const existingPost = postData[post_index];
      const existingEventTime = existingPost?.event_time || null;
      
      // Use meetingTimeEditPost if available, otherwise fall back to existing event_time
      const eventTimeToSend = ['mgm', 'mot', 'mzm', 'mmt'].includes(fileTypeEdit) 
         ? (meetingTimeEditPost || existingEventTime || null)
         : null;
      
      let content_info;
      content_info = await uploadCommunityContent(
         newText,
         selectedFileEdit,
         fileTypeEdit === 'aud-rec' ? 'aud' : fileTypeEdit === 'vid-rec' ? 'vid' : fileTypeEdit,
         fileTypeEdit,
         props.influencer,
         'post',
         post_id,
         '',
         '',
         true,
         null,
         ['mot', 'mzm', 'mgm', 'mmt', 'gos', 'god'].includes(fileTypeEdit) ? link : ['vey', 'vel', 'vev', 'vtw', 'vwi'].includes(fileTypeEdit) ? embedUrlKeyEdit : null,
         eventTimeToSend,
         currentEditPostTags,
         scheduleDateToSend,
      )
      const data = new FormData()
      data.append('post_index', post_index)
      data.append('post_id', post_id)
      data.append('tab_id', currentTabInfo.id)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/load_edited_post",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               setCommunityEvents(res.data.events);
               setResetPostEditTextId(post_id)
               let new_post_data = [...postData];
               new_post_data[res.data.post_index]['text'] = res.data.text
               new_post_data[res.data.post_index]['file'] = res.data.file
               new_post_data[res.data.post_index]['content_type'] = res.data.content_type
               new_post_data[res.data.post_index]['community_post_title'] = res.data.community_post_title
               new_post_data[res.data.post_index]['link'] = res.data.link
               // Update event_time if it exists in the response
               if (res.data.event_time !== undefined) {
                  new_post_data[res.data.post_index]['event_time'] = res.data.event_time;
               }
               if (res.data.scheduled_date !== undefined) {
                  new_post_data[res.data.post_index]['scheduled_date'] = res.data.scheduled_date;
               }
               // Use tags from backend response if available, otherwise use the captured tags value
               if (res.data.tags_list !== undefined && Array.isArray(res.data.tags_list)) {
                  new_post_data[res.data.post_index]['tags_list'] = res.data.tags_list;
               } else {
                  // Fallback: Use the captured tags value that was sent to the backend
                  // Ensure we have a valid array (filter out 'all' if present)
                  const updatedTags = currentEditPostTags.length > 0 
                     ? currentEditPostTags.filter(tag => tag && tag !== 'all' && tag.trim() !== '')
                     : [];
                  new_post_data[res.data.post_index]['tags_list'] = updatedTags
               }
               setPostData(new_post_data)
               
               // Update tags list if backend returned updated tags
               if (res.data.updated_tags !== undefined && Array.isArray(res.data.updated_tags)) {
                  // Update currentTabInfo with the new tags
                  setCurrentTabInfo(prev => ({
                     ...prev,
                     tags: res.data.updated_tags
                  }));
                  
                  // Update tabs array to reflect the updated tags
                  setTabs(prevTabs => 
                     prevTabs.map(tab => 
                        tab.id === currentTabInfo.id 
                           ? { ...tab, tags: res.data.updated_tags }
                           : tab
                     )
                  );
                  
                  // Get the post's tags from the response (the tags it was edited to)
                  const postTags = Array.isArray(res.data.tags_list) 
                     ? res.data.tags_list.filter(tag => tag && tag !== 'all' && tag.trim() !== '')
                     : [];
                  
                  // Switch to the category that the post was edited to
                  if (postTags.length > 0) {
                     // Switch to the first tag (category) that the post was assigned to
                     changeCategory(postTags[0]);
                  } else {
                     // If post has no tags, check if the currently selected category still exists
                     // If not, switch to the first available category with posts
                     const currentCategory = selectedCategory.toLowerCase();
                     const updatedTagsLower = res.data.updated_tags.map(tag => tag.toLowerCase());
                     
                     // If selected category is 'all' or 'events', keep it
                     if (currentCategory === 'all' || currentCategory === 'events') {
                        // Do nothing, keep the current selection
                     } else if (!updatedTagsLower.includes(currentCategory)) {
                        // Selected category was deleted, switch to first available category
                        if (res.data.updated_tags.length > 0) {
                           // Switch to the first category from the updated tags
                           const firstCategory = res.data.updated_tags[0];
                           changeCategory(firstCategory);
                        } else {
                           // No categories left, switch to 'all'
                           changeCategory('all');
                        }
                     }
                  }
               } else {
                  // If updated_tags not in response, still try to switch to post's category
                  const postTags = Array.isArray(res.data.tags_list) 
                     ? res.data.tags_list.filter(tag => tag && tag !== 'all' && tag.trim() !== '')
                     : [];
                  
                  if (postTags.length > 0) {
                     // Switch to the first tag (category) that the post was assigned to
                     changeCategory(postTags[0]);
                  }
               }
               
               setSubmitPostIsLoading(false)
               handleSnackbar('var(--brand-color)', 'success', t('community.post.editSuccess'));
               setSelectedFileEdit('');
               setSelectedPreviewUrlEdit('');
               setFileTypeEdit('txt');
               setIsMediaAttachedEdit(false);
               setEditPostTags(['all']);
               setPendingEditCategories([]); // Clear pending edit categories after successful submission
               // Clear event image edit state
               if (meetingImageEdit && meetingImageEdit.startsWith('blob:')) {
                  URL.revokeObjectURL(meetingImageEdit);
               }
               setMeetingImageEdit('');
               setMeetingImageFileNameEdit('');
               setMeetingType('');
               setSelectedFileEdit('');
            })

         .catch(error => {
            handleSnackbar('var(--danger-color)', 'error', t('community.upload.failedConnection'))
         })
   };


   const deletePost = (post_id, post_index) => {
      if (blockPreviewWrite()) return;
      // load the initial data
      const data = new FormData()
      data.append('post_id', post_id)
      data.append('post_index', post_index)
      data.append('tab_id', currentTabInfo.id)
      data.append('influencer_str', props.influencer)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/delete_post",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               if (String(res.data.success) === 'true') {
                  // Close the post preview modal if the deleted post is the one being viewed
                  if (modalOpen && selectedPostIndex !== null && selectedPostIndex === post_index) {
                     handleModalClose();
                  }
                  
                  let newPostData;
                  if (res.data.remove_index === 0) {
                     newPostData = postData.slice(1);
                  } else if (res.data.remove_index === (postData.length - 1)) {
                     newPostData = postData.slice(0, res.data.remove_index);
                  } else {
                     newPostData = [...postData.slice(0, res.data.remove_index), ...postData.slice(res.data.remove_index + 1)];
                  }
                  setPostData(newPostData);
                  if (newPostData.length === 0) setHasRealPosts(false);
                  setCommunityEvents(res.data.events);
                  
                  // Update tags list if backend returned updated tags
                  if (res.data.updated_tags !== undefined && Array.isArray(res.data.updated_tags)) {
                     // Update currentTabInfo with the new tags
                     setCurrentTabInfo(prev => ({
                        ...prev,
                        tags: res.data.updated_tags
                     }));
                     
                     // Update tabs array to reflect the updated tags
                     setTabs(prevTabs => 
                        prevTabs.map(tab => 
                           tab.id === currentTabInfo.id 
                              ? { ...tab, tags: res.data.updated_tags }
                              : tab
                        )
                     );
                     
                     // Check if the currently selected category still exists
                     // If not, switch to the first available category with posts
                     const currentCategory = selectedCategory.toLowerCase();
                     const updatedTagsLower = res.data.updated_tags.map(tag => tag.toLowerCase());
                     
                     // If selected category is 'all' or 'events', keep it
                     if (currentCategory === 'all' || currentCategory === 'events') {
                        // Do nothing, keep the current selection
                     } else if (!updatedTagsLower.includes(currentCategory)) {
                        // Selected category was deleted, switch to first available category
                        if (res.data.updated_tags.length > 0) {
                           // Switch to the first category from the updated tags
                           const firstCategory = res.data.updated_tags[0];
                           changeCategory(firstCategory);
                        } else {
                           // No categories left, switch to 'all'
                           changeCategory('all');
                        }
                     }
                  }
               }

               handleSnackbar('var(--brand-color)', 'success', t('community.post.deleteSuccess'))
            })
         .catch(error => {

            handleSnackbar('var(--danger-color)', 'error', t('dashboard.paidDms.deletePostError'))
            // setSnackbarColor('var(--danger-color)')
            // setSnackbarType('error');
            // setSnackbarMsg("There was an issue deleting your post")
            // setSnackbarOpen(true)
         })
   };

   async function submitThread(newThreadText, post_id, post_index) {
      if (blockPreviewWrite()) return;
      if (props.late_payment) { goToBilling(); return; }
      // load the initial data
      const data = new FormData()
      let content_info;
      content_info = await uploadCommunityContent(
         newThreadText,
         selectedFileThread,
         fileTypeThread === 'aud-rec' ? 'aud' : fileTypeThread === 'vid-rec' ? 'vid' : fileTypeThread,
         fileTypeThread,
         props.influencer,
         'thread',
         post_id,
         '',
         '',
         false,
         null,
         ['mot', 'mzm', 'mgm', 'mmt', 'gos', 'god'].includes(fileTypeThread) ? selectedPreviewUrlThread : ['vey', 'vel', 'vev', 'vtw', 'vwi'].includes(fileTypeThread) ? embedUrlKeyThread : null,
         ['mgm', 'mot', 'mzm', 'mmt'].includes(fileTypeThread) ? meetingTimeThread : null,
      )
      setSubmitPostIsLoading(true);
      data.append('post_id', post_id)
      data.append('post_index', post_index)
      data.append('tab_id', currentTabInfo.id)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/load_latest_thread",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               setResetThreadTextId(post_id);
               setCommunityEvents(res.data.events);
               let new_post_data = [...postData]
               new_post_data[res.data.post_index]['threads'] = [res.data.new_thread_info, ...postData[res.data.post_index]['threads']]
               new_post_data[res.data.post_index]['n_threads'] = new_post_data[res.data.post_index]['n_threads'] + 1
               setPostData(new_post_data);
               handleSnackbar('var(--brand-color)', 'success', t('dashboard.paidDms.threadSubmitted'))
               if ((props.influencer) === (res.data.new_thread_info.username)) {
                  window.Intercom('update', {
                     "active_on_community_last_7_days": true,
                  });
               }
               const data = new FormData()
               data.append('thread_id', res.data.new_thread_info.id)
               setSelectedFileThread('');
               setSelectedPreviewUrlThread('');
               setSelectedPreviewIdThread('');
               setFileTypeThread('txt');
               setIsMediaAttachedThread(false)
               axios(
                  {
                     method: 'POST',
                     url: backend_url + "api/influencer/community/send_new_thread_community_email",
                     data: data,
                     headers: {
                        'Content-type': 'application/json',
                     },
                     withCredentials: true
                  })
            })
         .catch(error => {
            handleSnackbar('var(--danger-color)', 'error', t('community.upload.failedConnection'))
         })
   };

   async function editThread(thread_id, text, post_index, thread_index, post_id, link) {
      // load the initial data
      let content_info;
      content_info = await uploadCommunityContent(
         text,
         selectedFileEdit,
         fileTypeEdit === 'aud-rec' ? 'aud' : fileTypeEdit === 'vid-rec' ? 'vid' : fileTypeEdit,
         fileTypeEdit,
         props.influencer,
         'thread',
         '',
         thread_id,
         '',
         true,
         null,
         ['mot', 'mzm', 'mgm', 'mmt', 'gos', 'god'].includes(fileTypeEdit) ? link : ['vey', 'vel', 'vev', 'vtw', 'vwi'].includes(fileTypeEdit) ? embedUrlKeyEdit : null,
         ['mgm', 'mot', 'mzm', 'mmt'].includes(fileTypeEdit) ? meetingTimeEditThread : null,
      )
      const data = new FormData()
      data.append('post_index', post_index)
      data.append('thread_id', thread_id)
      data.append('thread_index', thread_index)
      data.append('tab_id', currentTabInfo.id)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/load_edited_thread",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               setCommunityEvents(res.data.events);
               setResetThreadEditTextId(post_id)
               let new_post_data = [...postData]
               new_post_data[res.data.post_index]['threads'][res.data.thread_index].text = res.data.text
               new_post_data[res.data.post_index]['threads'][res.data.thread_index].file = res.data.file
               new_post_data[res.data.post_index]['threads'][res.data.thread_index].content_type = res.data.content_type
               new_post_data[res.data.post_index]['threads'][res.data.thread_index].link = res.data.link
               setPostData(new_post_data);
               setSubmitPostIsLoading(false)
               handleSnackbar('var(--brand-color)', 'success', t('dashboard.paidDms.threadEdited'));
               setSelectedFileEdit('');
               setSelectedPreviewUrlEdit('');
               setFileTypeEdit('txt');
               setIsMediaAttachedEdit(false);
            })
         .catch(error => {
            handleSnackbar('var(--danger-color)', 'error', t('community.upload.failedConnection'))
         })
   };


   const deleteThread = (thread_id, post_index, thread_index) => {
      if (blockPreviewWrite()) return;
      // load the initial data
      const data = new FormData()
      data.append('thread_id', thread_id)
      data.append('thread_index', thread_index)
      data.append('post_index', post_index)
      data.append('tab_id', currentTabInfo.id)
      data.append('influencer_str', props.influencer)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/delete_thread",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               if (String(res.data.success) === 'true') {
                  if (res.data.thread_index === 0) {
                     let new_post_data = [...postData]
                     new_post_data[post_index]['threads'] = new_post_data[post_index]['threads'].slice(1)
                     new_post_data[post_index]['n_threads'] = new_post_data[post_index]['n_threads'] - 1
                     setPostData(new_post_data)
                  } else if (res.data.thread_index === (postData[post_index]['threads'].length - 1)) {
                     let new_post_data = [...postData]
                     new_post_data[post_index]['threads'] = new_post_data[post_index]['threads'].slice(0, res.data.thread_index)
                     new_post_data[post_index]['n_threads'] = new_post_data[post_index]['n_threads'] - 1
                     setPostData(new_post_data)
                  } else {
                     let new_post_data = [...postData]
                     new_post_data[post_index]['threads'] = [...new_post_data[post_index]['threads'].slice(0, res.data.thread_index), ...new_post_data[post_index]['threads'].slice(res.data.thread_index + 1)]
                     new_post_data[post_index]['n_threads'] = new_post_data[post_index]['n_threads'] - 1
                     setPostData(new_post_data)
                  }
                  setCommunityEvents(res.data.events);
               }
            })
         .catch(error => {
            handleSnackbar('var(--danger-color)', 'error', t('dashboard.paidDms.deleteThreadError'))
         })
   };


   async function submitComment(commentText, thread_id, post_index, thread_index, post_id) {
      if (blockPreviewWrite()) return;
      if (props.late_payment) { goToBilling(); return; }
      // load the initial data
      const data = new FormData()
      let content_info;
      content_info = await uploadCommunityContent(
         commentText,
         selectedFileComment,
         fileTypeComment === 'aud-rec' ? 'aud' : fileTypeComment === 'vid-rec' ? 'vid' : fileTypeComment,
         fileTypeComment,
         props.influencer,
         'comment',
         '',
         thread_id,
         '',
         false,
         null,
         ['mot', 'mzm', 'mgm', 'mmt', 'gos', 'god'].includes(fileTypeComment) ? selectedPreviewUrlComment : ['vey', 'vel', 'vev', 'vtw', 'vwi'].includes(fileTypeComment) ? embedUrlKeyComment : null,
         ['mgm', 'mot', 'mzm', 'mmt'].includes(fileTypeComment) ? meetingTimeComment : null,
      )
      setSubmitPostIsLoading(true);
      data.append('thread_id', thread_id)
      data.append('post_index', post_index)
      data.append('thread_index', thread_index)
      data.append('tab_id', currentTabInfo.id)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/load_latest_comment",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               setResetCommentTextId(post_id);
               let new_post_data = [...postData]
               new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'] = [res.data.new_comment_info, ...new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments']]
               new_post_data[res.data.post_index]['threads'][res.data.thread_index]['n_comments'] = new_post_data[res.data.post_index]['threads'][res.data.thread_index]['n_comments'] + 1
               handleSnackbar('var(--brand-color)', 'success', t('community.comment.submitSuccess'))
               if ((props.influencer) === (res.data.new_comment_info.commenter)) {
                  window.Intercom('update', {
                     "active_on_community_last_7_days": true,
                  });
               }
               setPostData(new_post_data);
               setSelectedFileComment('');
               setSelectedPreviewUrlComment('');
               setFileTypeComment('txt');
               setSelectedPreviewIdComment('');
               setIsMediaAttachedComment(false)
               const data = new FormData()
               data.append('comment_id', res.data.new_comment_info.id)
               axios(
                  {
                     method: 'POST',
                     url: backend_url + "api/influencer/community/send_new_comment_community_email",
                     data: data,
                     headers: {
                        'Content-type': 'application/json',
                     },
                     withCredentials: true
                  })
            })
         .catch(error => {
            handleSnackbar('var(--danger-color)', 'error', t('community.upload.failedConnection'))
         })
   };

   async function editComment(text, comment_id, post_index, thread_index, comment_index, post_id, link) {
      // load the initial data
      let content_info;
      content_info = await uploadCommunityContent(
         text,
         selectedFileEdit,
         fileTypeEdit === 'aud-rec' ? 'aud' : fileTypeEdit === 'vid-rec' ? 'vid' : fileTypeEdit,
         fileTypeEdit,
         props.influencer,
         'comment',
         '',
         '',
         comment_id,
         true,
         null,
         ['mot', 'mzm', 'mgm', 'mmt', 'gos', 'god'].includes(fileTypeEdit) ? link : ['vey', 'vel', 'vev', 'vtw', 'vwi'].includes(fileTypeEdit) ? embedUrlKeyEdit : null,
         ['mgm', 'mot', 'mzm', 'mmt'].includes(fileTypeEdit) ? meetingTimeEditComment : null,
      )
      const data = new FormData()
      data.append('comment_id', comment_id);
      data.append('post_index', post_index);
      data.append('thread_index', thread_index);
      data.append('comment_index', comment_index);
      data.append('tab_id', currentTabInfo.id)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/load_edited_comment",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               setResetCommentEditTextId(post_id)
               let new_post_data = [...postData]
               new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'][res.data.comment_index].text = res.data.text
               new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'][res.data.comment_index].file = res.data.file
               new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'][res.data.comment_index].content_type = res.data.content_type
               new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'][res.data.comment_index].link = res.data.link
               setPostData(new_post_data);
               setSubmitPostIsLoading(false)
               handleSnackbar('var(--brand-color)', 'success', t('community.comment.editSuccess'));
               setSelectedFileEdit('');
               setSelectedPreviewUrlEdit('');
               setFileTypeEdit('txt');
               setIsMediaAttachedEdit(false)
            })
         .catch(error => {
            handleSnackbar('var(--danger-color)', 'error', t('community.upload.failedConnection'))
         })
   };


   const deleteComment = (comment_id, post_index, thread_index, comment_index) => {
      if (blockPreviewWrite()) return;
      // load the initial data
      const data = new FormData()
      data.append('comment_id', comment_id)
      data.append('thread_index', thread_index)
      data.append('post_index', post_index)
      data.append('comment_index', comment_index)
      data.append('tab_id', currentTabInfo.id)
      data.append('influencer_str', props.influencer)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/delete_comment",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               if (String(res.data.success) === 'true') {
                  if (res.data.comment_index === 0) {
                     let new_post_data = [...postData]
                     new_post_data[post_index]['threads'][res.data.thread_index]['comments'] = new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'].slice(1)
                     new_post_data[post_index]['threads'][res.data.thread_index]['n_comments'] = new_post_data[post_index]['threads'][res.data.thread_index]['n_comments'] - 1
                     setPostData(new_post_data)
                  } else if (res.data.comment_index === (postData[res.data.post_index]['threads'].length - 1)) {
                     let new_post_data = [...postData]
                     new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'] = new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'].slice(0, res.data.thread_index)
                     new_post_data[post_index]['threads'][res.data.thread_index]['n_comments'] = new_post_data[post_index]['threads'][res.data.thread_index]['n_comments'] - 1
                     setPostData(new_post_data)
                  } else {
                     let new_post_data = [...postData]
                     new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'] = [...new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'].slice(0, res.data.comment_index), ...new_post_data[res.data.post_index]['threads'][res.data.thread_index]['comments'].slice(res.data.comment_index + 1)]
                     new_post_data[post_index]['threads'][res.data.thread_index]['n_comments'] = new_post_data[post_index]['threads'][res.data.thread_index]['n_comments'] - 1
                     setPostData(new_post_data)
                  }
                  setCommunityEvents(res.data.events);
               }
            })
         .catch(error => {
            handleSnackbar('var(--danger-color)', 'error', t('dashboard.paidDms.deleteCommentError'))
         })
   };

   async function notifyUploadCompleteCommunity(id, message_type) {
      const startTime = performance.now();
      const response = await axios({
         method: 'get',
         url: backend_url + 'api/community_content/complete/' + message_type + '/' + id,
         headers: {
            'Content-type': 'application/json',
         },
         withCredentials: true
      });
      return response;
   }

   async function notifyUploadCompleteCommunityEdit(id, message_type, text, content_type) {
      let data = new FormData()
      data.append('text', text)
      data.append('content_type', content_type)
      const response = await axios({
         method: 'post',
         url: backend_url + 'api/community_content/complete_edit/' + message_type + '/' + id,
         data: data,
         headers: {
            'Content-type': 'application/json',
         },
         withCredentials: true
      });
      return response;
   }




   const validateNewPost = () => {
      if (newPostTextLength > 5000) {
         handleSnackbar('var(--danger-color)', 'error', t('community.post.maxCharLimit5000'))
      }
      else if (newPostTextLength < 1) {
         handleSnackbar('var(--danger-color)', 'error', 'Error submitting post. Try entering some text')
      }
      else {
         return true
      }
   }


   const scrollToPost = (post_id) => {
      setTimeout(() => {
         const postElement = document.getElementById(`post${post_id}`);
         if (postElement) {
            postElement.scrollIntoView({ behavior: 'smooth' });
         }
      }, 100); // Delay in milliseconds
   };


   const [filters, setFilters] = useState([]);

   useEffect(() => {
      if (props.is_me) {
         setFilters([
            { id: 'posts', label: t('community.filters.posts') },
            {id: 'upcoming-events', label: t('community.filters.upcomingEvents')},
            { id: 'active-posts', label: t('community.filters.activePosts') },
            // { id: 'community-guidelines', label: t('community.filters.guidelines') },
         ])
      } else {
         setFilters([
            { id: 'posts', label: t('community.filters.posts') },
            {id: 'upcoming-events', label: t('community.filters.upcomingEvents')},
            { id: 'active-posts', label: t('community.filters.activePosts') },
            // { id: 'community-guidelines', label: t('community.filters.guidelines') },
         ])
      }



   }, [props.is_me])

   useEffect(() => {
      // console.log('edited')
      if ((newPostTextLength > 0) || (isMediaAttached)) {
         setShowPostButton(true)
      } else {
         setShowPostButton(false)
      }
   }, [newPostTextLength, isMediaAttached])


   useEffect(() => {
      // console.log(selectedFilter)
   }, [selectedFilter])



   // Mobile Filter
   const handleFilterChange = (event, newFilter) => {
      setSelectedFilter(newFilter);
   };

   // New Post Component
   const handleInputChange = (event) => {
      if (event.target.value.length >= 5000) {
         setSnackbarColor('var(--danger-color)')
         setSnackbarType('error');
         setSnackbarMsg(t('common.upload.characterLimit5000'))
         setSnackbarOpen(true)
      } else {
         setNewPostText(event.target.value);
      }

   };

   const handleShareImage = () => {
      const imageInput = document.createElement('input');
      imageInput.type = 'file';
      imageInput.accept = 'image/*';
      imageInput.onchange = (event) => {
         const file = event.target.files[0];
         if (file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/jpeg') {
            const imageUrl = URL.createObjectURL(file);
            setSelectedFile(file);
            setSelectedPreviewUrl(imageUrl);
            setFileType('img');
            setShowPostButton(true)
            setIsMediaAttached(true)
         } else {
            setSnackbarSeverity('error')
            setSnackbarOpen(true)
            setSnackbarColor('var(--danger-color)')
            setSnackbarMsg(t('common.upload.unsupportedImage'))
         }
      };
      imageInput.click();
   };


   const handleShareVideo = () => {
      const videoInput = document.createElement('input');
      videoInput.type = 'file';
      videoInput.accept = 'video/*';
      videoInput.onchange = (event) => {
         const file = event.target.files[0];
         if (file.type === 'video/mp4' || file.type === 'video/quicktime') {
            const videoUrl = URL.createObjectURL(file);
            setSelectedFile(file);
            setSelectedPreviewUrl(videoUrl);
            setFileType('vid');
            setShowPostButton(true)
            setIsMediaAttached(true)
         } else {
            setSnackbarSeverity('error')
            setSnackbarOpen(true)
            setSnackbarColor('var(--danger-color)')
            setSnackbarMsg(t('common.upload.unsupportedVideo'))
         }
      };
      videoInput.click();
   };

   const handleShareAudio = () => {
      const audioInput = document.createElement('input');
      audioInput.type = 'file';
      audioInput.accept = 'audio/*'; // TODO still Specify the accepted file types (e.g., mp3, wav, etc.)
      audioInput.onchange = (event) => {
         const file = event.target.files[0];
         if (file.type === 'audio/mpeg' || file.type === 'audio/wav' || file.type === 'video/mp4' || file.type === 'audio/aac') {
            const audioUrl = URL.createObjectURL(file);
            setSelectedFile(file);
            setSelectedPreviewUrl(audioUrl);
            setFileType('aud');
            setShowPostButton(true)
            setIsMediaAttached(true)
         } else {
            setSnackbarSeverity('error')
            setSnackbarOpen(true)
            setSnackbarColor('var(--danger-color)')
            setSnackbarMsg(t('common.upload.unsupportedAudio'))
         }
      };
      audioInput.click();

   };

   const handleSharePDF = () => {

      const pdfInput = document.createElement('input');
      pdfInput.type = 'file';
      pdfInput.accept = '.pdf';
      pdfInput.onchange = (event) => {
         const file = event.target.files[0];
         const fileSizePDF = file.size;
         const fileSizeKB = fileSizePDF / 1024; // Convert bytes to KB
         const fileSizeMB = fileSizeKB / 1024;
         // console.log(fileSizeMB, 'MB')
         // console.log(`File Size in MB: ${fileSizeMB} MB`);
         if (fileSizeMB <= 5) {
            if (file.type === 'application/pdf') {
               const pdfUrl = URL.createObjectURL(file);
               setSelectedFile(file);
               setSelectedPreviewUrl(pdfUrl);
               setFileType('pdf');
               setShowPostButton(true)
               setIsMediaAttached(true)
            } else {
               setSnackbarSeverity('error')
               setSnackbarOpen(true)
               setSnackbarColor('var(--danger-color)')
               setSnackbarMsg(t('community.composer.unsupportedPdf'))
            }
         } else {
            setSnackbarSeverity('error')
            setSnackbarOpen(true)
            setSnackbarColor('var(--danger-color)')
            setSnackbarMsg(t('community.composer.pdfSizeLimit'))
         }
         //    if (file.type === 'application/pdf'){
         //       const pdfUrl = URL.createObjectURL(file);
         //       setSelectedFile(file);
         //       setSelectedPreviewUrl(pdfUrl);
         //       setFileType('pdf');
         //       setShowPostButton(true)
         //       setIsMediaAttached(true)
         //    } else {
         //       setSnackbarSeverity('error')
         //       setSnackbarOpen(true)
         //       setSnackbarColor('var(--danger-color)')
         //       setSnackbarMsg(t('common.upload.unsupportedPdf'))
         //    }

      };
      pdfInput.click();
   };

   useEffect(() => {
      if (videoInitialised === false) {
         if (localStreamInitialised !== true) {
            if (communityContentType === 'video') {
               try {
                  localStream.current.srcObject = mediaStream.current;
                  setLocalStreamInitialised(true);
                  setVideoInitialised(true);
                  setLocalisedStreamCounter('');
               }
               catch (error) {
                  if (localisedStreamCounter === '') {
                     setLocalisedStreamCounter(setInterval(() => setLocalStreamInitialised(localStreamInitialised + 1), 1000));
                  }
               }
            }
         }
      }
   }, [communityContentType, localStreamInitialised, videoInitialised])

   async function handleAudioDeviceChange(event) {
      event.preventDefault();
      try {
         if (communityContentType === 'audio') {
            mediaStream.current = await navigator.mediaDevices.getUserMedia(
               {
                  audio: { autoplay: true, deviceId: event.target.value, sampleRate: 44100 },
               }
            )
         } else {
            mediaStream.current = await navigator.mediaDevices.getUserMedia(
               {
                  audio: { autoplay: true, deviceId: event.target.value, sampleRate: 44100 },
                  video: { autoplay: true, deviceId: vDevID }, height: { ideal: 1920 },
               }
            )
         }
         mediaDevices.current = await navigator.mediaDevices.enumerateDevices();
         deviceList();
      } catch (error) {
         // Error handled silently
      }
      setChangingDevice(false);
   }


   async function handleVideoDeviceChange(event) {
      event.preventDefault();
      try {
         mediaStream.current = await navigator.mediaDevices.getUserMedia(
            {
               audio: { autoplay: true, deviceId: aDevID, sampleRate: 44100 },
               video: { autoplay: true, deviceId: event.target.value }, height: { ideal: 1920 },
            }
         )
         mediaDevices.current = await navigator.mediaDevices.enumerateDevices();
         deviceList();
      } catch (error) {
         // Error handled silently
      }
      document.getElementById('video_record').srcObject = mediaStream.current
      setChangingDevice(false);
   }

   function deviceList() {
      let auin = [];
      let audioOut = [];
      let vidin = [];
      mediaDevices.current.forEach(function (gotDevice) {
         let i = 0;
         if (gotDevice.kind === "audioinput") {
            auin.push({ label: gotDevice.label, id: gotDevice.deviceId, len: i++ });
         } else if (gotDevice.kind === "videoinput") {
            vidin.push({ label: gotDevice.label, id: gotDevice.deviceId });
         } else if (gotDevice.kind === "audiooutput") {
            audioOut.push({ label: gotDevice.label, id: gotDevice.deviceId });
         } else {
            // console.log("Some other kind of source/device: ", gotDevice);
         }
      });
      setRecordingDevices({ audioin: auin, videoin: vidin, audioout: audioOut });
   }

   async function getDevices(type, content_type) {
      setRecordingDurationMins('00');
      setRecordingDurationSeconds('00');
      setLocalStreamInitialised(0);
      try {
         mediaStream.current.getTracks().forEach((track) => track.stop())
         mediaStream.current = null;
         mediaDevices.current = null;
         mediaRecorder.current = null;
      } catch {

      }
      setRecordingDevices({
         audioin: null,
         audioout: null,
         videoin: null,
      })
      if (type !== 'post' && (fileType === 'aud-rec' || fileType === 'vid-rec')) {
         handleRemoveContent();
      }
      if (type !== 'thread' && (fileTypeThread === 'aud-rec' || fileTypeThread === 'vid-rec')) {
         setSelectedFileThread('');
         setSelectedPreviewUrlThread('');
         setFileTypeThread('txt');
         setIsMediaAttachedThread(false)
         setShowPostButtonThread(false);
         setSelectedPreviewIdThread('');
      }
      if (type !== 'comment' && (fileTypeComment === 'aud-rec' || fileTypeComment === 'vid-rec')) {
         setSelectedFileComment('');
         setSelectedPreviewUrlComment('');
         setFileTypeComment('txt');
         setIsMediaAttachedComment(false);
         setShowPostButtonComment(false);
         setSelectedPreviewIdComment('');
      }
      if (type !== 'edit' && (fileTypeEdit === 'aud-rec' || fileTypeEdit === 'vid-rec')) {
         setSelectedFileEdit('');
         setSelectedPreviewUrlEdit('');
         setFileTypeEdit('txt');
         setIsMediaAttachedEdit(false);
      }
      // try {
      try {
         if (content_type === 'video') {
            mediaStream.current = await navigator.mediaDevices.getUserMedia(video_constraints);
         } else {
            mediaStream.current = await navigator.mediaDevices.getUserMedia(audio_constraints)
         }
         mediaDevices.current = await navigator.mediaDevices.enumerateDevices();
      } catch (err) {
         setSnackbarOpen(true);
         setSnackbarMsg(t('community.recording.noDevices'))
         setSnackbarSeverity('error')
         setSnackbarColor('var(--danger-color)');
         if (type === 'post') {
            handleRemoveContent();
         } else if (type === 'thread') {
            setSelectedFileThread('');
            setSelectedPreviewUrlThread('');
            setFileTypeThread('txt');
            setIsMediaAttachedThread(false)
            setShowPostButtonThread(false);
            setSelectedPreviewIdThread('');
         } else if (type === 'comment') {
            setSelectedFileComment('');
            setSelectedPreviewUrlComment('');
            setFileTypeComment('txt');
            setIsMediaAttachedComment(false);
            setShowPostButtonComment(false);
            setSelectedPreviewIdComment('');
         } else if (type === 'edit') {
            setSelectedFileEdit('');
            setSelectedPreviewUrlEdit('');
            setFileTypeEdit('txt');
            setIsMediaAttachedEdit(false);
         }
      }
      deviceList();
      setCommunityContentType(content_type);
      if (content_type === 'video') {
         setVideoInitialised(false);
         setVideoBitsPerSecond(4000000);
      } else {
         setVideoBitsPerSecond(10);
      }
      if (mediaDevices.current.length > 0) {
         setRecordingStatus('ready');
      } else {
         setRecordingStatus('no_devices');
      }
   }

   const [isMember, setIsMember] = useState(false)
   const useNewComposer = true;

   async function handleAudioDeviceChange(event) {
      event.preventDefault();
      try {
         if (communityContentType === 'audio') {
            mediaStream.current = await navigator.mediaDevices.getUserMedia(
               {
                  audio: { autoplay: true, deviceId: event.target.value, sampleRate: 44100 },
               }
            )
         } else {
            mediaStream.current = await navigator.mediaDevices.getUserMedia(
               {
                  audio: { autoplay: true, deviceId: event.target.value, sampleRate: 44100 },
                  video: { autoplay: true, deviceId: vDevID }, height: { ideal: 1920 },
               }
            )
         }
         mediaDevices.current = await navigator.mediaDevices.enumerateDevices();
         deviceList();
      } catch (error) {
         // Error handled silently
      }
      setChangingDevice(false);
   }

   async function handleVideoDeviceChange(event) {
      event.preventDefault();
      try {
         mediaStream.current = await navigator.mediaDevices.getUserMedia(
            {
               audio: { autoplay: true, deviceId: aDevID, sampleRate: 44100 },
               video: { autoplay: true, deviceId: event.target.value }, height: { ideal: 1920 },
            }
         )
         mediaDevices.current = await navigator.mediaDevices.enumerateDevices();
         deviceList();
      } catch (error) {
         // Error handled silently
      }
      setChangingDevice(false);
   }

   const saveFile = async (url, filename) => {
      const a = document.createElement('a');
      a.download = filename
      a.href = url;
      a.addEventListener('click', (e) => {
         setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
      });
      a.click();
   };

   const handleSnackbar = (color, type, msg) => {
      setSnackbarColor(color)
      setSnackbarType(type);
      setSnackbarMsg(msg)
      setSnackbarOpen(true)
   }

   const handleSnackbarMeeting = (color, type, msg) => {
      setSnackbarColorMeeting(color)
      setSnackbarSeverityMeeting(type);
      setSnackbarMsgMeeting(msg)
      setSnackbarOpenMeeting(true)
   }

   const handleSnackbarClick = (color, type, pre_msg, post_msg, text_click) => {
      setSnackbarColorClick(color)
      setSnackbarSeverityClick(type);
      setSnackbarPreMsgClick(pre_msg)
      setSnackbarPostMsgClick(post_msg)
      setSnackbarTextClick(text_click)
      setSnackbarOpenClick(true)
   }

   const handlePostClick = (postIndex, editMode = false) => {
      if (props.isUnlockedCommunityPreview) {
         return;
      }
      setSelectedPostIndex(postIndex)
      setSelectedPostData(postData[postIndex])
      setOpenModalInEditMode(editMode)
      setModalOpen(true)
   }

   function createNewArray(array, endIndex) {
      return array.slice(0, endIndex + 1);
   }

   const [availableTiers, setAvailableTiers] = useState([''])

   const [modalOpen, setModalOpen] = useState(false)
   const [selectedPostIndex, setSelectedPostIndex] = useState(null)
   const [selectedPostData, setSelectedPostData] = useState(null)
   const [openModalInEditMode, setOpenModalInEditMode] = useState(false)

  const isClassicTabsLayout =
    props.pageLayoutType !== 'storefront' &&
    props.pageLayoutTypeUpdated !== 'storefront';
  const communityShareConfig =
    !props.is_me
      ? (() => {
          const influencerUsername =
            props.influencer_info?.influencer ||
            props.influencer ||
            props.influencer_name ||
            (typeof window !== 'undefined'
              ? window.location.pathname.split('/').filter(Boolean)[0]
              : '');
          if (!influencerUsername) return null;
          if (!currentTabInfo) return null;
          if (!Object.prototype.hasOwnProperty.call(currentTabInfo, 'affiliate_enabled') || currentTabInfo.affiliate_enabled !== true) return null;
          return {
            productType: 'community',
            productId: currentTabInfo?.id || tabId || props.tab_id || 0,
            productName: currentTabInfo?.name || props.community_name || 'Community',
            influencer: influencerUsername,
          };
        })()
      : null;
  const communitySidebarShareConfig =
    props.windowWidth >= 1280
      ? communityShareConfig
      : null;
  const showMobileCommunityShareButton = Boolean(
    props.windowWidth < 1280 &&
    communityShareConfig
  );

  // console.log(postData)
  const categoriesSection = (
    <>
                   {/* CATEGORIES AND SEARCH */}
                  {initialLoaded && props.current_user !== null && props.current_user !== '' && (props.is_me || accessToCurrentTab) && (
                     <div
                        style={{
                           width: '100%',
                           maxWidth: '100%',
                           minWidth: 0,
                           margin: '12px auto 4px auto',
                           padding: '0',
                           boxSizing: 'border-box',
                           overflow: 'hidden',
                        }}
                     >
                        <style>
                           {`
                              .community-categories-scroll::-webkit-scrollbar {
                                 height: 6px;
                              }
                              .community-categories-scroll::-webkit-scrollbar-track {
                                 background: transparent;
                              }
                              .community-categories-scroll::-webkit-scrollbar-thumb {
                                 background: #D1D5DB;
                                 border-radius: 3px;
                              }
                              .community-categories-scroll::-webkit-scrollbar-thumb:hover {
                                 background: #9CA3AF;
                              }
                              .community-categories-scroll {
                                 scrollbar-width: thin;
                                 scrollbar-color: #D1D5DB transparent;
                              }
                           `}
                        </style>
                        {/* Search bar */}
                        <div
                           style={{
                              display: 'flex', alignItems: 'center', gap: '14px',
                              padding: '12px 16px',
                              backgroundColor: '#fff',
                              border: '1px solid #E4EBF6',
                              borderRadius: '14px',
                              marginBottom: '14px',
                              cursor: 'text',
                              position: 'relative',
                           }}
                           onClick={() => {
                              const input = document.getElementById('community-search-input');
                              if (input) input.focus();
                           }}
                        >
                           <div style={{
                              width: 36, height: 36, borderRadius: '10px',
                              backgroundColor: '#F3F4F6', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                           }}>
                              {searchLoading ? (
                                 <CircularProgress size={16} sx={{ color: '#9CA3AF' }} />
                              ) : (
                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                              )}
                           </div>
                           <div style={{ flex: 1, minWidth: 0 }}>
                              {categorySearch ? (
                                 <input
                                    id="community-search-input"
                                    type="text"
                                    value={categorySearch}
                                    onChange={(e) => setCategorySearch(e.target.value)}
                                    style={{
                                       border: 'none', outline: 'none', width: '100%',
                                       fontSize: '15px', fontWeight: 500, color: '#1D1D1D',
                                       fontFamily: 'Roboto, sans-serif', backgroundColor: 'transparent',
                                       padding: 0,
                                    }}
                                 />
                              ) : (
                                 <>
                                    <input
                                       id="community-search-input"
                                       type="text"
                                       value={categorySearch}
                                       onChange={(e) => setCategorySearch(e.target.value)}
                                       placeholder={t('community.searchPlaceholder') || 'Search this community'}
                                       style={{
                                          border: 'none', outline: 'none', width: '100%',
                                          fontSize: '15px', fontWeight: 500, color: '#1D1D1D',
                                          fontFamily: 'Roboto, sans-serif', backgroundColor: 'transparent',
                                          padding: 0,
                                       }}
                                    />
                                    <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '2px 0 0 0', fontFamily: 'Roboto, sans-serif' }}>
                                       {t('community.searchSubtext') || 'Posts, threads, comments...'}
                                    </p>
                                 </>
                              )}
                           </div>
                           {categorySearch && (
                              <span
                                 onClick={(e) => { e.stopPropagation(); setCategorySearch(''); setSearchResults(null); }}
                                 style={{ display: 'flex', cursor: 'pointer', color: '#9CA3AF', flexShrink: 0 }}
                              >
                                 <IoClose style={{ fontSize: 18 }} />
                              </span>
                           )}
                        </div>
                        {/* Header row */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingLeft: '5px' }}>
                           <span style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', letterSpacing: '1px', textTransform: 'uppercase' }}>
                              {customizeCategories ? (t('community.editCategories') || 'Edit Categories') : (t('community.browseByCategory') || 'Browse by Category')}
                           </span>
                           {props.is_me && (
                              <button
                                 onClick={() => setCustomizeCategories(prev => !prev)}
                                 style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: customizeCategories ? '7px 16px' : '5px 12px',
                                    borderRadius: customizeCategories ? '999px' : '8px',
                                    border: 'none',
                                    backgroundColor: customizeCategories ? '#1D1D1D' : 'transparent',
                                    color: customizeCategories ? '#fff' : '#6B7280',
                                    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                                    fontFamily: 'Roboto, sans-serif', outline: 'none',
                                    transition: 'all 0.15s',
                                 }}
                                 onMouseEnter={e => { if (!customizeCategories) e.currentTarget.style.color = '#1D1D1D'; }}
                                 onMouseLeave={e => { if (!customizeCategories) e.currentTarget.style.color = '#6B7280'; }}
                              >
                                 {customizeCategories ? (
                                    <>
                                       <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                       {t('common.done') || 'Done'}
                                    </>
                                 ) : (
                                    <>
                                       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
                                       {t('community.customize') || 'Customize'}
                                    </>
                                 )}
                              </button>
                           )}
                        </div>
                        {/* Instruction text in edit mode */}
                        {customizeCategories && (
                           <div style={{
                              padding: '10px 14px', marginBottom: '10px',
                              backgroundColor: '#F3F4F6', borderRadius: '10px',
                              fontSize: '13px', color: '#6B7280', fontFamily: 'Roboto, sans-serif',
                           }}>
                              {t('community.editCategoriesHint') || 'Drag the handle to reorder · Tap a pill to rename · Tap ✕ to remove'}
                           </div>
                        )}
                        <div
                           ref={scrollContainerRef}
                           className="community-categories-scroll"
                           style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              overflowX: 'auto',
                              overflowY: 'hidden',
                              WebkitOverflowScrolling: 'touch',
                              paddingBottom: '4px',
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              flexWrap: 'nowrap',
                           }}
                        >
                           <div 
                              style={{ 
                                 display: 'flex', 
                                 gap: 12, 
                                 flexWrap: 'nowrap',
                                 minWidth: 'max-content',
                                 alignItems: 'flex-start',
                              }}
                           >
                              {/* Always show 'all' category */}
                              <div
                                 style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 4,
                                    flexShrink: 0,
                                 }}
                              >
                                 <button
                                    key="all"
                                    onClick={() => {
                                       setSelectedCategory('all');
                                       setSelectedFilter('posts');
                                       setShowPostsOrEvents('posts');
                                       setUploadPostTags(['all']);
                                       changeCategory('all');
                                    }}
                                    style={{
                                       padding: '8px 16px',
                                       height: 'auto',
                                       borderRadius: '999px',
                                       border: selectedCategory === 'all' ? '1px solid #1D1D1D' : '1px solid #E4EBF6',
                                       background: selectedCategory === 'all' ? '#1D1D1D' : 'white',
                                       color: selectedCategory === 'all' ? '#fff' : '#1D1D1D',
                                       fontWeight: 700,
                                       fontSize: 13,
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       boxShadow: selectedCategory === 'all' ? '0px 2px 8px rgba(40, 71, 177, 0.07)' : undefined,
                                       cursor: 'pointer',
                                       transition: 'background 0.15s, color 0.15s, border 0.15s',
                                       whiteSpace: 'nowrap',
                                       flexShrink: 0,
                                    }}
                                 >
                                    {t('community.allTab')}
                                 </button>
                                 {/* Spacer div to match height of icons row below other categories */}
                                 <div style={{ height: '20px', width: '1px' }} />
                              </div>
                              {/* Events button - mobile version only (desktop shows events in sidebar) */}
                              {props.windowWidth < 600 && communityEvents && communityEvents.length > 0 && (
                                 <div
                                    style={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       alignItems: 'center',
                                       gap: 4,
                                       flexShrink: 0,
                                    }}
                                 >
                                    <button
                                       onClick={() => {
                                          setSelectedCategory('events');
                                          setSelectedFilter('upcoming-events');
                                          setShowPostsOrEvents('events');
                                          setUploadPostTags(['all']);
                                          // Scroll to top
                                          window.scrollTo({ top: 0, behavior: 'smooth' });
                                       }}
                                       style={{
                                          padding: '8px 16px',
                                          height: 'auto',
                                          borderRadius: '999px',
                                          border: selectedCategory === 'events' ? `1px solid ${getBrandColor()}` : '1px solid #E4EBF6',
                                          background: selectedCategory === 'events' ? getBrandColor() : 'white',
                                          color: selectedCategory === 'events' ? '#fff' : '#1D1D1D',
                                          fontWeight: 700,
                                          fontSize: 13,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          boxShadow: selectedCategory === 'events' ? '0px 2px 8px rgba(40, 71, 177, 0.07)' : undefined,
                                          cursor: 'pointer',
                                          transition: 'background 0.15s, color 0.15s, border 0.15s',
                                          whiteSpace: 'nowrap',
                                          flexShrink: 0,
                                       }}
                                    >
                                       {t('community.upcomingEventsTab')}
                                    </button>
                                    {/* Spacer div to match height of icons row below other categories */}
                                    <div style={{ height: '20px', width: '1px' }} />
                                 </div>
                              )}
                              {/* Show other categories - only if user has access or is the influencer */}
                              {props.current_user !== null && props.current_user !== '' && (props.is_me || ((accessToCurrentTab && isMember) && Array.isArray(currentTabInfo?.tags) && currentTabInfo.tags.filter(cat => cat && typeof cat === 'string' && cat.trim() !== '').length > 0)) && (
                                 props.is_me ? (
                                    // Creator view: draggable categories
                                    <>
                                    <ReactSortable
                                       list={orderedCategories}
                                       setList={(newList) => {
                                          setOrderedCategories(newList);
                                          // Save with debounce
                                          const categoryNames = newList.map(item => typeof item === 'string' ? item : item.name || item.id);
                                          saveCategoryOrder(categoryNames);
                                       }}
                                       animation={200}
                                       handle=".category-drag-handle"
                                       scroll={false}
                                       scrollSensitivity={0}
                                       style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 6,
                                          flexWrap: 'nowrap',
                                          minWidth: 'max-content',
                                       }}
                                    >
                                       {orderedCategories.map((item, index) => {
                                          const cat = typeof item === 'string' ? item : item.name || item.id;
                                          const isActive = selectedCategory === cat;
                                          return (
                                             <div
                                                key={cat || `cat-${index}`}
                                                style={{
                                                   display: 'flex',
                                                   flexDirection: 'column',
                                                   alignItems: 'center',
                                                   gap: 4,
                                                   flexShrink: 0,
                                                }}
                                             >
                                                <button
                                                   onClick={() => {
                                                      if (!customizeCategories) {
                                                         setSelectedCategory(cat);
                                                         setSelectedFilter('posts');
                                                         setShowPostsOrEvents('posts');
                                                         setUploadPostTags([cat]);
                                                         changeCategory(cat);
                                                      }
                                                   }}
                                                   style={{
                                                      padding: '8px 16px',
                                                      height: 'auto',
                                                      borderRadius: '999px',
                                                      border: isActive ? '1px solid #1D1D1D' : '1px solid #E4EBF6',
                                                      background: isActive ? '#1D1D1D' : 'white',
                                                      color: isActive ? '#fff' : '#1D1D1D',
                                                      fontWeight: isActive ? 700 : 500,
                                                      fontSize: 13,
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      gap: '6px',
                                                      cursor: 'pointer',
                                                      transition: 'all 0.15s',
                                                      textAlign: 'center',
                                                      whiteSpace: 'nowrap',
                                                      flexShrink: 0,
                                                   }}
                                                >
                                                   {customizeCategories && (
                                                      <span className="category-drag-handle" style={{ cursor: 'grab', display: 'inline-flex', color: '#9CA3AF', flexShrink: 0 }}>
                                                         <TbGripVertical style={{ fontSize: '14px' }} />
                                                      </span>
                                                   )}
                                                   {customizeCategories && inlineEditCategory === cat ? (<>
                                                      <input
                                                         autoFocus
                                                         value={inlineEditValue}
                                                         onChange={(e) => setInlineEditValue(e.target.value)}
                                                         onBlur={() => {
                                                            // Delay to allow tick button click to fire first
                                                            setTimeout(() => {
                                                               setInlineEditCategory(prev => {
                                                                  if (prev === cat) { setInlineEditValue(''); return null; }
                                                                  return prev;
                                                               });
                                                            }, 200);
                                                         }}
                                                         onKeyDown={async (e) => {
                                                            if (e.key === 'Enter') {
                                                               const trimmed = inlineEditValue.trim();
                                                               if (trimmed && trimmed.toLowerCase() !== cat.toLowerCase()) {
                                                                  await editCommunityCategory(cat, trimmed);
                                                               }
                                                               setInlineEditCategory(null);
                                                               setInlineEditValue('');
                                                            }
                                                            if (e.key === 'Escape') { setInlineEditCategory(null); setInlineEditValue(''); }
                                                         }}
                                                         onClick={(e) => e.stopPropagation()}
                                                         style={{
                                                            border: 'none', outline: 'none',
                                                            background: 'transparent',
                                                            fontSize: 13, fontWeight: 500,
                                                            fontFamily: 'Roboto, sans-serif',
                                                            color: isActive ? '#fff' : '#1D1D1D',
                                                            width: Math.max(40, inlineEditValue.length * 8 + 10),
                                                            padding: 0,
                                                         }}
                                                      />
                                                      <span
                                                         onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const trimmed = inlineEditValue.trim();
                                                            if (trimmed && trimmed.toLowerCase() !== cat.toLowerCase()) {
                                                               await editCommunityCategory(cat, trimmed);
                                                            }
                                                            setInlineEditCategory(null);
                                                            setInlineEditValue('');
                                                         }}
                                                         style={{ cursor: 'pointer', display: 'inline-flex', color: isActive ? '#fff' : '#22C55E', flexShrink: 0 }}
                                                      >
                                                         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                                      </span>
                                                   </>) : (
                                                      <span
                                                         onClick={(e) => {
                                                            if (customizeCategories) {
                                                               e.stopPropagation();
                                                               setInlineEditCategory(cat);
                                                               setInlineEditValue(cat);
                                                            }
                                                         }}
                                                         style={{ cursor: customizeCategories ? 'text' : 'pointer' }}
                                                      >
                                                         {capitalizeCategory(cat)}
                                                      </span>
                                                   )}
                                                   {customizeCategories && (
                                                      <span
                                                         onClick={(e) => { e.stopPropagation(); deleteCommunityCategory(cat); }}
                                                         style={{ cursor: 'pointer', display: 'inline-flex', color: '#9CA3AF', flexShrink: 0, marginLeft: '2px' }}
                                                         onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
                                                         onMouseLeave={e => e.currentTarget.style.color = '#9CA3AF'}
                                                      >
                                                         <IoClose style={{ fontSize: '14px' }} />
                                                      </span>
                                                   )}
                                                </button>
                                                {/* Old icons below category name - hidden, replaced by inline icons */}
                                                {false && customizeCategories && !props.isUnlockedCommunityPreview && (
                                                   <div
                                                      style={{
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                         gap: 8,
                                                         justifyContent: 'center',
                                                      }}
                                                   >
                                                      {/* Drag handle */}
                                                      <span
                                                         className="category-drag-handle"
                                                         style={{
                                                            cursor: 'grab',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            color: '#6E89AF',
                                                            opacity: 0.5,
                                                            transition: 'opacity 0.2s',
                                                         }}
                                                         onMouseEnter={(e) => {
                                                            e.currentTarget.style.opacity = '1';
                                                         }}
                                                         onMouseLeave={(e) => {
                                                            e.currentTarget.style.opacity = '0.5';
                                                         }}
                                                      >
                                                         <TbGripHorizontal style={{ fontSize: '16px' }} />
                                                      </span>
                                                      {/* Edit icon */}
                                                      <DarkTooltip 
                                                         title={t('community.editCategoryName')}
                                                         enterTouchDelay={0}
                                                         placement="top"
                                                      >
                                                         <span
                                                            onClick={(e) => handleEditCategoryClick(cat, e)}
                                                            style={{
                                                               cursor: 'pointer',
                                                               display: 'inline-flex',
                                                               alignItems: 'center',
                                                               justifyContent: 'center',
                                                               padding: '2px',
                                                               borderRadius: '4px',
                                                               transition: 'background 0.15s',
                                                               color: '#6E89AF',
                                                            }}
                                                            onMouseEnter={(e) => {
                                                               e.currentTarget.style.background = 'rgba(110, 137, 175, 0.1)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                               e.currentTarget.style.background = 'transparent';
                                                            }}
                                                         >
                                                            <TbPencil style={{ fontSize: '16px' }} />
                                                         </span>
                                                      </DarkTooltip>
                                                   </div>
                                                )}
                                             </div>
                                          );
                                       })}
                                    </ReactSortable>
                                    {customizeCategories && !props.isUnlockedCommunityPreview && (
                                       <button
                                          onClick={() => {
                                             setEditingCategory(null);
                                             setEditCategoryName('');
                                             setEditCategoryModalOpen(true);
                                             setTimeout(() => {
                                                setEditCategoryModalAnimating(true);
                                             }, 10);
                                          }}
                                          style={{
                                             padding: '8px 16px',
                                             height: 'auto',
                                             borderRadius: '999px',
                                             border: '1px solid #E4EBF6',
                                             background: 'white',
                                             color: '#1D1D1D',
                                             fontWeight: 500,
                                             fontSize: 13,
                                             display: 'flex',
                                             alignItems: 'center',
                                             justifyContent: 'center',
                                             cursor: 'pointer',
                                             transition: 'all 0.15s',
                                             gap: '6px',
                                             flexShrink: 0,
                                          }}
                                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
                                       >
                                          <TbPlus style={{ fontSize: 15 }} />
                                          {t('community.addCategory') || 'Add'}
                                       </button>
                                    )}
                                 </>
                                 ) : (
                                    // Non-creator view: regular categories (no drag)
                                    getOrderedCategories.map((cat, index) => {
                                       const isActive = selectedCategory === cat;
                                       return (
                                          <div
                                             key={cat || `cat-${index}`}
                                             style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                flexShrink: 0,
                                             }}
                                          >
                                             <button
                                                onClick={() => {
                                                   setSelectedCategory(cat);
                                                   setSelectedFilter('posts');
                                                   setShowPostsOrEvents('posts');
                                                   setUploadPostTags([cat]);
                                                   changeCategory(cat);
                                                }}
                                                style={{
                                                   padding: '8px 16px',
                                                   height: 'auto',
                                                   borderRadius: '999px',
                                                   border: isActive ? `1px solid ${getBrandColor()}` : '1px solid #E4EBF6',
                                                   background: isActive ? getBrandColor() : 'white',
                                                   color: isActive ? '#fff' : '#1D1D1D',
                                                   fontWeight: 700,
                                                   fontSize: 14,
                                                   display: 'flex',
                                                   alignItems: 'center',
                                                   justifyContent: 'center',
                                                   boxShadow: isActive ? '0px 2px 8px rgba(40, 71, 177, 0.07)' : undefined,
                                                   cursor: 'pointer',
                                                   transition: 'background 0.15s, color 0.15s, border 0.15s',
                                                   textAlign: 'center',
                                                   whiteSpace: 'nowrap',
                                                   flexShrink: 0,
                                                }}
                                             >
                                                {capitalizeCategory(cat)}
                                             </button>
                                          </div>
                                       );
                                    })
                                 )
                              )}
                              {/* Info icon about categories - hidden */}
                              {false && props.is_me && (
                                 <div
                                    style={{
                                       display: 'flex',
                                       alignItems: 'flex-start',
                                       flexShrink: 0,
                                       marginLeft: '4px',
                                       paddingTop: '8px',
                                    }}
                                 >
                                    <DarkTooltip 
                                       title={t('community.categoryTooltip')}
                                       enterTouchDelay={0}
                                       placement="top"
                                    >
                                       <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
                                          <BiInfoCircle style={{ fontSize: '16px', color: '#6E89AF' }} />
                                       </span>
                                    </DarkTooltip>
                                 </div>
                              )}
                           </div>
                        </div>
                     </div>
                  )}
                  {searchResults !== null && searchResults.length === 0 && !searchLoading && !(props.is_me && showExamplePosts && postData.length === 0) && (
                     <p style={{ fontSize: '14px', color: '#9CA3AF', textAlign: 'center', margin: '20px 0', fontFamily: 'Roboto, sans-serif' }}>
                        {t('common.noResults') || 'No results found'}
                     </p>
                  )}
    </>
  );

  let community_post_blocks = ''
  let pinned_post_block = ''

  // When searching, show search results instead of normal posts
  const displayPosts = searchResults !== null ? searchResults : postData;

 if (displayPosts.length > 0) {
     // Identify pinned index first so we can exclude it from the main list
     const pinnedIndex = searchResults === null ? displayPosts.findIndex(p => p.pinned === true) : -1;

     community_post_blocks = displayPosts.map((data, index) => (
        index === pinnedIndex ? null :
         <Grid xs={12} className={props.windowWidth < 1280 ? 'content_island_mobile' : 'content_island'}
            key={index}
            id={`post${data.post_id}`}
            style={{
               height: 'auto',
               margin: '20px 0px 0px 0px'
            }}>
            <div style={{ padding: props.windowWidth > 600 ? '0px' : '0px', boxSizing: 'border-box', width: '100%' }}>
               <CommunityPostSummary
                  post_index={index}
                  username={data.username}
                  display_name={data.display_name}
                  user_icon={data.user_icon}
                  time={data.time}
                  subscription={data.subscription}
                  text={data.text}
                  content_type={data.content_type}
                  content_file={data.file}
                  post_tier={data.tier}
                  influencer_tier_names={props.influencer_tiers}
                  n_threads={data.n_threads}
                  n_comments={data.n_comments}
                  post_like_count={data.like_count}
                  threads={data.threads}
                  tags_list={data.tags_list}
                  onClick={handlePostClick}
                  onLike={postLikeChange}
                  post_id={data.post_id}
                  post_user_value_count={data.user_like_value}
                  link={data.link}
                  pinned={data.pinned}
                  current_user={props.current_user}
                  influencer_name={props.influencer}
                  handleUpdatePinnedPost={handleUpdatePinnedPost}
                  handleRemovePinnedPost={handleRemovePinnedPost}
                  isPinnedPost={isPinnedPost}
                  community_post_title={data.community_post_title}
                  editPost={editPost}
                  deletePost={deletePost}
                  getBrandColor={getBrandColor}
                  event_time={data.event_time}
                  scheduled_date={data.scheduled_date}
               />
            </div>
         </Grid>
      ))


   // Build pinned post (single) if available — hide during search
    if (pinnedIndex > -1 && searchResults === null) {
      const data = postData[pinnedIndex];
      pinned_post_block = (
        <Grid xs={12} className={props.windowWidth < 1280 ? 'content_island_mobile' : 'content_island'}
          key={`pinned-${data.post_id}`}
          id={`post${data.post_id}`}
          style={{
            height: 'auto',
            margin: '20px 0px 0px 0px'
          }}>
          <div style={{ padding: props.windowWidth > 600 ? '0px' : '0px', boxSizing: 'border-box', width: '100%' }}>
            <CommunityPostSummary
              post_index={pinnedIndex}
              username={data.username}
              display_name={data.display_name}
              user_icon={data.user_icon}
              time={data.time}
              subscription={data.subscription}
              text={data.text}
              content_type={data.content_type}
              content_file={data.file}
              post_tier={data.tier}
              influencer_tier_names={props.influencer_tiers}
              n_threads={data.n_threads}
              n_comments={data.n_comments}
              post_like_count={data.like_count}
              threads={data.threads}
                tags_list={data.tags_list}
              onClick={handlePostClick}
              onLike={postLikeChange}
              post_id={data.post_id}
              post_user_value_count={data.user_like_value}
              link={data.link}
              pinned={data.pinned}
              current_user={props.current_user}
              influencer_name={props.influencer}
              handleUpdatePinnedPost={handleUpdatePinnedPost}
              handleRemovePinnedPost={handleRemovePinnedPost}
              isPinnedPost={isPinnedPost}
              community_post_title={data.community_post_title}
              editPost={editPost}
              deletePost={deletePost}
              getBrandColor={getBrandColor}
              event_time={data.event_time}
              scheduled_date={data.scheduled_date}
              shareConfig={communityShareConfig}
            />
          </div>
        </Grid>
      )
    }
  } else if (props.is_me && showExamplePosts && postData.length === 0 && !hasRealPosts) {
      // Show example posts for creators with no content
      const { mockPosts } = generateMockPreviewData(
         props.influencer_name || props.influencer,
         props.current_user_icon,
         props.display_name || props.influencer_name,
         t
      );
      // Filter by selected category
      let filteredMockPosts = selectedCategory === 'all'
         ? mockPosts
         : mockPosts.filter(p => p.tags_list && p.tags_list.some(tag => tag.toLowerCase() === selectedCategory.toLowerCase()));

      // Also filter by search query if searching
      if (categorySearch && categorySearch.trim().length >= 2) {
         const query = categorySearch.toLowerCase().trim();
         filteredMockPosts = filteredMockPosts.filter(p => {
            const fields = [p.text, p.username, p.display_name, p.community_post_title, ...(p.tags_list || [])];
            return fields.some(f => f && String(f).toLowerCase().includes(query));
         });
      }

      community_post_blocks = (
         <div>
            <div style={{
               display: 'flex', justifyContent: 'space-between', alignItems: 'center',
               flexWrap: 'wrap', gap: '8px',
               padding: '0px 0 12px 0', marginBottom: '8px',
            }}>
               <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                  <TbSparkles style={{ fontSize: '18px', color: '#B45309', strokeWidth: 1.5, flexShrink: 0 }} />
                  <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: 500, fontFamily: 'Roboto, sans-serif', whiteSpace: 'nowrap' }}>
                     {t('community.examplePosts.inspireTitle')}
                  </span>
               </div>
               <button
                  onClick={() => dismissExamplePosts()}
                  style={{
                     fontSize: '13px', color: '#6B7280', cursor: 'pointer',
                     display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500,
                     padding: '6px 14px', borderRadius: '8px',
                     border: '1px dashed #D1D5DB', backgroundColor: 'transparent',
                     fontFamily: 'Roboto, sans-serif',
                     transition: 'all 0.15s', flexShrink: 0, whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
               >
                  <IoClose style={{ fontSize: 14 }} /> {t('community.examplePosts.clearAll')}
               </button>
            </div>
            {filteredMockPosts.filter(p => !hiddenExamplePosts.includes(p.post_id)).map((data, index) => (
               <div
                  key={data.post_id}
                  style={{
                     height: 'auto',
                     margin: '16px 0px 0px 0px',
                     position: 'relative',
                     border: '2px dashed #D1D5DB',
                     borderRadius: '14px',
                     overflow: 'visible',
                     backgroundColor: '#fff',
                     animation: 'exampleBorderPulse 2.5s ease-in-out infinite',
                  }}>
                  <div style={{
                     position: 'absolute', bottom: '12px', right: '12px', zIndex: 2,
                     display: 'flex', alignItems: 'center', gap: '8px',
                  }}>
                     <span
                        onClick={() => setHiddenExamplePosts(prev => [...prev, data.post_id])}
                        style={{
                           display: 'inline-flex', alignItems: 'center', gap: '6px',
                           fontSize: '11px', fontWeight: 600, color: '#8B7355',
                           backgroundColor: '#F0ECFF', padding: '5px 12px 5px 8px',
                           borderRadius: '20px',
                           border: 'none',
                           cursor: 'pointer',
                           transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#E4DFFF'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F0ECFF'}
                     >
                        <IoClose style={{ fontSize: 13, color: '#8B7355' }} />
                        <span style={{
                           width: '6px', height: '6px', borderRadius: '50%',
                           backgroundColor: '#B45309',
                           display: 'inline-block',
                           animation: 'examplePulse 2s ease-in-out infinite',
                        }} />
                        <style>{`@keyframes examplePulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.8); } } @keyframes exampleBorderPulse { 0%, 100% { border-color: #D1D5DB; box-shadow: 0 0 0px rgba(165, 180, 252, 0); } 50% { border-color: #A5B4FC; box-shadow: 0 0 12px rgba(165, 180, 252, 0.4); } }`}</style>
                        {t('community.examplePosts.example')}
                     </span>
                  </div>
                  <div style={{ boxSizing: 'border-box', width: '100%' }}>
                     <CommunityPostSummary
                        post_index={index}
                        username={data.username}
                        display_name={data.display_name}
                        user_icon={data.user_icon}
                        time={data.time}
                        subscription={data.subscription}
                        text={data.text}
                        content_type={data.content_type || 'txt'}
                        content_file={data.file || ''}
                        post_tier={data.tier || 0}
                        influencer_tier_names={props.influencer_tiers || {}}
                        n_threads={data.n_threads || 0}
                        n_comments={data.n_comments || 0}
                        post_like_count={data.like_count || data.n_likes || 0}
                        threads={data.threads || []}
                        tags_list={data.tags_list || []}
                        onClick={() => {}}
                        onLike={() => {}}
                        post_id={data.post_id}
                        post_user_value_count={0}
                        link={data.link || ''}
                        pinned={data.pinned || false}
                        current_user={props.current_user}
                        influencer_name={props.influencer}
                        handleUpdatePinnedPost={() => {}}
                        handleRemovePinnedPost={() => {}}
                        isPinnedPost={() => false}
                        community_post_title={data.community_post_title || ''}
                        editPost={() => {}}
                        deletePost={() => {}}
                        getBrandColor={getBrandColor}
                        event_time={data.event_time}
                        is_example={true}
                     />
                  </div>
               </div>
            ))}
         </div>
      );
   } else {
      community_post_blocks = (
         <p
            style={{
               fontSize: '14px',
               fontWeight: '500',
               margin: '40px auto 40px auto',
               color: '#6E89AF',
               textAlign: 'center',
               width: '90%',
            }}
         >
            {t('community.composer.nothingHereYet')}
         </p>
      )
   }

   const [showTabDropdown, setShowTabDropdown] = useState(false);


   const [altPlaceholder, setAltPlaceholder] = useState('')
   const [redirectPage, setRedirectPage] = useState('')

   function openCommunityCheckout() {
      const form_data = new FormData()
      form_data.append('influencer', props.influencer)
      form_data.append('currency', props.currency)
      form_data.append('duration', selectedDuration)
      form_data.append('community_tab_id', tabId)
      form_data.append('promo_code', promoCode)

      axios({
         method: 'post',
         url: backend_url + "api/checkout/get_new_payment_intent_community",
         data: form_data,
         headers: {
            'Content-type': 'application/json',
         },
         withCredentials: true
      }).then(res => {
         if (res.data.error) {
            if (res.data.error === 'non_connect') {
               props.setState({
                  openCheckoutError: true,
               })
            }
         } else {
            setOpenCheckoutModal(true);
         }
      })
   }


   useEffect(() => {
      if (props.current_user === '') {
         setAltPlaceholder('Login to join the conversation')
         setRedirectPage('/login')
      } else  {
         setIsMember(true)
      }

      if (props.influencer_bool) {
         if (isCustomerPreview) {
            setAccessToCurrentTab(true);
         // Logged-out visitors see the sales experience.
         } else if (props.isLoggedOutView) {
            setAccessToCurrentTab(false);
         } else {
            // Check if user has access to the current tab
            // For free communities, user must have access (subscription) to view content
            // Don't automatically grant access just because user is logged in
            const currentTabAccess = tabs.find(tab => tab.id === tabId)?.access;
            setAccessToCurrentTab((props.mobilePreview || (props.current_page_view === 'preview' && props.view_type === 'user')) ? false : (currentTabAccess === true));
         }
      }
   }, [props.current_user, props.influencer_info['current_tier'], props.view_type, props.isLoggedOutView, isCustomerPreview, tabs, tabId])

   const handleEditingContentModal = (postId, postIndex, currentText) => {
      setEditingContentModal(true)
      setEditingContentText(currentText)
      setEditingContentTextIndex(postIndex)
      setEditingContentTextId(postId)
   }


   const handleModalClose = () => {
      setModalOpen(false)
      setSelectedPostIndex(null)
      setSelectedPostData(null)
      setOpenModalInEditMode(false)
   }

   const changeCategory = (name, post_id=null) => {
      // If we currently have posts loaded, remember that real posts exist
      if (postData.length > 0) setHasRealPosts(true);
      setSelectedCategory(name);
      // Update uploadPostTags to match the selected category (unless it's 'all' or 'events')
      if (name !== 'all' && name !== 'events') {
         setUploadPostTags([name]);
      } else {
         setUploadPostTags(['all']);
      }
      const data = new FormData()
      data.append('tag', name);
      data.append('post_id', post_id)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/initial_load_posts/" + props.influencer + '/' + tabId,
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         }
      ).then(
         res => {
            setCommunityEvents(res.data.events);
            if (res.data.library) setLibraryItems(res.data.library);
            setPostData(res.data.post_info)
            setNPosts(res.data.n_posts)
            if (res.data.n_members !== undefined) setInfluencerTotalMembers(res.data.n_members);
            if (res.data.n_posts < 6) {
               setScrollHasMore(false);
            } else {
               setScrollHasMore(true);
            }
            setIsloading(false);
         })
      .catch(error => {
         setIsloading(false);
      })
   }
   const changeTab = (id, name, post_id=null) => {
      setIsloading(true);
      setTabId(id);
      setTabName(name);
      setSelectedCategory('all');
      const tabAccess = tabs.find(tab => tab.id === id)?.access;
      if (isCustomerPreview) {
         setAccessToCurrentTab(true);
      // Logged-out visitors see the sales experience.
      } else if (props.isLoggedOutView) {
         setAccessToCurrentTab(false);
      } else {
         setAccessToCurrentTab((props.mobilePreview || (props.current_page_view === 'preview' && props.view_type === 'user')) ? false : tabAccess);
      }
      const foundTab = tabs.find(tab => tab.id === id);
      setCurrentTabInfo(foundTab ? {
         ...foundTab,
         tags: Array.isArray(foundTab.tags) ? foundTab.tags : []
      } : {});
      const data = new FormData()
      data.append('post_id', post_id);
      data.append('tag', 'all');
      if (tabAccess === true || isCustomerPreview){
         axios(
            {
               method: 'POST',
               url: backend_url + "api/influencer/community/initial_load_posts/" + props.influencer + '/' + id,
               data: data,
               headers: {
                  'Content-type': 'application/json',
               },
               withCredentials: true
            }
         ).then(
            res => {
               setCommunityEvents(res.data.events);
               setLibraryItems(res.data.library || []);
               setPostData(res.data.post_info)
               setNPosts(res.data.n_posts)
               if (res.data.n_members !== undefined) setInfluencerTotalMembers(res.data.n_members);
               if (res.data.n_posts < 6) {
                  setScrollHasMore(false);
               } else {
                  setScrollHasMore(true);
               }
               setIsloading(false);
            })
         .catch(error => {
            setIsloading(false)
         })
      } else {
         setIsloading(false);
         setPostData([]);
         setNPosts(0);
         setScrollHasMore(false);
         setAccessToCurrentTab(false);
      }
   }

   function removeHtmlTags(inputString) {
      return inputString.replace(/<[^>]*>/g, '');
   }

   const editPostTitle = (post_id, post_index, new_title) => {
      if (blockPreviewWrite()) return;
      let cleanedString = removeHtmlTags(new_title)
      const data = new FormData()
      data.append('post_id', post_id)
      data.append('post_index', post_index)
      data.append('new_title', cleanedString)
      data.append('influencer_str', props.influencer)
      axios(
         {
            method: 'POST',
            url: backend_url + "api/influencer/community/edit_post_title",
            data: data,
            headers: {
               'Content-type': 'application/json',
            },
            withCredentials: true
         })
         .then(
            res => {
               if (!res.data.error) {
                  let post_data_new = [...postData];
                  post_data_new[res.data.post_index]['community_post_title'] = res.data.new_title;
                  setPostData(post_data_new);
                  handleSnackbar('var(--brand-color)', 'success', t('community.post.titleEditSuccess'))
               }
               setEditingContentModal(false)
            })
         .catch(error => {
            handleSnackbar('var(--danger-color)', 'error', t('community.post.errorUpdatingContent'))
         })
   };

   let padding_bottom = '20px'
   // if ((props.windowWidth < 600) && (props.influencer_info['current_tier'] === -1)) {
   //    padding_bottom = '120px'
   if (props.windowWidth < 600) {
      padding_bottom = '80px'
   } else if (props.windowWidth < 900) {
      padding_bottom = '65px'
   } else {
      padding_bottom = '20px'
   }
   // communityShareConfig, communitySidebarShareConfig, showMobileCommunityShareButton, isClassicTabsLayout defined earlier (before first use)

   // console.log('communityEvents', communityEvents)
   return (
      <>
      <div style={{ width: '100%', boxSizing: 'border-box', padding: props.hide_tab_switcher ? '0px' : props.windowWidth < 600 && !props.is_me ? '65px 0px 0px 0px' : props.windowWidth < 600 ? '0px 0px 0px 0px' : props.windowWidth < 1200 && !props.is_me ? '65px 16px 0px 16px' : props.windowWidth < 1200 ? '0px 16px 0px 16px' : '0px'}}>

         {/* Tab name with back button for product preview */}
         {/* {props.isUnlockedCommunityPreview && (
            <Box sx={{ 
               position: 'relative', 
               height: '40px', 
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '100%',
            }}>
               <ChevronLeftIcon
                  style={{
                     fontSize: '26px',
                     color: props.textColor || '#222222',
                     cursor: 'pointer',
                     position: 'absolute',
                     left: '0px',
                     top: '50%',
                     transform: 'translateY(-50%)',
                     zIndex: 1
                  }}
               />
               <Typography sx={{ 
                  fontWeight: 700, 
                  fontSize: '15px', 
                  color: props.designProperties?.appTextColor || '#111827', 
                  textAlign: 'center', 
                  letterSpacing: '-0.2px', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  maxWidth: 'calc(100% - 40px)',
                  px: '40px',
                  margin: 0
               }}>
                  {currentTabInfo?.name}
               </Typography>
            </Box>
         )} */}

         {
            initialLoading ?
               // TODO add delay to prevent flash
               <div
                  style={{
                     display: 'flex',
                     justifyContent: 'center',
                     alignItems: 'center',
                     height: '20vh',
                     zIndex: 9999,
                  }}
               >
                  <CircularProgress sx={{ color: 'var(--brand-color)' }} />
               </div>
               :
               <div style={{ marginTop: props.is_me ? (props.windowWidth < 600 ? '-20px' : props.windowWidth < 1280 ? '-10px' : '-25px') : (props.windowWidth < 600 ? '-10px' : props.windowWidth >= 1280 ? '-15px' : props.windowWidth >= 1200 ? '-15px' : '0px') }}>
                  {/* Community Header Card */}
                  {/* Top bar: dropdown + search - hidden, merged into info card */}
                  {false && initialLoaded && currentTabInfo && tabs && tabs.length > 1 && (
                     <div style={{
                        width: '100%',
                        backgroundColor: 'transparent',
                        marginBottom: '10px',
                        boxSizing: 'border-box',
                        overflow: 'visible',
                        zIndex: 10,
                     }}>
                     <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: props.windowWidth < 600 ? '10px 0px' : '10px 24px',
                        overflow: 'visible',
                        boxSizing: 'border-box',
                     }}>
                              <div style={{
                                 display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                 gap: '12px',
                                 flexWrap: props.windowWidth < 600 ? 'wrap' : 'nowrap',
                              }}>
                                 <ClickAwayListener onClickAway={() => setShowTabDropdown(false)}>
                                    <div style={{ position: 'relative' }}>
                                       <button
                                          onClick={() => { if (!props.hide_tab_switcher && props.is_me) setShowTabDropdown(prev => !prev); }}
                                          onMouseEnter={(e) => { if (props.is_me) e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                          style={{
                                             display: 'flex', alignItems: 'center', gap: '12px',
                                             padding: '8px 14px 8px 8px',
                                             borderRadius: '14px', border: 'none',
                                             backgroundColor: 'transparent', cursor: 'pointer',
                                             outline: 'none', fontFamily: 'Roboto, sans-serif',
                                             transition: 'background-color 0.15s',
                                          }}
                                       >
                                          {(() => {
                                             const selTab = tabs.find(t2 => t2.name === tabName);
                                             const tThumb = selTab?.product_thumbnail || selTab?.thumbnail || selTab?.thumbnail_url || selTab?.thumbnailUrl;
                                             const tSrc = tThumb && tThumb !== 'null' && tThumb !== 'null/' && !tThumb.endsWith('/fitwith/')
                                                ? (tThumb.startsWith('null') ? getImageUrl(tThumb) : tThumb)
                                                : null;
                                             return tSrc ? (
                                                <img src={tSrc} alt="" style={{ width: 40, height: 40, borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }} />
                                             ) : (
                                                <span style={{
                                                   width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                                                   background: `linear-gradient(135deg, ${getBrandColor()} 0%, ${getBrandColor()}cc 100%)`,
                                                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                   fontSize: '15px', fontWeight: 700, color: '#fff'
                                                }}>
                                                   {selTab?.name ? selTab.name.substring(0, 2).toUpperCase() : 'CM'}
                                                </span>
                                             );
                                          })()}
                                          <span style={{ fontWeight: 600, fontSize: '16px', color: '#1D1D1D' }}>
                                             {tabName}
                                          </span>
                                          {props.is_me && (
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                             <polyline points="7 10 12 5 17 10" /><polyline points="7 14 12 19 17 14" />
                                          </svg>
                                          )}
                                       </button>
                                       {showTabDropdown && !props.hide_tab_switcher && (
                                          <div style={{
                                             position: 'absolute', top: 'calc(100% + 4px)', left: 0,
                                             minWidth: '200px', backgroundColor: '#fff',
                                             borderRadius: '12px', border: '1px solid #E4EBF6',
                                             boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
                                             padding: '4px 0', zIndex: 1000,
                                          }}>
                                             {tabs.filter(tab => props.is_me || tab.free || tab.access).map((tab) => (
                                                <div
                                                   key={tab.id}
                                                   onClick={() => { changeTab(tab.id, tab.name); setShowTabDropdown(false); }}
                                                   style={{
                                                      padding: '10px 16px', cursor: 'pointer',
                                                      display: 'flex', alignItems: 'center', gap: '10px',
                                                      backgroundColor: tab.name === tabName ? '#F8F9FA' : 'transparent',
                                                      fontWeight: tab.name === tabName ? 600 : 400,
                                                      fontSize: '14px', color: '#1D1D1D',
                                                      fontFamily: 'Roboto, sans-serif',
                                                      transition: 'background-color 0.1s',
                                                   }}
                                                   onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                                                   onMouseLeave={(e) => e.currentTarget.style.backgroundColor = tab.name === tabName ? '#F8F9FA' : 'transparent'}
                                                >
                                                   {(() => {
                                                      const ddThumb = tab?.product_thumbnail || tab?.thumbnail || tab?.thumbnail_url || tab?.thumbnailUrl;
                                                      const ddSrc = ddThumb && ddThumb !== 'null' && ddThumb !== 'null/' && !ddThumb.endsWith('/fitwith/')
                                                         ? (ddThumb.startsWith('null') ? getImageUrl(ddThumb) : ddThumb)
                                                         : null;
                                                      return ddSrc ? (
                                                         <img src={ddSrc} alt="" style={{ width: 28, height: 28, borderRadius: '8px', objectFit: 'cover' }} />
                                                      ) : (
                                                         <span style={{
                                                            width: 28, height: 28, borderRadius: '8px',
                                                            background: `linear-gradient(135deg, ${getBrandColor()} 0%, ${getBrandColor()}cc 100%)`,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: '11px', fontWeight: 700, color: '#fff', flexShrink: 0
                                                         }}>
                                                            {tab.name?.substring(0, 2).toUpperCase() || 'CM'}
                                                         </span>
                                                      );
                                                   })()}
                                                   {tab.name}
                                                </div>
                                             ))}
                                          </div>
                                       )}
                                    </div>
                                 </ClickAwayListener>
                                 {false && props.windowWidth >= 900 && (
                                    <div style={{ position: 'relative', flex: 1, maxWidth: '280px' }}>
                                       <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', display: 'flex', pointerEvents: 'none' }}>
                                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                       </span>
                                       <input
                                          type="text"
                                          placeholder={t('common.search')}
                                          value={categorySearch}
                                          onChange={(e) => setCategorySearch(e.target.value)}
                                          style={{
                                             width: '100%', padding: '8px 12px 8px 36px',
                                             borderRadius: '10px', border: '1px solid #E4EBF6',
                                             backgroundColor: '#F8F9FA', fontSize: '13px',
                                             fontFamily: 'Roboto, sans-serif', color: '#1D1D1D',
                                             outline: 'none', boxSizing: 'border-box', height: '38px',
                                          }}
                                       />
                                    </div>
                                 )}
                              </div>
                        </div>
                     </div>
                  )}

                  {/* Community Info Row - also acts as dropdown when multiple tabs */}
                  {initialLoaded && currentTabInfo && (props.is_me || accessToCurrentTab) && (
                     <ClickAwayListener onClickAway={() => setShowTabDropdown(false)}>
                     <div style={{
                        backgroundColor: '#fff',
                        border: '1px solid #E4EBF6',
                        borderRadius: '14px',
                        padding: props.windowWidth < 600 ? '14px 10px' : '16px 24px',
                        maxWidth: 'calc(1200px + (100% - 1200px) / 2)',
                        width: '100%',
                        margin: props.windowWidth < 600 ? '10px auto 10px auto' : '0 auto 10px auto',
                        boxSizing: 'border-box',
                        position: 'relative',
                     }}
                     >
                        <div style={{
                           display: 'flex', alignItems: 'center', gap: '18px',
                        }}>
                              {(() => {
                                 const thumb = currentTabInfo?.product_thumbnail || currentTabInfo?.thumbnail || currentTabInfo?.thumbnail_url || currentTabInfo?.thumbnailUrl;
                                 const thumbSrc = thumb && thumb !== 'null' && thumb !== 'null/' && !thumb.endsWith('/fitwith/')
                                    ? (thumb.startsWith('null') ? getImageUrl(thumb) : thumb)
                                    : null;
                                 return thumbSrc ? (
                                    <img src={thumbSrc} alt="" style={{ width: 56, height: 56, borderRadius: '14px', objectFit: 'cover', flexShrink: 0 }} />
                                 ) : (
                                    <div style={{
                                       width: 56, height: 56, borderRadius: '14px', flexShrink: 0,
                                       background: `linear-gradient(135deg, ${getBrandColor() === 'var(--brand-color)' ? '#667eea' : getBrandColor()} 0%, ${getBrandColor() === 'var(--brand-color)' ? '#764ba2' : getBrandColor()}cc 100%)`,
                                       display: 'flex', alignItems: 'center', justifyContent: 'center',
                                       fontSize: '20px', fontWeight: 700, color: '#fff',
                                    }}>
                                       {currentTabInfo?.name ? currentTabInfo.name.substring(0, 2).toUpperCase() : 'CM'}
                                    </div>
                                 );
                              })()}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                 {(() => {
                                    const accessibleTabs = props.is_me ? tabs : tabs?.filter(t => t.access);
                                    const hasMultiple = props.is_me && accessibleTabs && accessibleTabs.length > 1;
                                    return (
                                 <div
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: hasMultiple ? 'pointer' : 'default', borderRadius: '8px', padding: '2px 4px', margin: '-2px -4px', transition: 'background-color 0.15s' }}
                                    onClick={(e) => { if (hasMultiple && !props.hide_tab_switcher) { e.stopPropagation(); setShowTabDropdown(prev => !prev); } }}
                                    onMouseEnter={e => { if (hasMultiple) e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                 >
                                    <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1D1D1D', margin: 0, fontFamily: 'Roboto, sans-serif', lineHeight: 1.3 }}>
                                       {currentTabInfo?.name || 'Community'}
                                    </h2>
                                    {hasMultiple && !props.hide_tab_switcher && (
                                       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                          <polyline points="7 10 12 5 17 10" /><polyline points="7 14 12 19 17 14" />
                                       </svg>
                                    )}
                                 </div>
                                    );
                                 })()}
                                 <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px', flexWrap: 'wrap' }}>
                                    {props.display_name && (
                                       <span style={{ fontSize: '13px', color: '#9CA3AF', fontFamily: 'Roboto, sans-serif' }}>
                                          {props.display_name}
                                       </span>
                                    )}
                                    {props.display_name && <span style={{ fontSize: '13px', color: '#D1D5DB' }}>·</span>}
                                    <span style={{ fontSize: '13px', color: '#9CA3AF', fontFamily: 'Roboto, sans-serif', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                       <FaUsers style={{ fontSize: '12px' }} />
                                       <strong style={{ color: '#1D1D1D', fontWeight: 600 }}>{influencerTotalMembers?.toLocaleString() || 0}</strong> {t('desktopSalesCard.members')}
                                    </span>
                                 </div>
                              </div>
                        </div>

                        {/* Dropdown list */}
                        {showTabDropdown && tabs && tabs.length > 1 && !props.hide_tab_switcher && (
                           <div style={{
                              position: 'absolute', top: 'calc(100% + 6px)', left: 0,
                              minWidth: '280px', maxWidth: '400px', width: 'auto',
                              backgroundColor: '#fff', borderRadius: '14px',
                              border: '1px solid #E4EBF6',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                              padding: '6px 0', zIndex: 1000,
                              maxHeight: 300, overflowY: 'auto',
                           }}>
                              {tabs.filter(tab => props.is_me || tab.free || tab.access).map((tab) => {
                                 const isSelected = String(tab.id) === String(tabId);
                                 const ddThumb = tab?.product_thumbnail || tab?.thumbnail || tab?.thumbnail_url || tab?.thumbnailUrl;
                                 const ddSrc = ddThumb && ddThumb !== 'null' && ddThumb !== 'null/' && !ddThumb.endsWith('/fitwith/')
                                    ? (ddThumb.startsWith('null') ? getImageUrl(ddThumb) : ddThumb)
                                    : null;
                                 return (
                                    <div
                                       key={tab.id}
                                       onClick={(e) => { e.stopPropagation(); changeTab(tab.id, tab.name); setShowTabDropdown(false); }}
                                       style={{
                                          padding: '10px 18px', cursor: 'pointer',
                                          display: 'flex', alignItems: 'center', gap: '14px',
                                          backgroundColor: isSelected ? '#F8F9FA' : 'transparent',
                                          fontWeight: isSelected ? 600 : 400,
                                          fontSize: '15px', color: '#1D1D1D',
                                          transition: 'background-color 0.1s',
                                       }}
                                       onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                                       onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#F8F9FA' : 'transparent'}
                                    >
                                       {ddSrc ? (
                                          <img src={ddSrc} alt="" style={{ width: 36, height: 36, borderRadius: '10px', objectFit: 'cover' }} />
                                       ) : (
                                          <span style={{
                                             width: 36, height: 36, borderRadius: '10px',
                                             background: `linear-gradient(135deg, ${getBrandColor()} 0%, ${getBrandColor()}cc 100%)`,
                                             display: 'flex', alignItems: 'center', justifyContent: 'center',
                                             fontSize: '13px', fontWeight: 700, color: '#fff', flexShrink: 0
                                          }}>
                                             {tab.name?.substring(0, 2).toUpperCase() || 'CM'}
                                          </span>
                                       )}
                                       {tab.name}
                                    </div>
                                 );
                              })}
                           </div>
                        )}

                        {/* Community / Library tabs - all widths */}
                        {(() => {
                           const saveTabLabel = (tabKey) => {
                              const fieldKey = tabKey === 'posts' ? 'community_tab_label' : 'library_tab_label';
                              const form_data = new FormData();
                              form_data.append('tab_id', tabId);
                              form_data.append(fieldKey, editTabLabelValue);
                              axios({ method: 'post', url: getBackendUrl() + 'api/influencer/community/set_tab_labels', data: form_data, withCredentials: true })
                                 .then(() => {
                                    setCurrentTabInfo(prev => ({ ...prev, [fieldKey]: editTabLabelValue || null }));
                                 })
                                 .catch(err => console.error('Failed to save tab label:', err));
                              setEditingTabLabel(null);
                           };
                           const visibleTabs = [
                              { key: 'posts', label: currentTabInfo?.community_tab_label || t('community.communityTab'), icon: <TbMessageDots size={14} /> },
                              { key: 'library', label: currentTabInfo?.library_tab_label || t('community.libraryTab'), icon: <TbSchool size={14} /> },
                           ].filter(tab => tab.key !== 'library' || props.is_me || (libraryItems && libraryItems.length > 0));
                           if (visibleTabs.length <= 1) return null;
                           return (
                           <div style={{
                              display: 'inline-flex', gap: '0px',
                              flexWrap: 'wrap',
                              marginTop: '14px',
                              backgroundColor: '#F3F4F6',
                              borderRadius: '10px',
                              padding: '3px',
                              maxWidth: '100%',
                           }}>
                              {visibleTabs.map(tab => {
                                 const isActive = selectedFilter === tab.key || (tab.key === 'posts' && selectedFilter !== 'library');
                                 const isEditing = editingTabLabel === tab.key;
                                 return (
                                    <button
                                       key={tab.key}
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          if (isEditing) return;
                                          setSelectedFilter(tab.key);
                                          setLibraryProductView(null);
                                          setLibraryCourseView(null);
                                          if (tab.key === 'posts') {
                                             setSelectedCategory('all');
                                             setShowPostsOrEvents('posts');
                                          }
                                       }}
                                       style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '5px',
                                          padding: '6px 16px',
                                          fontSize: '13px',
                                          fontWeight: isActive ? 600 : 500,
                                          color: isActive ? '#1D1D1D' : '#6B7280',
                                          backgroundColor: isActive ? '#FFFFFF' : 'transparent',
                                          border: 'none',
                                          borderRadius: '8px',
                                          cursor: 'pointer',
                                          fontFamily: 'Roboto, sans-serif',
                                          transition: 'all 0.15s',
                                          boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          maxWidth: 'calc(100vw - 40px)',
                                       }}
                                    >
                                       <span style={{ display: 'flex', opacity: isActive ? 1 : 0.6 }}>{tab.icon}</span>
                                       {isEditing ? (
                                          <input
                                             autoFocus
                                             value={editTabLabelValue}
                                             onChange={(e) => setEditTabLabelValue(e.target.value)}
                                             onBlur={() => saveTabLabel(tab.key)}
                                             onKeyDown={(e) => { if (e.key === 'Enter') saveTabLabel(tab.key); if (e.key === 'Escape') setEditingTabLabel(null); }}
                                             onClick={(e) => e.stopPropagation()}
                                             maxLength={50}
                                             style={{
                                                border: 'none', outline: 'none', background: 'transparent',
                                                fontSize: '13px', fontWeight: 600, fontFamily: 'Roboto, sans-serif',
                                                color: '#1D1D1D', width: Math.max(60, Math.min(editTabLabelValue.length * 8, 300)) + 'px', padding: 0,
                                             }}
                                          />
                                       ) : (
                                          <>
                                             {tab.label && tab.label.length > 60 ? tab.label.substring(0, 60) + '...' : tab.label}
                                             {props.is_me && (
                                                <TbPencil
                                                   size={12}
                                                   style={{ marginLeft: '4px', opacity: 0.4, cursor: 'pointer' }}
                                                   onClick={(e) => {
                                                      e.stopPropagation();
                                                      setEditTabLabelValue(tab.key === 'posts' ? (currentTabInfo?.community_tab_label || '') : (currentTabInfo?.library_tab_label || ''));
                                                      setEditingTabLabel(tab.key);
                                                   }}
                                                />
                                             )}
                                          </>
                                       )}
                                    </button>
                                 );
                              })}
                           </div>
                           );
                        })()}

                        {/* Info/Events tabs - mobile/tablet only */}
                        {props.windowWidth < 1280 && selectedFilter !== 'library' && (
                           <div style={{
                              display: 'flex', gap: '4px',
                              marginTop: '14px',
                              padding: '4px',
                              backgroundColor: '#F3F4F6',
                              borderRadius: '12px',
                           }}>
                              {[
                                 { key: 'posts', label: t('community.postsTab') },
                                 { key: 'about', label: t('community.aboutTab') },
                                 { key: 'upcoming-events', label: t('community.upcomingEventsTab') },
                              ].map(tab => (
                                 <button
                                    key={tab.key}
                                    onClick={(e) => {
                                       e.stopPropagation();
                                       setSelectedFilter(tab.key);
                                       if (tab.key === 'posts') {
                                          setSelectedCategory('all');
                                          setShowPostsOrEvents('posts');
                                       } else if (tab.key === 'upcoming-events') {
                                          setSelectedCategory('events');
                                          setShowPostsOrEvents('events');
                                       }
                                    }}
                                    style={{
                                       flex: 1,
                                       padding: '8px 12px',
                                       fontSize: '13px',
                                       fontWeight: selectedFilter === tab.key ? 600 : 500,
                                       color: selectedFilter === tab.key ? '#1D1D1D' : '#6B7280',
                                       backgroundColor: selectedFilter === tab.key ? '#FFFFFF' : 'transparent',
                                       border: 'none',
                                       borderRadius: '10px',
                                       cursor: 'pointer',
                                       fontFamily: 'Roboto, sans-serif',
                                       transition: 'all 0.15s',
                                       boxShadow: selectedFilter === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                       whiteSpace: 'nowrap',
                                    }}
                                 >
                                    {tab.label}
                                 </button>
                              ))}
                           </div>
                        )}
                     </div>
                     </ClickAwayListener>
                  )}

                  {/* Community dropdown - hidden when header card is shown */}
                  {initialLoaded && tabs && tabs.length > 1 && !currentTabInfo && !props.isLoggedOutView && (
                     props.pageLayoutType === 'storefront'
                        ? (props.is_me || tabs.filter(tab => tab.free || tab.access).length > 1)
                        : true
                  ) && (

                     <div
                        style={{
                           display: props.windowWidth < 600 ? 'block' : 'flex',
                           justifyContent: 'center',
                           width: '100%',
                           maxWidth: '1200px',
                           margin: props.is_me ? '10px auto 10px auto' : (props.pageLayoutType === "normal" ? '20px auto 0px auto' : (props.windowWidth < 600 ? '10px auto 10px auto' : '10px auto 40px auto')),
                           padding: props.is_me ? '0px' : (props.windowWidth < 600 ? '0px 0px' : props.windowWidth < 1200 ? '0px 16px' : '0px 0px'),
                           boxSizing: 'border-box',
                           position: 'relative',
                           zIndex: props.windowWidth < 600 ? 100 : 1100,
                        }}
                     >
                        <motion.div
                           initial={{ opacity: 0, y: -10 }}
                           animate={{ opacity: 1, y: 0 }}
                           transition={{ duration: 0.5, ease: "easeOut" }}
                           style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'flex-start',
                              justifyItems: props.windowWidth < 600 ? 'center' : 'flex-start',
                              position: 'relative',
                              zIndex: props.windowWidth < 600 ? 100 : 1100, // Higher than category dropdown (100) on tablet
                           }}
                        >
                           <div
                              style={{
                                 width: '100%',
                                 maxWidth:
                                    props.mobilePreview === true
                                       ? 320
                                       : props.windowWidth < 600
                                       ? 'calc(100vw - 32px)'
                                       : 340,
                                 position: 'relative',
                                 margin: props.windowWidth < 600 ? '0px auto' : '0px',
                                 justifyContent: 'center',
                              }}
                           >
                              <ClickAwayListener
                                 onClickAway={() => setShowTabDropdown(false)}
                              >
                                 <div style={{ position: 'relative', width: '100%' }}>
                                    <motion.button
                                       whileTap={{ scale: 0.98 }}
                                       // Less hover effect: just a subtle shadow, no scale
                                       onClick={() => { if (!props.hide_tab_switcher) setShowTabDropdown((prev) => !prev); }}
                                       style={{
                                          width: '100%',
                                          background: 'white',
                                          border: showTabDropdown
                                             ? `1px solid ${getBrandColor()}`
                                             : '1px solid #E4EBF6',
                                          borderRadius: '10px',
                                          // maxWidth: 'calc(100vw - 20px)',
                                          margin: '0px auto',
                                          padding: '10px 16px',
                                          fontSize: '16px',
                                          fontWeight: 600,
                                          maxWidth: props.mobilePreview === true ? '280px' : (props.windowWidth < 600 ? '100%' : '340px'),
                                          color: '#1D1D1D',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          cursor: 'pointer',
                                          boxShadow: '0 1px 6px rgba(110,137,175,0.04)',
                                          transition: 'border 0.2s, box-shadow 0.2s',
                                          outline: 'none',
                                          position: 'relative',
                                          minHeight: 44,
                                          height: 44,
                                       }}
                                       whileHover={{
                                          boxShadow: "0 2px 8px rgba(110,137,175,0.1)",
                                          // border: showTabDropdown
                                          //    ? `1.5px solid ${getBrandColor()}`
                                          //    : '1.5px solid #B0BFD9', // darker border on hover
                                       }}
                             

                                       aria-haspopup="listbox"
                                       aria-expanded={!!showTabDropdown}
                                    >
                                       <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                          {(() => {
                                             const selectedTab = tabs.find(
                                                (tab) => tab.name === tabName
                                             );
                                             const tabThumb = selectedTab?.product_thumbnail || selectedTab?.thumbnail || selectedTab?.thumbnail_url || selectedTab?.thumbnailUrl;
                                             const tabThumbSrc = tabThumb && tabThumb !== 'null' && tabThumb !== 'null/' && !tabThumb.endsWith('/fitwith/')
                                                ? (tabThumb.startsWith('null') ? getImageUrl(tabThumb) : tabThumb)
                                                : null;
                                             return (
                                                <>
                                                   {tabThumbSrc ? (
                                                      <img src={tabThumbSrc} alt="" style={{ width: 32, height: 32, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                                                   ) : (
                                                      <span style={{
                                                         width: 32, height: 32, borderRadius: '8px',
                                                         background: `linear-gradient(135deg, ${getBrandColor()} 0%, ${getBrandColor()}cc 100%)`,
                                                         display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                         fontSize: '13px', fontWeight: 700, color: '#fff', flexShrink: 0
                                                      }}>
                                                         {selectedTab?.name ? selectedTab.name.substring(0, 2).toUpperCase() : 'CM'}
                                                      </span>
                                                   )}
                                                   <span
                                                      style={{
                                                         fontWeight: 600,
                                                         fontSize: '16px',
                                                         color: '#1D1D1D',
                                                         lineHeight: 1.2,
                                                         maxWidth: 200,
                                                         overflow: 'hidden',
                                                         textOverflow: 'ellipsis',
                                                         whiteSpace: 'nowrap',
                                                         display: 'inline-block',
                                                         verticalAlign: 'middle',
                                                      }}
                                                      title={selectedTab ? selectedTab.name : 'Select Tab'}
                                                   >
                                                      {selectedTab ? selectedTab.name : 'Select Tab'}
                                                   </span>
                                                   {!selectedTab?.free && !selectedTab?.access && (
                                                      <FaLock
                                                         style={{
                                                            fontSize: '14px',
                                                            color: '#6E89AF',
                                                            marginLeft: 4,
                                                         }}
                                                      />
                                                   )}
                                                   {!selectedTab?.free && selectedTab?.access && (
                                                      <FaCrown
                                                         style={{
                                                            fontSize: '16px',
                                                            color: '#6E89AF',
                                                            marginLeft: 4,
                                                         }}
                                                      />
                                                   )}
                                                </>
                                             );
                                          })()}
                                       </span>
                                       {!props.hide_tab_switcher && (
                                       <motion.span
                                          animate={{ rotate: showTabDropdown ? 180 : 0 }}
                                          transition={{ duration: 0.25 }}
                                          style={{ display: 'flex', alignItems: 'center' }}
                                       >
                                          <TbChevronUp
                                             style={{
                                                fontSize: '22px',
                                                color: '#6E89AF',
                                                transition: 'transform 0.2s',
                                             }}
                                          />
                                       </motion.span>
                                       )}
                                    </motion.button>
                                    <AnimatePresence>
                                       {showTabDropdown && !props.hide_tab_switcher && (
                                          <motion.ul
                                             initial={{ opacity: 0, y: -10 }}
                                             animate={{ opacity: 1, y: 0 }}
                                             exit={{ opacity: 0, y: -10 }}
                                             transition={{ duration: 0.25 }}
                                             style={{
                                                position: 'absolute',
                                                top: 'calc(100% + 6px)',
                                                left: 0,
                                                width: '100%',
                                                background: 'white',
                                                borderRadius: '10px',
                                                boxShadow: '0 6px 24px rgba(110,137,175,0.10)',
                                                padding: '4px 0',
                                                zIndex: props.windowWidth < 600 ? 2000 : 1000, // Higher on mobile to be above events icon menus (999) and composer, but still below modals (3000+) and tooltips (1500+)
                                                listStyle: 'none',
                                                margin: 0,
                                                border: '1px solid #E4EBF6',
                                             }}
                                             role="listbox"
                                          >
                                             {tabs.map((tab) => {
                                                const isSelected = tab.name === tabName;
                                                return (
                                                   <motion.li
                                                      key={tab.id}
                                                      onClick={() => {
                                                         setShowTabDropdown(false);
                                                         changeTab(tab.id, tab.name);
                                                      }}
                                                      whileHover={{
                                                         backgroundColor: isSelected
                                                            ? 'rgba(110,137,175,0.06)'
                                                            : 'rgba(110,137,175,0.04)',
                                                      }}
                                                      style={{
                                                         padding: '10px 18px',
                                                         cursor: 'pointer',
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                         fontWeight: isSelected ? 700 : 500,
                                                         color: isSelected ? getBrandColor() : '#1D1D1D',
                                                         fontSize: '15px',
                                                         background: isSelected
                                                            ? 'rgba(110,137,175,0.06)'
                                                            : 'transparent',
                                                         boxShadow: isSelected
                                                            ? `0 1px 6px 0 rgba(110,137,175,0.04)`
                                                            : 'none',
                                                         // borderRadius: '7px',
                                                         transition:
                                                            'background 0.15s, color 0.15s, box-shadow 0.15s',
                                                         gap: 8,
                                                         minHeight: 36,
                                                         height: 36,
                                                      }}
                                                      role="option"
                                                      aria-selected={isSelected}
                                                   >
                                                      <span
                                                         style={{
                                                            flex: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 6,
                                                            minWidth: 0,
                                                         }}
                                                      >
                                                         <span
                                                            style={{
                                                               maxWidth: 230,
                                                               overflow: 'hidden',
                                                               textOverflow: 'ellipsis',
                                                               whiteSpace: 'nowrap',
                                                               display: 'inline-block',
                                                               verticalAlign: 'middle',
                                                            }}
                                                            title={tab.name}
                                                         >
                                                            {tab.name}
                                                         </span>
                                                         {!tab?.free && !tab?.access && (
                                                            <FaLock
                                                               style={{
                                                                  fontSize: '14px',
                                                                  color: '#6E89AF',
                                                               }}
                                                            />
                                                         )}
                                                         {!tab?.free && tab?.access && (
                                                            <FaCrown
                                                               style={{
                                                                  fontSize: '14px',
                                                                  color: '#6E89AF',
                                                               }}
                                                            />
                                                         )}
                                                      </span>
                                                      {/* {isSelected && (
                                                         <motion.span
                                                            initial={{ scale: 0.7, opacity: 0 }}
                                                            animate={{ scale: 1, opacity: 1 }}
                                                            transition={{ duration: 0.2 }}
                                                            style={{ marginLeft: 6 }}
                                                         >
                                                            <TbTag
                                                               style={{
                                                                  fontSize: '16px',
                                                                  color: getBrandColor(),
                                                               }}
                                                            />
                                                         </motion.span>
                                                      )} */}
                                                   </motion.li>
                                                );
                                             })}
                                          </motion.ul>
                                       )}
                                    </AnimatePresence>
                                 </div>
                              </ClickAwayListener>
                           </div>
                        </motion.div>
                     </div>
                  )}


                  {/* Main content with composer + posts */}
                  {isLoading || !currentTabInfo || Object.keys(currentTabInfo).length === 0 ?

                     <div
                        style={{
                           display: 'flex',
                           justifyContent: 'center',
                           alignItems: 'center',
                           height: '20vh',
                           zIndex: 9999,
                        }}
                     >
                        <CircularProgress sx={{ color: 'var(--brand-color)' }} />
                     </div>
                     :
                     !accessToCurrentTab ?

                        <div style={props.windowWidth < 1200 && !props.is_me ? { backgroundColor: '#fff', marginTop: '-100px', paddingTop: '100px', position: 'relative', zIndex: 5 } : { display: 'contents' }}>
                        <CommunityPreview
                           key={tabId}
                           windowWidth={props.windowWidth}
                           influencer={props.influencer}
                           display_name={props.display_name}
                           influencer_name={props.influencer_name}
                           influencer_icon={props.influencer_icon}
                              currency={props.currency}
                           designProperties={props.designProperties}
                           current_user={props.current_user}
                           connected_stripe_account_id={props.connected_stripe_account_id}
                           stripe_direct_charge_account_id={props.stripe_direct_charge_account_id || props.influencer_info?.stripe_direct_charge_account_id}
                           influencer_info={props.influencer_info}
                           setState={props.setState}
                           tabName={tabName}
                           tabId={tabId}
                           currentTabInfo={currentTabInfo}
                           accessToCurrentTab={accessToCurrentTab}
                           openCheckoutModal={openCheckoutModal}
                           setOpenCheckoutModal={setOpenCheckoutModal}
                           selectedDuration={selectedDuration}
                              setSelectedDuration={setSelectedDuration}
                           promoCode={promoCode}
                              setPromoCode={setPromoCode}
                           promoError={promoError}
                              setPromoError={setPromoError}
                           checkoutPercentOff={checkoutPercentOff}
                              setCheckoutPercentOff={setCheckoutPercentOff}
                              updateCommunitySubscriptionIntent={updateCommunitySubscriptionIntent}
                           editedPromoCode={editedPromoCode}
                              setEditedPromoCode={setEditedPromoCode}
                           openCommunityCheckout={openCommunityCheckout}
                           getBrandColor={getBrandColor}
                           openFreeCommunity={openFreeCommunity}
                           real_checkout={!props.previewRolloutOwner}
                              />
                        </div>
                        :
                  <>

                  {/* <span>{currentTabInfo?.tags?.join(', ')}</span> */}
                     {/* Mobile Version */}
                     {
                        props.windowWidth >= 1280 ? '' :
                           <div style={{
                              overflowX: 'hidden',
                              width: '100%',
                              margin: '10px 0px 16px 0px',
                              filter: 'none',
                              minHeight: 'calc(100vh - 400px)',
                              minWidth: 0,
                              pointerEvents: "auto",
                              userSelect: 'text',
                           }}>

                     
                              {
                                 selectedFilter === 'posts' ?
                                    <Grid item xs={12} lg={props.windowWidth < 500 ? 12 : 10} style={{ height: 'auto', margin: '0px 0px 0px 0px' }}>
                                       <Grid container>
                                          {showMobileCommunityShareButton ? (
                                             <Grid item xs={12}>
                                                <div
                                                   style={{
                                                      width: '100%',
                                                      display: 'flex',
                                                      justifyContent: 'center',
                                                      alignItems: 'center',
                                                      padding: props.windowWidth < 600 ? '0px 0px 12px 0px' : '0px 16px 12px 16px',
                                                      boxSizing: 'border-box',
                                                   }}
                                                >
                                                   <ShareEarnButton
                                                      productType={communityShareConfig.productType}
                                                      productId={communityShareConfig.productId}
                                                      productName={communityShareConfig.productName}
                                                      influencer={communityShareConfig.influencer}
                                                      hasAccess={true}
                                                      affiliateEnabled={currentTabInfo?.affiliate_enabled === true}
                                                      buttonVariant="text"
                                                   />
                                                </div>
                                             </Grid>
                                          ) : null}
                                          {/* <Grid item xs={12} className={!isMember && props.is_me === false ? (props.windowWidth < 1280 ? 'content_island_locked_mobile' : 'content_island_locked') : (props.windowWidth < 1280 ? 'content_island_mobile' : 'content_island')} */}
                                          <Grid item xs={12}
                                             style={{
                                                // backgroundColor: 'var(--rebranding-content-background-color)',
                                                height: 'auto',
                                                borderRadius: '12px',
                                                margin: '0px 0px 0px 0px'
                                             }}>
                                             <div style={{ boxSizing: 'border-box' }}>
                                                <CommunityComposer
                                                   windowWidth={props.windowWidth}
                                                   currentUser={props.current_user}
                                                   currentUserIcon={props.current_user_icon}
                                                   placeholder={placeholder}
                                                   textValue={newPostText}
                                                   onTextChange={(v) => setNewPostText(v)}
                                                   onTextLengthChange={setNewPostTextLength}
                                                   onAttachImage={handleShareImage}
                                                   selectedPreviewUrl={selectedPreviewUrl}
                                                   fileType={fileType}
                                                   onRemoveAttachment={handleRemoveContent}
                                                   onSubmit={() => submitPost(tabId)}
                                                   scheduleDate={postScheduleDate}
                                                   onScheduleDateChange={setPostScheduleDate}
                                                   disabled={previewReadOnly}
                                                   brandColor={getBrandColor()}
                                                   handleClickVideo={handleClickVideo}
                                                   handleCloseVideo={handleCloseVideo}
                                                   handleShareVideo={handleShareVideo}
                                                   handleClickAudio={handleClickAudio}
                                                   handleCloseAudio={handleCloseAudio}
                                                   handleShareAudio={handleShareAudio}
                                                   handleClickDoc={handleClickDoc}
                                                   handleCloseDoc={handleCloseDoc}
                                                   handleSharePDF={handleSharePDF}
                                                   handleClick={handleClick}
                                                   handleClose={handleClose}
                                                   getDevices={getDevices}
                                                   anchorElVideo={anchorElVideo}
                                                   anchorElAudio={anchorElAudio}
                                                   anchorElDoc={anchorElDoc}
                                                   anchorEl={anchorEl}
                                                   setFileType={setFileType}
                                                   setIsMediaAttached={setIsMediaAttached}
                                                   setSelectedPreviewUrl={setSelectedPreviewUrl}
                                                   setEmbedUrlType={setEmbedUrlType}
                                                   setEmbedUrlIsOpen={setEmbedUrlIsOpen}
                                                   setSnackbarEmbed={setSnackbarEmbed}
                                                   setDocModalOpen={setDocModalOpen}
                                                   setDocType={setDocType}
                                                   setSnackbarOpenDoc={setSnackbarOpenDoc}
                                                   setMeetingModalOpen={setMeetingModalOpen}
                                                   setMeetingType={setMeetingType}
                                                   setSnackbarOpenMeeting={setSnackbarOpenMeeting}
                                                   handleRemoveContent={handleRemoveContent}
                                                   meetingLink={meetingLink}
                                                   setMeetingLink={setMeetingLink}
                                                   docLink={docLink}
                                                   setDocLink={setDocLink}
                                                   embedVideoUrl={embedVideoUrl}
                                                   setEmbedVideoUrl={setEmbedVideoUrl}
                                                   postMeetingTime={postMeetingTime}
                                                   setPostMeetingTime={setPostMeetingTime}
                                                   meetingModalOpen={meetingModalOpen}
                                                   docModalOpen={docModalOpen}
                                                   embedUrlIsOpen={embedUrlIsOpen}
                                                   recordingStatus={recordingStatus}
                                                   setRecordingStatus={setRecordingStatus}
                                                   recordingDurationMins={recordingDurationMins}
                                                   setRecordingDurationMins={setRecordingDurationMins}
                                                   recordingDurationSeconds={recordingDurationSeconds}
                                                   setRecordingDurationSeconds={setRecordingDurationSeconds}
                                                   recordingDevices={recordingDevices}
                                                   setRecordingDevices={setRecordingDevices}
                                                   mediaRecorder={mediaRecorder}
                                                   mediaStream={mediaStream}
                                                   mediaDevices={mediaDevices}
                                                   setADevID={setADevID}
                                                   aDevID={aDevID}
                                                   changingDevice={changingDevice}
                                                   setChangingDevice={setChangingDevice}
                                                   localStream={localStream}
                                                   videoBitsPerSecond={videoBitsPerSecond}
                                                   setVDevID={setVDevID}
                                                   vDevID={vDevID}
                                                   recordedFileFormat={recordedFileFormat}
                                                   setRecordedFileFormat={setRecordedFileFormat}
                                                   setSelectedFile={setSelectedFile}
                                                   setShowPostButton={setShowPostButton}
                                                   embedUrlKey={embedUrlKey}
                                                   is_me={props.is_me}
                                                   handleAudioDeviceChange={handleAudioDeviceChange}
                                                   handleVideoDeviceChange={handleVideoDeviceChange}
                                                   saveFile={saveFile}
                                                   handleFullscreenOpen={handleFullscreenOpen}
                                                   processEmbedUrlSet={processEmbedUrlSet}
                                                   embedUrlType={embedUrlType}
                                                   setEmbedVideoUrlThread={setEmbedVideoUrlThread}
                                                   setEmbedVideoUrlComment={setEmbedVideoUrlComment}
                                                   setEmbedVideoUrlEdit={setEmbedVideoUrlEdit}
                                                   snackbarMsgEmbed={snackbarMsgEmbed}
                                                   snackbarEmbed={snackbarEmbed}
                                                   embedVideoUrlThread={embedVideoUrlThread}
                                                   embedVideoUrlComment={embedVideoUrlComment}
                                                   embedVideoUrlEdit={embedVideoUrlEdit}
                                                   docType={docType}
                                                   activeDocFileType={activeDocFileType}
                                                   setActiveDocFileType={setActiveDocFileType}
                                                   setFileTypeThread={setFileTypeThread}
                                                   setDocLinkThread={setDocLinkThread}
                                                   setFileTypeComment={setFileTypeComment}
                                                   setDocLinkComment={setDocLinkComment}
                                                   setDocLinkEdit={setDocLinkEdit}
                                                   setFileTypeEdit={setFileTypeEdit}
                                                   docLinkThread={docLinkThread}
                                                   docLinkComment={docLinkComment}
                                                   docLinkEdit={docLinkEdit}
                                                   checkDocLink={checkDocLink}
                                                   meetingType={meetingType}
                                                   setMeetingLinkThread={setMeetingLinkThread}
                                                     setMeetingLinkComment={setMeetingLinkComment}
                                                     setMeetingLinkEdit={setMeetingLinkEdit}
                                                     meetingLinkThread={meetingLinkThread}
                                                     meetingLinkComment={meetingLinkComment}
                                                     meetingLinkEdit={meetingLinkEdit}
                                                     snackbarMsgMeeting={snackbarMsgMeeting}
                                                     snackbarOpenMeeting={snackbarOpenMeeting}
                                                     snackbarSeverityMeeting={snackbarSeverityMeeting}
                                                     snackbarColorMeeting={snackbarColorMeeting}
                                                     checkMeetingLink={checkMeetingLink}
                                                     currentTabInfo={currentTabInfo}
                                                     uploadPostTags={uploadPostTags}
                                                     setUploadPostTags={setUploadPostTags}
                                                     editPostTags={editPostTags}
                                                     setEditPostTags={setEditPostTags}
                                                     addCommunityCategory={addCommunityCategory}
                                                     editCategoryModalOpen={editCategoryModalOpen}
                                                     onEditCategory={(cat) => {
                                                      setEditingCategory(cat);
                                                      setEditCategoryName(cat);
                                                      setEditCategoryModalOpen(true);
                                                      setTimeout(() => {
                                                         setEditCategoryModalAnimating(true);
                                                      }, 10);
                                                   }}
                                                   isUnlockedCommunityPreview={props.isUnlockedCommunityPreview}
                                                   setMeetingImageEdit={setMeetingImageEdit}
                                                   setMeetingImageFileNameEdit={setMeetingImageFileNameEdit}
                                                   meetingImageEdit={meetingImageEdit}
                                                   meetingImageFileNameEdit={meetingImageFileNameEdit}
                                                   setSelectedFileEdit={setSelectedFileEdit}
                                                   pendingCategories={pendingCategories}
                                                   setPendingCategories={setPendingCategories}
                                                />
                                             </div>
                                          </Grid>
                                          {selectedFilter === 'posts' && (
                                             <div style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>{categoriesSection}</div>
                                          )}
                                          {selectedFilter === 'posts' && pinned_post_block}
                                          <div
                                             ref={postsContainerRef}
                                             style={{ minWidth: '100%' }}
                                          >
                                             {community_post_blocks}
                                             {/* Sentinel element for Intersection Observer */}
                                             {scrollHasMore && (
                                                <div 
                                                   ref={scrollSentinelRef}
                                                   style={{
                                                      height: '1px',
                                                      width: '100%',
                                                      marginTop: '20px',
                                                   }}
                                                />
                                             )}
                                             {/* Loading indicator */}
                                             {scrollLoading && scrollHasMore && (
                                                <Box sx={{ display: 'flex', width: '100%', marginTop: '20px' }}>
                                                   <CircularProgress sx={{ margin: 'auto', color: 'var(--brand-color)' }} />
                                                </Box>
                                             )}
                                             {/* End message */}
                                             {!scrollHasMore && nPosts > 0 && (
                                                <p style={{
                                                   fontSize: '14px',
                                                   fontWeight: '500',
                                                   margin: '40px auto 40px auto',
                                                   color: '#6E89AF',
                                                   textAlign: 'center',
                                                   width: '90%',
                                                }}>
                                                   {t('community.reachedEnd')}</p>
                                             )}
                                          </div>
                                       </Grid>
                                    </Grid>
                                    : selectedFilter === 'upcoming-events' && props.windowWidth < 1280 ? (
                                       <Grid item xs={12} style={{ height: 'auto', margin: '0px 0px 0px 0px' }}>
                                               <CommunityUpcomingEvents brandColor={getBrandColor()} events={
                           postData.length === 0 && !hasRealPosts && props.is_me && showExamplePosts && communityEvents.length === 0
                              ? [
                                 { id: 'mock-event-1', community_post_title: t('community.examplePosts.event1Title'), event_time: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(), content_type: 'mzm', link: 'https://zoom.us/j/example', is_example: true },
                                 { id: 'mock-event-2', community_post_title: t('community.examplePosts.event2Title'), event_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), content_type: 'mgm', link: 'https://meet.google.com/example', is_example: true },
                              ]
                              : communityEvents
                        } onAddEvent={scrollToComposerAndOpenMeeting} windowWidth={props.windowWidth} is_me={props.is_me} />
                                       </Grid>
                                    ) : selectedFilter === 'about' && props.windowWidth < 1280 ? (
                                       <Grid item xs={12} style={{ height: 'auto', margin: '0px 0px 0px 0px' }}>
                                          {/* Community Info */}
                                          <div style={{
                                             backgroundColor: '#fff',
                                             border: '1px solid #E4EBF6',
                                             borderRadius: '12px',
                                             padding: '24px',
                                             marginBottom: '16px',
                                          }}>
                                             <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                                                {(() => {
                                                   const thumb = currentTabInfo?.product_thumbnail || currentTabInfo?.thumbnail;
                                                   return thumb && thumb !== 'null' ? (
                                                      <img src={thumb} alt="" style={{ width: 56, height: 56, borderRadius: '14px', objectFit: 'cover', flexShrink: 0 }} />
                                                   ) : (
                                                      <div style={{
                                                         width: 56, height: 56, borderRadius: '14px', flexShrink: 0,
                                                         background: `linear-gradient(135deg, ${getBrandColor()} 0%, ${getBrandColor()}cc 100%)`,
                                                         display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                         fontSize: '20px', fontWeight: 700, color: '#fff',
                                                      }}>
                                                         {currentTabInfo?.name ? currentTabInfo.name.substring(0, 2).toUpperCase() : 'CM'}
                                                      </div>
                                                   );
                                                })()}
                                                <div>
                                                   <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1D1D1D', margin: 0, fontFamily: 'Roboto, sans-serif' }}>
                                                      {currentTabInfo?.name || 'Community'}
                                                   </h3>
                                                   <p style={{ fontSize: '13px', color: '#9CA3AF', margin: '2px 0 0 0', fontFamily: 'Roboto, sans-serif' }}>
                                                      {props.display_name || props.influencer}
                                                   </p>
                                                </div>
                                             </div>

                                             {/* Description */}
                                             <CommunityDescription description={currentTabInfo?.description || t('community.preview.defaultDescription')} />

                                             {/* Member count */}
                                             <div style={{
                                                display: 'flex', justifyContent: 'space-around', alignItems: 'center',
                                                padding: '10px 0 4px 0', margin: '0 0 12px 0',
                                                borderTop: '1px solid #E4EBF6',
                                             }}>
                                                <div style={{ textAlign: 'center' }}>
                                                   <p style={{ fontSize: '14px', fontWeight: '700', color: '#1D1D1D', margin: '0', fontFamily: 'Roboto, sans-serif' }}>
                                                      {influencerTotalMembers || 0}
                                                   </p>
                                                   <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '2px 0 0 0', fontFamily: 'Roboto, sans-serif' }}>
                                                      {influencerTotalMembers === 1 ? t('community.library.member', 'Member') : t('community.library.members', 'Members')}
                                                   </p>
                                                </div>
                                             </div>

                                             {/* Share button */}
                                             <Button
                                                onClick={() => {
                                                   setShareLinkCopied(false);
                                                   setShareModalOpen(true);
                                                }}
                                                variant="contained"
                                                startIcon={<TbShare />}
                                                style={{
                                                   width: '100%',
                                                   padding: '8px 24px',
                                                   borderRadius: '8px',
                                                   border: 'none',
                                                   color: '#fff',
                                                   fontSize: '13px',
                                                   fontWeight: '600',
                                                   textTransform: 'none',
                                                   fontFamily: 'Roboto, sans-serif',
                                                   background: getBrandColor() || 'var(--brand-color)',
                                                   boxShadow: 'none',
                                                }}
                                             >
                                                {t('community.share')}
                                             </Button>
                                          </div>
                                       </Grid>
                                    ) : selectedFilter === 'library' ? (
                                       <Library {...props} libraryItems={libraryItems} setLibraryItems={setLibraryItems} tabId={tabId} />
                                    ) : selectedFilter === '_disabled_old_library_' ? (
                                       <Grid item xs={12} style={{ height: 'auto', margin: '0px 0px 0px 0px' }}>
                                         {(libraryProductView || libraryProductLoading || libraryCourseView || libraryCourseLoading || libraryMembershipView || libraryMembershipLoading || libraryCoachingView || libraryCommunityView || libraryMessagesView) ? (
                                             <div>
                                                {/* Back arrow */}
                                                <div
                                                   onClick={() => { setLibraryProductView(null); setLibraryProductLoading(false); setLibraryCourseView(null); setLibraryCourseLoading(false); setLibraryMembershipView(null); setLibraryMembershipLoading(false); setLibraryCoachingView(null); setLibraryCommunityView(null); setLibraryMessagesView(null); }}
                                                   style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 0', cursor: 'pointer', marginBottom: '20px' }}
                                                >
                                                   <span style={{ fontSize: '18px', color: '#6B7280' }}>←</span>
                                                   <span style={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', fontFamily: 'Roboto, sans-serif' }}>
                                                      {t('community.library.backToLibrary', 'Back to Library')}
                                                   </span>
                                                </div>
                                               {(libraryProductLoading || libraryCourseLoading || libraryMembershipLoading) ? (
                                                   <div style={{ backgroundColor: '#fff', border: '1px solid #E4EBF6', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
                                                      <CircularProgress size={24} />
                                                   </div>
                                                ) : libraryProductView ? (
                                                   <div style={{ maxWidth: props.windowWidth <= 600 ? '100%' : '600px', width: '100%', margin: '0 auto', padding: '0px' }}>
                                                      <CreatorContentCard
                                                         key={`library-product-${libraryProductView.id}`}
                                                         {...props}
                                                         {...libraryProductView}
                                                         windowWidth={props.windowWidth}
                                                         is_me={false}
                                                         influencer={props.influencer}
                                                         current_user={props.current_user}
                                                         currency={props.currency}
                                                         view_type={props.view_type}
                                                         display_name={props.display_name}
                                                      />
                                                   </div>
                                               ) : libraryMembershipView ? (
                                                  <div style={{
                                                     display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                                     width: props.windowWidth <= 600 ? '100%' : '600px',
                                                     maxWidth: props.windowWidth <= 600 ? '100%' : '600px',
                                                     minWidth: props.windowWidth > 1500 ? '600px' : 'auto',
                                                     margin: '0 auto',
                                                  }}>
                                                     {libraryMembershipView?.name && (
                                                        <Typography style={{ fontSize: '20px', fontWeight: 700, color: '#1D1D1D', marginBottom: '14px' }}>
                                                           {libraryMembershipView.name}
                                                        </Typography>
                                                     )}
                                                     <div style={{ marginBottom: '18px' }}>
                                                        <ContentFilters
                                                           designProperties={props.designProperties}
                                                           is_me={props.is_me}
                                                           windowWidth={props.windowWidth}
                                                           anchorEl={libraryMembershipFilterUi.anchorEl}
                                                           setState={(updates) => setLibraryMembershipFilterUi((prev) => ({ ...prev, ...updates }))}
                                                           content_menu_open={libraryMembershipFilterUi.content_menu_open}
                                                           category_menu_open={libraryMembershipFilterUi.category_menu_open}
                                                           tier_menu_open={libraryMembershipFilterUi.tier_menu_open}
                                                           sort_menu_open={libraryMembershipFilterUi.sort_menu_open}
                                                           content_filter={libraryMembershipContentFilter}
                                                           category_filter={libraryMembershipCategoryFilter}
                                                           tier_filter={libraryMembershipTier}
                                                           sort_filter={libraryMembershipSortFilter}
                                                           content_types={libraryMembershipContentTypes}
                                                           categories={membershipCategories}
                                                           sort_options={membershipSortOptions}
                                                           tierOptions={membershipTierOptions}
                                                           selected_tab={'sub_posts'}
                                                           onSelectContentType={(val) => {
                                                              setLibraryMembershipFilterUi((prev) => ({ ...prev, content_menu_open: false }));
                                                              handleLibraryMembershipFilterChange('content', val);
                                                           }}
                                                           onSelectCategory={(val) => {
                                                              setLibraryMembershipFilterUi((prev) => ({ ...prev, category_menu_open: false }));
                                                              handleLibraryMembershipFilterChange('category', val);
                                                           }}
                                                           onSelectTier={(val) => {
                                                              setLibraryMembershipFilterUi((prev) => ({ ...prev, tier_menu_open: false }));
                                                              setLibraryMembershipTier(val);
                                                              setLibraryMembershipContentList([]);
                                                              setLibraryMembershipContentListLength(0);
                                                              setLibraryMembershipPagePosition(0);
                                                              setLibraryMembershipPageN(1);
                                                              setLibraryMembershipInfScroll(true);
                                                              setLibraryMembershipLoading(true);
                                                              loadLibraryMembershipContent(val, 1);
                                                           }}
                                                           onSelectSort={(val) => {
                                                              setLibraryMembershipFilterUi((prev) => ({ ...prev, sort_menu_open: false }));
                                                              handleLibraryMembershipFilterChange('sort', val);
                                                           }}
                                                        />
                                                        <Box sx={{ justifyContent: 'center', margin: '24px auto 20px 0px' }}>
                                                           <SearchBarInfeed
                                                              {...props}
                                                              is_me={props.is_me}
                                                              influencer_username={props.influencer}
                                                              influencer_display_name={props.display_name || props.influencer}
                                                              type={'sub_posts'}
                                                              search_func={handleMembershipSearchResult}
                                                              search_func_collection={() => {}}
                                                           />
                                                        </Box>
                                                     </div>
                                                     {(() => {
                                                        const nPages = Math.ceil(libraryMembershipContentListLength / 20);
                                                        const filteredList = (libraryMembershipContentList || []).filter((item) => {
                                                           if (!libraryMembershipSearchQuery) return true;
                                                           const q = libraryMembershipSearchQuery.toLowerCase();
                                                           const name = (item.name || item.community_post_title || '').toLowerCase();
                                                           const desc = (item.description || item.content_text || '').replace(/<[^>]*>/g, '').toLowerCase();
                                                           return name.includes(q) || desc.includes(q);
                                                        });
                                                        if (filteredList.length === 0 && !libraryMembershipInfScroll) return (
                                                           <div style={{ backgroundColor: '#fff', border: '1px solid #E4EBF6', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
                                                              <p style={{ fontSize: '14px', color: '#9CA3AF' }}>{t('community.library.noContent', 'No content available')}</p>
                                                           </div>
                                                        );
                                                        return (
                                                           <div style={{
                                                              display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                                              width: props.windowWidth <= 600 ? '100%' : '600px',
                                                              maxWidth: props.windowWidth <= 600 ? '100%' : '600px',
                                                              minWidth: props.windowWidth > 1500 ? '600px' : 'auto',
                                                              margin: '0 auto',
                                                           }}>
                                                              {nPages > 1 && (
                                                                 <Grid container alignItems="center" justifyContent="center" style={{ marginTop: '20px', marginBottom: '20px' }}>
                                                                    <StyledPagination
                                                                       count={nPages}
                                                                       page={libraryMembershipPageN}
                                                                       onChange={(event, page) => {
                                                                          handleLibraryMembershipPageChange(page);
                                                                          window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                       }}
                                                                    />
                                                                 </Grid>
                                                              )}
                                                              <InfiniteScroll
                                                                 dataLength={filteredList.length}
                                                                 next={handleLibraryMembershipInfScroll}
                                                                 hasMore={libraryMembershipInfScroll}
                                                                 loader={<div style={{ textAlign: 'center', padding: '20px' }}><CircularProgress size={24} /></div>}
                                                              >
                                                                 {filteredList.map(item => (
                                                                    <div id={`membership-content-${item.id}`} key={`membership-content-${item.id}`} style={{
                                                                       paddingBottom: '30px',
                                                                       width: props.windowWidth <= 600 ? '100%' : '600px',
                                                                       maxWidth: props.windowWidth <= 600 ? '100%' : '600px',
                                                                       minWidth: props.windowWidth > 1500 ? '600px' : 'auto',
                                                                       margin: '0px auto',
                                                                    }}>
                                                                       <CreatorContentCard
                                                                          {...props}
                                                                          {...item}
                                                                          windowWidth={props.windowWidth}
                                                                          is_me={false}
                                                                          influencer={props.influencer}
                                                                          current_user={props.current_user}
                                                                          currency={props.currency}
                                                                          view_type={props.view_type}
                                                                          display_name={props.display_name}
                                                                       />
                                                                    </div>
                                                                 ))}
                                                              </InfiniteScroll>
                                                           </div>
                                                        );
                                                     })()}
                                                  </div>
                                               ) : libraryCoachingView ? (
                                                  <Async
                                                     {...props}
                                                     display_name={props.display_name}
                                                     current_user={props.current_user}
                                                     current_user_icon={props.current_user_icon}
                                                     username={props.current_user}
                                                     is_me={props.is_me}
                                                     influencer={props.influencer}
                                                     asyncActive={props.subscribe_popup_async_active}
                                                     influencer_tiers={props.influencer_tiers}
                                                     active_influencer_tiers={[
                                                        ...(props.active_influencer_tiers || []),
                                                        ...(props.subscribe_popup_async_active && props.subscribe_popup_async_name ? [props.subscribe_popup_async_name] : [])
                                                     ]}
                                                     influencer_info={props.influencer_info}
                                                     currency={props.currency}
                                                     selectedMemberTabOverwrite={props.selectedMemberTabOverwrite}
                                                     post_id={props.post_id}
                                                     async_apply={props.async_apply}
                                                     windowWidth={props.windowWidth}
                                                     setState={props.setState}
                                                     scroll={false}
                                                     current_tier={props.is_me ? 4 : props.subscribe_popup_current_tier}
                                                     async_tier_access={props.is_me ? true : props.async_tier_access}
                                                     hide_offering_card={true}
                                                  />
                                               ) : libraryMessagesView ? (
                                                  <PaidDMs
                                                     {...props}
                                                     current_user={props.current_user}
                                                     display_name={props.display_name}
                                                     current_user_icon={props.current_user_icon}
                                                     username={props.current_user}
                                                     is_me={props.is_me}
                                                     influencer={props.influencer}
                                                     influencer_info={props.influencer_info}
                                                     currency={props.currency}
                                                     post_id={props.post_id}
                                                     dm_active={props.dm_active}
                                                     dmSettingsId={libraryMessagesView?.id}
                                                     windowWidth={Math.max(window.innerWidth, 1280)}
                                                     activeProductData={props.activeProductData}
                                                     setState={props.setState}
                                                     setMaxMessageLength={() => {}}
                                                     maxMessageLength={10000}
                                                     updateHasMessage={() => {}}
                                                     isLibraryView={true}
                                                  />
                                               ) : libraryCommunityView ? (
                                                  <Community
                                                     {...props}
                                                     display_name={props.display_name}
                                                     current_user={props.current_user}
                                                     current_user_icon={props.current_user_icon}
                                                     username={props.current_user}
                                                     is_me={props.is_me}
                                                     influencer={props.influencer}
                                                     influencer_tiers={props.influencer_tiers}
                                                     active_influencer_tiers={props.active_influencer_tiers}
                                                     influencer_info={props.influencer_info}
                                                     currency={props.currency}
                                                     current_tier={props.subscribe_popup_current_tier}
                                                     post_id={props.post_id}
                                                     tab_id={libraryCommunityView?.id}
                                                     hide_tab_switcher={true}
                                                     view_type={props.view_type}
                                                     setState={props.setState}
                                                     windowWidth={props.windowWidth}
                                                     theme={props.theme}
                                                  />
                                               ) : libraryCourseView ? (
                                                   <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                      {/* Desktop: Chapter sidebar */}
                                                      {libraryCourseView.chapters && libraryCourseView.chapters.length > 0 &&
                                                       !((props.is_me === true && props.windowWidth <= 1424)
                                                       || (props.is_me === false && props.windowWidth <= 1100)) && (
                                                         <div style={{
                                                            position: 'absolute', left: '0', top: '0', width: '25%', maxWidth: '250px',
                                                            height: 'fit-content', maxHeight: 'calc(100vh - 100px)', overflowY: 'auto', paddingRight: '20px', zIndex: 1,
                                                         }}>
                                                            <div style={{
                                                               backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #E4EBF6',
                                                               padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', position: 'sticky', top: '80px',
                                                            }}>
                                                               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #E4EBF6' }}>
                                                                  <span style={{ fontSize: '16px', fontWeight: 600, color: '#1D1D1D' }}>
                                                                     {t('community.library.chapters', 'Chapters')}
                                                                  </span>
                                                               </div>
                                                               <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                                  {libraryCourseView.chapters.map(ch => {
                                                                     const isSelected = libraryCourseChapter === ch.chapter_name;
                                                                     return (
                                                                        <div
                                                                           key={ch.id}
                                                                           onClick={() => {
                                                                              setLibraryCourseChapter(ch.chapter_name);
                                                                              setLibraryCourseContentList([]);
                                                                              setLibraryCourseInfScroll(true);
                                                                              loadLibraryCourseContent(libraryCourseView?.course?.name || '', ch.chapter_name, 1);
                                                                           }}
                                                                           style={{
                                                                              padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease',
                                                                              backgroundColor: isSelected ? '#E6EDFB' : 'transparent',
                                                                              border: isSelected ? '1px solid var(--brand-color, #2563EB)' : '1px solid transparent',
                                                                              display: 'flex', alignItems: 'center', gap: '8px',
                                                                           }}
                                                                        >
                                                                           <span style={{ fontSize: '14px', fontWeight: isSelected ? 600 : 500, color: isSelected ? 'var(--brand-color, #2563EB)' : '#1D1D1D', textTransform: 'capitalize' }}>
                                                                              {ch.chapter_name}
                                                                           </span>
                                                                        </div>
                                                                     );
                                                                  })}
                                                                  {libraryCourseView.chapter_content['_uncategorised_'] && libraryCourseView.chapter_content['_uncategorised_'].length > 0 && (
                                                                     <div
                                                                        onClick={() => {
                                                                           setLibraryCourseChapter('_uncategorised_');
                                                                           setLibraryCourseContentList([]);
                                                                           setLibraryCourseInfScroll(true);
                                                                           loadLibraryCourseContent(libraryCourseView?.course?.name || '', '_uncategorised_', 1);
                                                                        }}
                                                                        style={{
                                                                           padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease',
                                                                           backgroundColor: libraryCourseChapter === '_uncategorised_' ? '#E6EDFB' : 'transparent',
                                                                           border: libraryCourseChapter === '_uncategorised_' ? '1px solid var(--brand-color, #2563EB)' : '1px solid transparent',
                                                                        }}
                                                                     >
                                                                        <span style={{ fontSize: '14px', fontWeight: libraryCourseChapter === '_uncategorised_' ? 600 : 500, color: libraryCourseChapter === '_uncategorised_' ? 'var(--brand-color, #2563EB)' : '#1D1D1D' }}>
                                                                           {t('community.library.other', 'Other')}
                                                                        </span>
                                                                     </div>
                                                                  )}
                                                               </div>
                                                            </div>
                                                         </div>
                                                      )}
                                                      {/* Mobile: Chapter dropdown */}
                                                      {libraryCourseView.chapters && libraryCourseView.chapters.length > 0 &&
                                                       ((props.is_me === true && props.windowWidth <= 1424)
                                                       || (props.is_me === false && props.windowWidth <= 1100)) && (
                                                         <div style={{ width: '100%', maxWidth: '600px', margin: '0 auto 20px auto', padding: '0 12px', boxSizing: 'border-box' }}>
                                                            <p style={{ fontSize: '16px', fontWeight: 600, color: '#1D1D1D', marginBottom: '12px' }}>
                                                               {t('community.library.chapters', 'Chapters')}
                                                            </p>
                                                            <FormControl fullWidth sx={{ minWidth: 0 }}>
                                                               <Select
                                                                  value={libraryCourseChapter || ''}
                                                                  onChange={(e) => {
                                                                     const ch = e.target.value;
                                                                     setLibraryCourseChapter(ch);
                                                                     setLibraryCourseContentList([]);
                                                                     setLibraryCourseInfScroll(true);
                                                                     loadLibraryCourseContent(libraryCourseView?.course?.name || '', ch, 1);
                                                                  }}
                                                                  sx={{
                                                                     height: '42px',
                                                                     borderRadius: '8px',
                                                                     fontSize: { xs: '13px', sm: '14px' },
                                                                     fontWeight: 500,
                                                                     textTransform: 'capitalize',
                                                                     backgroundColor: '#fff',
                                                                     '& .MuiSelect-select': {
                                                                        padding: { xs: '10px 32px 10px 12px', sm: '10px 32px 10px 14px' },
                                                                     },
                                                                     '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#E4EBF6',
                                                                     },
                                                                     '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: 'var(--brand-color)',
                                                                     },
                                                                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: 'var(--brand-color)',
                                                                        borderWidth: '1px',
                                                                     },
                                                                  }}
                                                                  MenuProps={{
                                                                     PaperProps: {
                                                                        sx: {
                                                                           maxHeight: '300px',
                                                                           borderRadius: '8px',
                                                                           boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                                           maxWidth: 'calc(100vw - 24px)',
                                                                        },
                                                                     },
                                                                     anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                                     transformOrigin: { vertical: 'top', horizontal: 'left' },
                                                                  }}
                                                               >
                                                                  {libraryCourseView.chapters.map(ch => (
                                                                     <MenuItem key={ch.id} value={ch.chapter_name}>
                                                                        {ch.chapter_name}
                                                                     </MenuItem>
                                                                  ))}
                                                                  {libraryCourseView.chapter_content['_uncategorised_'] && libraryCourseView.chapter_content['_uncategorised_'].length > 0 && (
                                                                     <MenuItem value="_uncategorised_">
                                                                        {t('community.library.other', 'Other')}
                                                                     </MenuItem>
                                                                  )}
                                                               </Select>
                                                            </FormControl>
                                                         </div>
                                                      )}
                                                      {/* Content items */}
                                                      {(() => {
                                                         const nPages = Math.ceil(libraryCourseContentListLength / 20);
                                                         if (libraryCourseContentList.length === 0 && !libraryCourseInfScroll) return (
                                                            <div style={{ backgroundColor: '#fff', border: '1px solid #E4EBF6', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
                                                               <p style={{ fontSize: '14px', color: '#9CA3AF' }}>{t('community.library.noContent', 'No content in this chapter')}</p>
                                                            </div>
                                                         );
                                                         return (
                                                            <div style={{
                                                               display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                                               width: props.windowWidth <= 600 ? '100%' : '600px',
                                                               maxWidth: props.windowWidth <= 600 ? '100%' : '600px',
                                                               minWidth: props.windowWidth > 1500 ? '600px' : 'auto',
                                                               margin: '0 auto',
                                                            }}>
                                                               {nPages > 1 && (
                                                                  <Grid container alignItems="center" justifyContent="center" style={{ marginTop: '20px', marginBottom: '20px' }}>
                                                                     <StyledPagination
                                                                        count={nPages}
                                                                        page={libraryCoursePageN}
                                                                        onChange={(event, page) => {
                                                                           handleLibraryCoursePageChange(page);
                                                                           window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                        }}
                                                                     />
                                                                  </Grid>
                                                               )}
                                                               <InfiniteScroll
                                                                  dataLength={libraryCourseContentList.length}
                                                                  next={handleLibraryCourseInfScroll}
                                                                  hasMore={libraryCourseInfScroll}
                                                                  loader={<div style={{ textAlign: 'center', padding: '20px' }}><CircularProgress size={24} /></div>}
                                                               >
                                                                  {libraryCourseContentList.map(item => (
                                                                     <div key={`course-content-${item.id}`} style={{
                                                                        paddingBottom: '30px',
                                                                        width: props.windowWidth <= 600 ? '100%' : '600px',
                                                                        maxWidth: props.windowWidth <= 600 ? '100%' : '600px',
                                                                        minWidth: props.windowWidth > 1500 ? '600px' : 'auto',
                                                                        margin: '0px auto',
                                                                     }}>
                                                                        <CreatorContentCard
                                                                           {...props}
                                                                           {...item}
                                                                           windowWidth={props.windowWidth}
                                                                           is_me={false}
                                                                           influencer={props.influencer}
                                                                           current_user={props.current_user}
                                                                           currency={props.currency}
                                                                           view_type={props.view_type}
                                                                           display_name={props.display_name}
                                                                        />
                                                                     </div>
                                                                  ))}
                                                               </InfiniteScroll>
                                                               {nPages > 1 && (
                                                                  <Grid container alignItems="center" justifyContent="center" style={{ marginTop: '20px' }}>
                                                                     <StyledPagination
                                                                        count={nPages}
                                                                        page={libraryCoursePageN}
                                                                        onChange={(event, page) => {
                                                                           handleLibraryCoursePageChange(page);
                                                                           window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                        }}
                                                                     />
                                                                  </Grid>
                                                               )}
                                                            </div>
                                                         );
                                                      })()}
                                                   </div>
                                                ) : null}
                                             </div>
                                          ) : libraryItems.length === 0 ? (
                                             <div style={{ backgroundColor: '#fff', border: '1px solid #E4EBF6', borderRadius: '12px', padding: '24px' }}>
                                                <p style={{ fontSize: '14px', color: '#9CA3AF', textAlign: 'center', padding: '20px 0' }}>
                                                   {t('community.noLibraryItems')}
                                                </p>
                                             </div>
                                          ) : (
                                             (() => {
                                                const grouped = libraryItems.reduce((acc, item) => {
                                                   (acc[item.type] = acc[item.type] || []).push(item);
                                                   return acc;
                                                }, {});
                                                const typeConfig = {
                                                   membership: { icon: <TbRosette size={20} />, label: t('community.library.memberships', 'Memberships'), bg: '#DBEAFE', color: '#1E40AF' },
                                                   coaching: { icon: <TbHeartHandshake size={20} />, label: t('community.library.coaching', '1:1 Coaching'), bg: '#FCE7F3', color: '#9D174D' },
                                                   coaching_call: { icon: <TbUsers size={20} />, label: t('community.library.coachingCalls', 'Coaching calls'), bg: '#E0E7FF', color: '#3730A3' },
                                                   webinar: { icon: <TbUsers size={20} />, label: t('community.library.webinars', 'Webinars'), bg: '#E0E7FF', color: '#3730A3' },
                                                   course: { icon: <TbSchool size={20} />, label: t('community.library.courses', 'Courses'), bg: '#FEF3C7', color: '#92400E' },
                                                   digital_product: { icon: <TbPackage size={20} />, label: t('community.library.digitalProducts', 'Digital products'), bg: '#EDE9FE', color: '#5B21B6' },
                                                   community: { icon: <TbSeeding size={20} />, label: t('community.library.communities', 'Communities'), bg: '#D1FAE5', color: '#065F46' },
                                                   messages: { icon: <TbMessageCircleDollar size={20} />, label: t('community.library.customProducts', 'Custom products'), bg: '#FFEDD5', color: '#9A3412' },
                                                   url: { icon: <TbLink size={20} />, label: t('community.library.outsideUrls', 'Outside URLs'), bg: '#E0F2FE', color: '#0369A1' },
                                                };
                                                const contentTypeBadge = (ct) => {
                                                   const map = { vid: 'Video', aud: 'Audio', img: 'Image', pdf: 'PDF', bnd: 'Bundle', txt: 'Text' };
                                                   return map[ct] || '';
                                                };
                                                const itemsPerRow = Math.min(3, Math.max(1, Math.floor((props.windowWidth - 32) / (160 + 12))));
                                                return Object.entries(grouped).map(([type, items]) => {
                                                   const isExpanded = expandedLibrarySections[type];
                                                   const visibleItems = isExpanded ? items : items.slice(0, itemsPerRow);
                                                   return (
                                                   <div key={type} style={{ marginBottom: '24px' }}>
                                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                         <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ fontSize: '18px', display: 'flex', color: typeConfig[type]?.color || '#374151' }}>{typeConfig[type]?.icon}</span>
                                                            <span style={{ fontSize: '15px', fontWeight: 700, color: '#1D1D1D', fontFamily: 'Roboto, sans-serif' }}>
                                                               {typeConfig[type]?.label || type}
                                                            </span>
                                                            <span style={{ fontSize: '13px', color: '#9CA3AF', fontWeight: 500 }}>{items.length}</span>
                                                         </div>
                                                         {items.length > itemsPerRow && (
                                                            <span
                                                               onClick={(e) => { e.stopPropagation(); setExpandedLibrarySections(prev => ({ ...prev, [type]: !prev[type] })); }}
                                                               style={{ fontSize: '13px', fontWeight: 600, color: 'var(--brand-color)', cursor: 'pointer', fontFamily: 'Roboto, sans-serif' }}>
                                                               {isExpanded ? t('community.library.hide', 'Hide') : `${t('community.library.viewAll', 'View all')} \u2192`}
                                                            </span>
                                                         )}
                                                      </div>
                                                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`, gap: '12px' }}>
                                                         {visibleItems.map((item) => (
                                                            <div
                                                               key={`${item.type}-${item.id}`}
                                                               onClick={() => {
                                                                  if (item.type === 'digital_product') {
                                                                     openLibraryProduct(item.id);
                                                                     window.scrollTo({ top: 0, behavior: 'instant' });
                                                                  }
                                                                  else if (item.type === 'course') { openLibraryCourse(item.id); window.scrollTo({ top: 0, behavior: 'instant' }); }
                                                                  else if (item.type === 'community') { openLibraryCommunity(item); }
                                                                  else if (item.type === 'membership') openLibraryMembership(item);
                                                                  else if (item.type === 'coaching') { openLibraryCoaching(item); window.scrollTo({ top: 0, behavior: 'instant' }); }
                                                                  else if (item.type === 'coaching_call' || item.type === 'webinar') { if (item.url) window.open(item.url, '_blank'); else window.location.assign(`/${props.influencer}?open_url_product=true&product_id=${item.id}`); }
                                                                  else if (item.type === 'messages') { openLibraryMessages(item); }
                                                                  else if (item.type === 'url') { if (item.url) window.open(item.url, '_blank'); else window.location.assign(`/${props.influencer}?open_url_product=true&product_id=${item.id}`); }
                                                               }}
                                                               style={{
                                                                  backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #E4EBF6',
                                                                  cursor: 'pointer', transition: 'all 0.2s', overflow: 'hidden',
                                                               }}
                                                               onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                               onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
                                                            >
                                                               <div style={{ width: '100%', aspectRatio: '16/9', backgroundColor: '#F3F4F6', position: 'relative', overflow: 'hidden' }}>
                                                                  <img src={item.thumbnail || 'data:,'} alt=""
                                                                     style={{ width: '100%', height: '100%', objectFit: 'cover', display: item.thumbnail ? 'block' : 'none', position: 'relative', zIndex: 1 }}
                                                                     onError={(e) => { e.target.style.display = 'none'; }}
                                                                     onLoad={(e) => { if (e.target.naturalWidth === 0) { e.target.style.display = 'none'; } }} />
                                                                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                     backgroundColor: typeConfig[item.type]?.bg || '#F3F4F6', position: 'absolute', top: 0, left: 0 }}>
                                                                     <span style={{ color: typeConfig[item.type]?.color || '#374151' }}>
                                                                        {React.cloneElement(typeConfig[item.type]?.icon || <TbPackage size={36} />, { size: 36 })}
                                                                     </span>
                                                                  </div>
                                                                  {item.type === 'course' && (item.n_modules > 0 || item.n_lessons > 0) && (
                                                                     <div style={{ position: 'absolute', bottom: '6px', left: '6px', right: '6px', display: 'flex', gap: '4px' }}>
                                                                        {item.n_modules > 0 && <span style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px' }}>{item.n_modules} {item.n_modules === 1 ? 'chapter' : 'chapters'}</span>}
                                                                        {item.n_lessons > 0 && <span style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px' }}>{item.n_lessons} {item.n_lessons === 1 ? 'post' : 'posts'}</span>}
                                                                     </div>
                                                                  )}
                                                                  {item.type === 'digital_product' && item.content_type && (
                                                                     <span style={{ position: 'absolute', top: '6px', left: '6px', backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px' }}>
                                                                        {contentTypeBadge(item.content_type)}
                                                                     </span>
                                                                  )}
                                                               </div>
                                                               <div style={{ padding: '12px 14px' }}>
                                                                  <p style={{ fontSize: '13px', fontWeight: 700, color: '#1D1D1D', margin: '0 0 4px 0', fontFamily: 'Roboto, sans-serif',
                                                                     overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.3 }}>
                                                                     {item.name}
                                                                  </p>
                                                                  {item.description && (
                                                                     <p style={{ fontSize: '11px', color: '#6B7280', margin: 0, fontFamily: 'Roboto, sans-serif',
                                                                        overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                                                                        {item.description.replace(/<[^>]*>/g, '')}
                                                                     </p>
                                                                  )}
                                                               </div>
                                                            </div>
                                                         ))}
                                                      </div>
                                                   </div>
                                                   );
                                                });
                                             })()
                                          )}
                                       </Grid>
                                    ) : ''
                              }


                           </div>
                     }


                     {/* Desktop Version */}
                     {
                        props.windowWidth >= 1280 ?
                           <div style={{
              

                              filter: 'none',
                              maxHeight: '',
                              minHeight: '',
                              overflow: '',
                              pointerEvents: "auto",
                              userSelect: "text",
                           }}>

                              <motion.div 
                                 initial={{ opacity: 0, y: 20 }}
                                 animate={{ opacity: 1, y: 0 }}
                                 exit={{ opacity: 0, y: -20 }}
                                 transition={{ duration: 0.6, ease: "easeOut" }}
                                          style={{
                                    display: 'flex', 
                                    height: '100%', 
                                    width: '100%', 
                                    position: 'relative',
                                    gap: '40px',
                                    maxWidth: '1200px',
                                    margin: '0 auto',
                                    flexDirection: 'row',
                                    padding: props.windowWidth < 600 ? '10px 0px 40px 0px' : '10px 0px 40px 0px',
                                    boxSizing: 'border-box',
                                    minHeight: '600px'
                                 }}>
                                 {/* Main content area - expanded to match locked state width */}
                                 <div style={{
                                    flex: '2',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '24px',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                 }}>
                                    {selectedFilter === 'library' ? (
                                       <Library {...props} libraryItems={libraryItems} setLibraryItems={setLibraryItems} tabId={tabId} />
                                    ) : selectedFilter === '_disabled_old_library_mobile_' ? (
                                       <div style={{ padding: '0' }}>
                                         {(libraryProductView || libraryProductLoading || libraryCourseView || libraryCourseLoading || libraryMembershipView || libraryMembershipLoading || libraryCoachingView || libraryCommunityView || libraryMessagesView) ? (
                                             <div>
                                                {/* Back arrow */}
                                                <div
                                                   onClick={() => { setLibraryProductView(null); setLibraryProductLoading(false); setLibraryCourseView(null); setLibraryCourseLoading(false); setLibraryMembershipView(null); setLibraryMembershipLoading(false); setLibraryCoachingView(null); setLibraryCommunityView(null); setLibraryMessagesView(null); }}
                                                   style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 0', cursor: 'pointer', marginBottom: '20px' }}
                                                >
                                                   <span style={{ fontSize: '18px', color: '#6B7280' }}>←</span>
                                                   <span style={{ fontSize: '14px', fontWeight: 600, color: '#6B7280', fontFamily: 'Roboto, sans-serif' }}>
                                                      {t('community.library.backToLibrary', 'Back to Library')}
                                                   </span>
                                                </div>
                                               {(libraryProductLoading || libraryCourseLoading || libraryMembershipLoading) ? (
                                                   <div style={{ backgroundColor: '#fff', border: '1px solid #E4EBF6', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
                                                      <CircularProgress size={24} />
                                                   </div>
                                                ) : libraryProductView ? (
                                                   <div style={{ maxWidth: props.windowWidth <= 600 ? '100%' : '600px', width: '100%', margin: '0 auto', padding: '0px' }}>
                                                      <CreatorContentCard
                                                         key={`library-product-${libraryProductView.id}`}
                                                         {...props}
                                                         {...libraryProductView}
                                                         windowWidth={props.windowWidth}
                                                         is_me={false}
                                                         influencer={props.influencer}
                                                         current_user={props.current_user}
                                                         currency={props.currency}
                                                         view_type={props.view_type}
                                                         display_name={props.display_name}
                                                      />
                                                   </div>
                                               ) : libraryMembershipView ? (
                                                  <div style={{
                                                     display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                                     width: props.windowWidth <= 600 ? '100%' : '600px',
                                                     maxWidth: props.windowWidth <= 600 ? '100%' : '600px',
                                                     minWidth: props.windowWidth > 1500 ? '600px' : 'auto',
                                                     margin: '0 auto',
                                                  }}>
                                                     {libraryMembershipView?.name && (
                                                        <Typography style={{ fontSize: '20px', fontWeight: 700, color: '#1D1D1D', marginBottom: '14px' }}>
                                                           {libraryMembershipView.name}
                                                        </Typography>
                                                     )}
                                                     <div style={{ marginBottom: '18px' }}>
                                                        <ContentFilters
                                                           designProperties={props.designProperties}
                                                           is_me={props.is_me}
                                                           windowWidth={props.windowWidth}
                                                           anchorEl={libraryMembershipFilterUi.anchorEl}
                                                           setState={(updates) => setLibraryMembershipFilterUi((prev) => ({ ...prev, ...updates }))}
                                                           content_menu_open={libraryMembershipFilterUi.content_menu_open}
                                                           category_menu_open={libraryMembershipFilterUi.category_menu_open}
                                                           tier_menu_open={libraryMembershipFilterUi.tier_menu_open}
                                                           sort_menu_open={libraryMembershipFilterUi.sort_menu_open}
                                                           content_filter={libraryMembershipContentFilter}
                                                           category_filter={libraryMembershipCategoryFilter}
                                                           tier_filter={libraryMembershipTier}
                                                           sort_filter={libraryMembershipSortFilter}
                                                           content_types={libraryMembershipContentTypes}
                                                           categories={membershipCategories}
                                                           sort_options={membershipSortOptions}
                                                           tierOptions={membershipTierOptions}
                                                           selected_tab={'sub_posts'}
                                                           onSelectContentType={(val) => {
                                                              setLibraryMembershipFilterUi((prev) => ({ ...prev, content_menu_open: false }));
                                                              handleLibraryMembershipFilterChange('content', val);
                                                           }}
                                                           onSelectCategory={(val) => {
                                                              setLibraryMembershipFilterUi((prev) => ({ ...prev, category_menu_open: false }));
                                                              handleLibraryMembershipFilterChange('category', val);
                                                           }}
                                                           onSelectTier={(val) => {
                                                              setLibraryMembershipFilterUi((prev) => ({ ...prev, tier_menu_open: false }));
                                                              setLibraryMembershipTier(val);
                                                              setLibraryMembershipContentList([]);
                                                              setLibraryMembershipContentListLength(0);
                                                              setLibraryMembershipPagePosition(0);
                                                              setLibraryMembershipPageN(1);
                                                              setLibraryMembershipInfScroll(true);
                                                              setLibraryMembershipLoading(true);
                                                              loadLibraryMembershipContent(val, 1);
                                                           }}
                                                           onSelectSort={(val) => {
                                                              setLibraryMembershipFilterUi((prev) => ({ ...prev, sort_menu_open: false }));
                                                              handleLibraryMembershipFilterChange('sort', val);
                                                           }}
                                                        />
                                                        <Box sx={{ justifyContent: 'center', margin: '24px auto 20px 0px' }}>
                                                           <SearchBarInfeed
                                                              {...props}
                                                              is_me={props.is_me}
                                                              influencer_username={props.influencer}
                                                              influencer_display_name={props.display_name || props.influencer}
                                                              type={'sub_posts'}
                                                              search_func={handleMembershipSearchResult}
                                                              search_func_collection={() => {}}
                                                           />
                                                        </Box>
                                                     </div>
                                                     {(() => {
                                                        const nPages = Math.ceil(libraryMembershipContentListLength / 20);
                                                        const filteredList = (libraryMembershipContentList || []).filter((item) => {
                                                           if (!libraryMembershipSearchQuery) return true;
                                                           const q = libraryMembershipSearchQuery.toLowerCase();
                                                           const name = (item.name || item.community_post_title || '').toLowerCase();
                                                           const desc = (item.description || item.content_text || '').replace(/<[^>]*>/g, '').toLowerCase();
                                                           return name.includes(q) || desc.includes(q);
                                                        });
                                                        if (filteredList.length === 0 && !libraryMembershipInfScroll) return (
                                                           <div style={{ backgroundColor: '#fff', border: '1px solid #E4EBF6', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
                                                              <p style={{ fontSize: '14px', color: '#9CA3AF' }}>{t('community.library.noContent', 'No content available')}</p>
                                                           </div>
                                                        );
                                                        return (
                                                           <div style={{
                                                              display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                                              width: props.windowWidth <= 600 ? '100%' : '600px',
                                                              maxWidth: props.windowWidth <= 600 ? '100%' : '600px',
                                                              minWidth: props.windowWidth > 1500 ? '600px' : 'auto',
                                                              margin: '0 auto',
                                                           }}>
                                                              {nPages > 1 && (
                                                                 <Grid container alignItems="center" justifyContent="center" style={{ marginTop: '20px', marginBottom: '20px' }}>
                                                                    <StyledPagination
                                                                       count={nPages}
                                                                       page={libraryMembershipPageN}
                                                                       onChange={(event, page) => {
                                                                          handleLibraryMembershipPageChange(page);
                                                                          window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                       }}
                                                                    />
                                                                 </Grid>
                                                              )}
                                                              <InfiniteScroll
                                                                 dataLength={filteredList.length}
                                                                 next={handleLibraryMembershipInfScroll}
                                                                 hasMore={libraryMembershipInfScroll}
                                                                 loader={<div style={{ textAlign: 'center', padding: '20px' }}><CircularProgress size={24} /></div>}
                                                              >
                                                                 {filteredList.map(item => (
                                                                    <div id={`membership-content-${item.id}`} key={`membership-content-${item.id}`} style={{
                                                                       paddingBottom: '30px',
                                                                       width: props.windowWidth <= 600 ? '100%' : '600px',
                                                                       maxWidth: props.windowWidth <= 600 ? '100%' : '600px',
                                                                       minWidth: props.windowWidth > 1500 ? '600px' : 'auto',
                                                                       margin: '0px auto',
                                                                    }}>
                                                                       <CreatorContentCard
                                                                          {...props}
                                                                          {...item}
                                                                          windowWidth={props.windowWidth}
                                                                          is_me={false}
                                                                          influencer={props.influencer}
                                                                          current_user={props.current_user}
                                                                          currency={props.currency}
                                                                          view_type={props.view_type}
                                                                          display_name={props.display_name}
                                                                       />
                                                                    </div>
                                                                 ))}
                                                              </InfiniteScroll>
                                                           </div>
                                                        );
                                                     })()}
                                                  </div>
                                               ) : libraryCoachingView ? (
                                                  <Async
                                                     {...props}
                                                     display_name={props.display_name}
                                                     current_user={props.current_user}
                                                     current_user_icon={props.current_user_icon}
                                                     username={props.current_user}
                                                     is_me={props.is_me}
                                                     influencer={props.influencer}
                                                     asyncActive={props.subscribe_popup_async_active}
                                                     influencer_tiers={props.influencer_tiers}
                                                     active_influencer_tiers={[
                                                        ...(props.active_influencer_tiers || []),
                                                        ...(props.subscribe_popup_async_active && props.subscribe_popup_async_name ? [props.subscribe_popup_async_name] : [])
                                                     ]}
                                                     influencer_info={props.influencer_info}
                                                     currency={props.currency}
                                                     selectedMemberTabOverwrite={props.selectedMemberTabOverwrite}
                                                     post_id={props.post_id}
                                                     async_apply={props.async_apply}
                                                     windowWidth={props.windowWidth}
                                                     setState={props.setState}
                                                     scroll={false}
                                                     current_tier={props.is_me ? 4 : props.subscribe_popup_current_tier}
                                                     async_tier_access={props.is_me ? true : props.async_tier_access}
                                                     hide_offering_card={true}
                                                  />
                                               ) : libraryMessagesView ? (
                                                  <PaidDMs
                                                     {...props}
                                                     current_user={props.current_user}
                                                     display_name={props.display_name}
                                                     current_user_icon={props.current_user_icon}
                                                     username={props.current_user}
                                                     is_me={props.is_me}
                                                     influencer={props.influencer}
                                                     influencer_info={props.influencer_info}
                                                     currency={props.currency}
                                                     post_id={props.post_id}
                                                     dm_active={props.dm_active}
                                                     dmSettingsId={libraryMessagesView?.id}
                                                     windowWidth={Math.max(window.innerWidth, 1280)}
                                                     activeProductData={props.activeProductData}
                                                     setState={props.setState}
                                                     setMaxMessageLength={() => {}}
                                                     maxMessageLength={10000}
                                                     updateHasMessage={() => {}}
                                                     isLibraryView={true}
                                                  />
                                               ) : libraryCommunityView ? (
                                                  <Community
                                                     {...props}
                                                     display_name={props.display_name}
                                                     current_user={props.current_user}
                                                     current_user_icon={props.current_user_icon}
                                                     username={props.current_user}
                                                     is_me={props.is_me}
                                                     influencer={props.influencer}
                                                     influencer_tiers={props.influencer_tiers}
                                                     active_influencer_tiers={props.active_influencer_tiers}
                                                     influencer_info={props.influencer_info}
                                                     currency={props.currency}
                                                     current_tier={props.subscribe_popup_current_tier}
                                                     post_id={props.post_id}
                                                     tab_id={libraryCommunityView?.id}
                                                     hide_tab_switcher={true}
                                                     view_type={props.view_type}
                                                     setState={props.setState}
                                                     windowWidth={props.windowWidth}
                                                     theme={props.theme}
                                                  />
                                               ) : libraryCourseView ? (
                                                   <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                      {/* Desktop: Chapter sidebar */}
                                                      {libraryCourseView.chapters && libraryCourseView.chapters.length > 0 &&
                                                       !((props.is_me === true && props.windowWidth <= 1424)
                                                       || (props.is_me === false && props.windowWidth <= 1100)) && (
                                                         <div style={{
                                                            position: 'absolute', left: '0', top: '0', width: '25%', maxWidth: '250px',
                                                            height: 'fit-content', maxHeight: 'calc(100vh - 100px)', overflowY: 'auto', paddingRight: '20px', zIndex: 1,
                                                         }}>
                                                            <div style={{
                                                               backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #E4EBF6',
                                                               padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', position: 'sticky', top: '80px',
                                                            }}>
                                                               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #E4EBF6' }}>
                                                                  <span style={{ fontSize: '16px', fontWeight: 600, color: '#1D1D1D' }}>
                                                                     {t('community.library.chapters', 'Chapters')}
                                                                  </span>
                                                               </div>
                                                               <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                                  {libraryCourseView.chapters.map(ch => {
                                                                     const isSelected = libraryCourseChapter === ch.chapter_name;
                                                                     return (
                                                                        <div
                                                                           key={ch.id}
                                                                           onClick={() => {
                                                                              setLibraryCourseChapter(ch.chapter_name);
                                                                              setLibraryCourseContentList([]);
                                                                              setLibraryCourseInfScroll(true);
                                                                              loadLibraryCourseContent(libraryCourseView?.course?.name || '', ch.chapter_name, 1);
                                                                           }}
                                                                           style={{
                                                                              padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease',
                                                                              backgroundColor: isSelected ? '#E6EDFB' : 'transparent',
                                                                              border: isSelected ? '1px solid var(--brand-color, #2563EB)' : '1px solid transparent',
                                                                              display: 'flex', alignItems: 'center', gap: '8px',
                                                                           }}
                                                                        >
                                                                           <span style={{ fontSize: '14px', fontWeight: isSelected ? 600 : 500, color: isSelected ? 'var(--brand-color, #2563EB)' : '#1D1D1D', textTransform: 'capitalize' }}>
                                                                              {ch.chapter_name}
                                                                           </span>
                                                                        </div>
                                                                     );
                                                                  })}
                                                                  {libraryCourseView.chapter_content['_uncategorised_'] && libraryCourseView.chapter_content['_uncategorised_'].length > 0 && (
                                                                     <div
                                                                        onClick={() => {
                                                                           setLibraryCourseChapter('_uncategorised_');
                                                                           setLibraryCourseContentList([]);
                                                                           setLibraryCourseInfScroll(true);
                                                                           loadLibraryCourseContent(libraryCourseView?.course?.name || '', '_uncategorised_', 1);
                                                                        }}
                                                                        style={{
                                                                           padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease',
                                                                           backgroundColor: libraryCourseChapter === '_uncategorised_' ? '#E6EDFB' : 'transparent',
                                                                           border: libraryCourseChapter === '_uncategorised_' ? '1px solid var(--brand-color, #2563EB)' : '1px solid transparent',
                                                                        }}
                                                                     >
                                                                        <span style={{ fontSize: '14px', fontWeight: libraryCourseChapter === '_uncategorised_' ? 600 : 500, color: libraryCourseChapter === '_uncategorised_' ? 'var(--brand-color, #2563EB)' : '#1D1D1D' }}>
                                                                           {t('community.library.other', 'Other')}
                                                                        </span>
                                                                     </div>
                                                                  )}
                                                               </div>
                                                            </div>
                                                         </div>
                                                      )}
                                                      {/* Mobile: Chapter dropdown */}
                                                      {libraryCourseView.chapters && libraryCourseView.chapters.length > 0 &&
                                                       ((props.is_me === true && props.windowWidth <= 1424)
                                                       || (props.is_me === false && props.windowWidth <= 1100)) && (
                                                         <div style={{ width: '100%', maxWidth: '600px', margin: '0 auto 20px auto', padding: '0 12px', boxSizing: 'border-box' }}>
                                                            <p style={{ fontSize: '16px', fontWeight: 600, color: '#1D1D1D', marginBottom: '12px' }}>
                                                               {t('community.library.chapters', 'Chapters')}
                                                            </p>
                                                            <FormControl fullWidth sx={{ minWidth: 0 }}>
                                                               <Select
                                                                  value={libraryCourseChapter || ''}
                                                                  onChange={(e) => {
                                                                     const ch = e.target.value;
                                                                     setLibraryCourseChapter(ch);
                                                                     setLibraryCourseContentList([]);
                                                                     setLibraryCourseInfScroll(true);
                                                                     loadLibraryCourseContent(libraryCourseView?.course?.name || '', ch, 1);
                                                                  }}
                                                                  sx={{
                                                                     height: '42px',
                                                                     borderRadius: '8px',
                                                                     fontSize: { xs: '13px', sm: '14px' },
                                                                     fontWeight: 500,
                                                                     textTransform: 'capitalize',
                                                                     backgroundColor: '#fff',
                                                                     '& .MuiSelect-select': {
                                                                        padding: { xs: '10px 32px 10px 12px', sm: '10px 32px 10px 14px' },
                                                                     },
                                                                     '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#E4EBF6',
                                                                     },
                                                                     '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: 'var(--brand-color)',
                                                                     },
                                                                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: 'var(--brand-color)',
                                                                        borderWidth: '1px',
                                                                     },
                                                                  }}
                                                                  MenuProps={{
                                                                     PaperProps: {
                                                                        sx: {
                                                                           maxHeight: '300px',
                                                                           borderRadius: '8px',
                                                                           boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                                           maxWidth: 'calc(100vw - 24px)',
                                                                        },
                                                                     },
                                                                     anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                                     transformOrigin: { vertical: 'top', horizontal: 'left' },
                                                                  }}
                                                               >
                                                                  {libraryCourseView.chapters.map(ch => (
                                                                     <MenuItem key={ch.id} value={ch.chapter_name}>
                                                                        {ch.chapter_name}
                                                                     </MenuItem>
                                                                  ))}
                                                                  {libraryCourseView.chapter_content['_uncategorised_'] && libraryCourseView.chapter_content['_uncategorised_'].length > 0 && (
                                                                     <MenuItem value="_uncategorised_">
                                                                        {t('community.library.other', 'Other')}
                                                                     </MenuItem>
                                                                  )}
                                                               </Select>
                                                            </FormControl>
                                                         </div>
                                                      )}
                                                      {/* Content items */}
                                                      {(() => {
                                                         const nPages = Math.ceil(libraryCourseContentListLength / 20);
                                                         if (libraryCourseContentList.length === 0 && !libraryCourseInfScroll) return (
                                                            <div style={{ backgroundColor: '#fff', border: '1px solid #E4EBF6', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
                                                               <p style={{ fontSize: '14px', color: '#9CA3AF' }}>{t('community.library.noContent', 'No content in this chapter')}</p>
                                                            </div>
                                                         );
                                                         return (
                                                            <div style={{
                                                               display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                                               width: props.windowWidth <= 600 ? '100%' : '600px',
                                                               maxWidth: props.windowWidth <= 600 ? '100%' : '600px',
                                                               minWidth: props.windowWidth > 1500 ? '600px' : 'auto',
                                                               margin: '0 auto',
                                                            }}>
                                                               {nPages > 1 && (
                                                                  <Grid container alignItems="center" justifyContent="center" style={{ marginTop: '20px', marginBottom: '20px' }}>
                                                                     <StyledPagination
                                                                        count={nPages}
                                                                        page={libraryCoursePageN}
                                                                        onChange={(event, page) => {
                                                                           handleLibraryCoursePageChange(page);
                                                                           window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                        }}
                                                                     />
                                                                  </Grid>
                                                               )}
                                                               <InfiniteScroll
                                                                  dataLength={libraryCourseContentList.length}
                                                                  next={handleLibraryCourseInfScroll}
                                                                  hasMore={libraryCourseInfScroll}
                                                                  loader={<div style={{ textAlign: 'center', padding: '20px' }}><CircularProgress size={24} /></div>}
                                                               >
                                                                  {libraryCourseContentList.map(item => (
                                                                     <div key={`course-content-${item.id}`} style={{
                                                                        paddingBottom: '30px',
                                                                        width: props.windowWidth <= 600 ? '100%' : '600px',
                                                                        maxWidth: props.windowWidth <= 600 ? '100%' : '600px',
                                                                        minWidth: props.windowWidth > 1500 ? '600px' : 'auto',
                                                                        margin: '0px auto',
                                                                     }}>
                                                                        <CreatorContentCard
                                                                           {...props}
                                                                           {...item}
                                                                           windowWidth={props.windowWidth}
                                                                           is_me={false}
                                                                           influencer={props.influencer}
                                                                           current_user={props.current_user}
                                                                           currency={props.currency}
                                                                           view_type={props.view_type}
                                                                           display_name={props.display_name}
                                                                        />
                                                                     </div>
                                                                  ))}
                                                               </InfiniteScroll>
                                                               {nPages > 1 && (
                                                                  <Grid container alignItems="center" justifyContent="center" style={{ marginTop: '20px' }}>
                                                                     <StyledPagination
                                                                        count={nPages}
                                                                        page={libraryCoursePageN}
                                                                        onChange={(event, page) => {
                                                                           handleLibraryCoursePageChange(page);
                                                                           window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                        }}
                                                                     />
                                                                  </Grid>
                                                               )}
                                                            </div>
                                                         );
                                                      })()}
                                                   </div>
                                                ) : null}
                                             </div>
                                          ) : libraryItems.length === 0 ? (
                                             <div style={{ backgroundColor: '#fff', border: '1px solid #E4EBF6', borderRadius: '12px', padding: '24px' }}>
                                                <p style={{ fontSize: '14px', color: '#9CA3AF', textAlign: 'center', padding: '20px 0' }}>
                                                   {t('community.noLibraryItems')}
                                                </p>
                                             </div>
                                          ) : (
                                             (() => {
                                                const grouped = libraryItems.reduce((acc, item) => {
                                                   (acc[item.type] = acc[item.type] || []).push(item);
                                                   return acc;
                                                }, {});
                                                const typeConfig = {
                                                   membership: { icon: <TbRosette size={20} />, label: t('community.library.memberships', 'Memberships'), bg: '#DBEAFE', color: '#1E40AF' },
                                                   coaching: { icon: <TbHeartHandshake size={20} />, label: t('community.library.coaching', '1:1 Coaching'), bg: '#FCE7F3', color: '#9D174D' },
                                                   coaching_call: { icon: <TbUsers size={20} />, label: t('community.library.coachingCalls', 'Coaching calls'), bg: '#E0E7FF', color: '#3730A3' },
                                                   webinar: { icon: <TbUsers size={20} />, label: t('community.library.webinars', 'Webinars'), bg: '#E0E7FF', color: '#3730A3' },
                                                   course: { icon: <TbSchool size={20} />, label: t('community.library.courses', 'Courses'), bg: '#FEF3C7', color: '#92400E' },
                                                   digital_product: { icon: <TbPackage size={20} />, label: t('community.library.digitalProducts', 'Digital products'), bg: '#EDE9FE', color: '#5B21B6' },
                                                   community: { icon: <TbSeeding size={20} />, label: t('community.library.communities', 'Communities'), bg: '#D1FAE5', color: '#065F46' },
                                                   messages: { icon: <TbMessageCircleDollar size={20} />, label: t('community.library.customProducts', 'Custom products'), bg: '#FFEDD5', color: '#9A3412' },
                                                   url: { icon: <TbLink size={20} />, label: t('community.library.outsideUrls', 'Outside URLs'), bg: '#E0F2FE', color: '#0369A1' },
                                                };
                                                const contentTypeBadge = (ct) => {
                                                   const map = { vid: 'Video', aud: 'Audio', img: 'Image', pdf: 'PDF', bnd: 'Bundle', txt: 'Text' };
                                                   return map[ct] || '';
                                                };
                                                return Object.entries(grouped).map(([type, items]) => {
                                                   const isExpanded = expandedLibrarySections[type];
                                                   const visibleItems = isExpanded ? items : items.slice(0, 3);
                                                   return (
                                                   <div key={type} style={{ marginBottom: '28px' }}>
                                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                                                         <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ fontSize: '18px', display: 'flex', color: typeConfig[type]?.color || '#374151' }}>{typeConfig[type]?.icon}</span>
                                                            <span style={{ fontSize: '16px', fontWeight: 700, color: '#1D1D1D', fontFamily: 'Roboto, sans-serif' }}>
                                                               {typeConfig[type]?.label || type}
                                                            </span>
                                                            <span style={{ fontSize: '14px', color: '#9CA3AF', fontWeight: 500 }}>{items.length}</span>
                                                         </div>
                                                         {items.length > 3 && (
                                                            <span
                                                               onClick={(e) => { e.stopPropagation(); setExpandedLibrarySections(prev => ({ ...prev, [type]: !prev[type] })); }}
                                                               style={{ fontSize: '13px', fontWeight: 600, color: 'var(--brand-color)', cursor: 'pointer', fontFamily: 'Roboto, sans-serif' }}>
                                                               {isExpanded ? t('community.library.hide', 'Hide') : `${t('community.library.viewAll', 'View all')} \u2192`}
                                                            </span>
                                                         )}
                                                      </div>
                                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                                                         {visibleItems.map((item) => (
                                                            <div
                                                               key={`${item.type}-${item.id}`}
                                                               onClick={() => {
                                                                  if (item.type === 'digital_product') {
                                                                     openLibraryProduct(item.id);
                                                                     window.scrollTo({ top: 0, behavior: 'instant' });
                                                                  }
                                                                  else if (item.type === 'course') { openLibraryCourse(item.id); window.scrollTo({ top: 0, behavior: 'instant' }); }
                                                                  else if (item.type === 'community') { openLibraryCommunity(item); }
                                                                  else if (item.type === 'membership') openLibraryMembership(item);
                                                                  else if (item.type === 'coaching') { openLibraryCoaching(item); window.scrollTo({ top: 0, behavior: 'instant' }); }
                                                                  else if (item.type === 'coaching_call' || item.type === 'webinar') { if (item.url) window.open(item.url, '_blank'); else window.location.assign(`/${props.influencer}?open_url_product=true&product_id=${item.id}`); }
                                                                  else if (item.type === 'messages') { openLibraryMessages(item); }
                                                                  else if (item.type === 'url') { if (item.url) window.open(item.url, '_blank'); else window.location.assign(`/${props.influencer}?open_url_product=true&product_id=${item.id}`); }
                                                               }}
                                                               style={{
                                                                  backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #E4EBF6',
                                                                  cursor: 'pointer', transition: 'all 0.2s', overflow: 'hidden',
                                                               }}
                                                               onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                                               onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
                                                            >
                                                               <div style={{ width: '100%', aspectRatio: '16/9', backgroundColor: '#F3F4F6', position: 'relative', overflow: 'hidden' }}>
                                                                  {item.thumbnail ? (
                                                                     <img src={item.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                  ) : (
                                                                     <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        backgroundColor: typeConfig[item.type]?.bg || '#F3F4F6' }}>
                                                                        <span style={{ color: typeConfig[item.type]?.color || '#374151' }}>
                                                                           {React.cloneElement(typeConfig[item.type]?.icon || <TbPackage size={36} />, { size: 36 })}
                                                                        </span>
                                                                     </div>
                                                                  )}
                                                                  {item.type === 'course' && (item.n_modules > 0 || item.n_lessons > 0) && (
                                                                     <div style={{ position: 'absolute', bottom: '8px', left: '8px', right: '8px', display: 'flex', gap: '6px' }}>
                                                                        {item.n_modules > 0 && <span style={{ backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px' }}>{item.n_modules} {item.n_modules === 1 ? 'chapter' : 'chapters'}</span>}
                                                                        {item.n_lessons > 0 && <span style={{ backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px' }}>{item.n_lessons} {item.n_lessons === 1 ? 'post' : 'posts'}</span>}
                                                                     </div>
                                                                  )}
                                                                  {item.type === 'digital_product' && item.content_type && (
                                                                     <span style={{ position: 'absolute', top: '8px', left: '8px', backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px' }}>
                                                                        {contentTypeBadge(item.content_type)}
                                                                     </span>
                                                                  )}
                                                               </div>
                                                               <div style={{ padding: '12px 14px' }}>
                                                                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#1D1D1D', margin: '0 0 4px 0', fontFamily: 'Roboto, sans-serif',
                                                                     overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.3 }}>
                                                                     {item.name}
                                                                  </p>
                                                                  {item.description && (
                                                                     <p style={{ fontSize: '12px', color: '#6B7280', margin: 0, fontFamily: 'Roboto, sans-serif', lineHeight: 1.4,
                                                                        overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                                                                        {item.description.replace(/<[^>]*>/g, '')}
                                                                     </p>
                                                                  )}
                                                               </div>
                                                            </div>
                                                         ))}
                                                      </div>
                                                   </div>
                                                   );
                                                });
                                             })()
                                          )}
                                       </div>
                                    ) : (
                                    <Grid container>
                                       <Grid xs={12}
                                          style={{
                                             // backgroundColor: 'var(--rebranding-content-background-color)',
                                             height: 'auto',
                                             borderRadius: '12px',
                                             margin: '0px 0px 0px 0px',
                                             border: 'none',
                                          }}>
                                          <div style={{ padding: '0px', boxSizing: 'border-box' }}>
                                             <CommunityComposer
                                                   windowWidth={props.windowWidth}
                                                   currentUser={props.current_user}
                                                   currentUserIcon={props.current_user_icon}
                                                   placeholder={placeholder}
                                                   textValue={newPostText}
                                                   onTextChange={(v) => setNewPostText(v)}
                                                   onTextLengthChange={setNewPostTextLength}
                                                   onAttachImage={handleShareImage}
                                                   selectedPreviewUrl={selectedPreviewUrl}
                                                   fileType={fileType}
                                                   onRemoveAttachment={handleRemoveContent}
                                                   onSubmit={() => submitPost(tabId)}
                                                   scheduleDate={postScheduleDate}
                                                   onScheduleDateChange={setPostScheduleDate}
                                                   disabled={previewReadOnly}
                                                   brandColor={getBrandColor()}
                                                   handleClickVideo={handleClickVideo}
                                                   handleCloseVideo={handleCloseVideo}
                                                   handleShareVideo={handleShareVideo}
                                                   handleClickAudio={handleClickAudio}
                                                   handleCloseAudio={handleCloseAudio}
                                                   handleShareAudio={handleShareAudio}
                                                   handleClickDoc={handleClickDoc}
                                                   handleCloseDoc={handleCloseDoc}
                                                   handleSharePDF={handleSharePDF}
                                                   handleClick={handleClick}
                                                   handleClose={handleClose}
                                                   getDevices={getDevices}
                                                   anchorElVideo={anchorElVideo}
                                                   anchorElAudio={anchorElAudio}
                                                   anchorElDoc={anchorElDoc}
                                                   anchorEl={anchorEl}
                                                   setFileType={setFileType}
                                                   setIsMediaAttached={setIsMediaAttached}
                                                   setSelectedPreviewUrl={setSelectedPreviewUrl}
                                                   setEmbedUrlType={setEmbedUrlType}
                                                   setEmbedUrlIsOpen={setEmbedUrlIsOpen}
                                                   setSnackbarEmbed={setSnackbarEmbed}
                                                   setDocModalOpen={setDocModalOpen}
                                                   setDocType={setDocType}
                                                   setSnackbarOpenDoc={setSnackbarOpenDoc}
                                                   setMeetingModalOpen={setMeetingModalOpen}
                                                   setMeetingType={setMeetingType}
                                                   setSnackbarOpenMeeting={setSnackbarOpenMeeting}
                                                   handleRemoveContent={handleRemoveContent}
                                                   meetingLink={meetingLink}
                                                   setMeetingLink={setMeetingLink}
                                                   docLink={docLink}
                                                   setDocLink={setDocLink}
                                                   embedVideoUrl={embedVideoUrl}
                                                   setEmbedVideoUrl={setEmbedVideoUrl}
                                                   postMeetingTime={postMeetingTime}
                                                   setPostMeetingTime={setPostMeetingTime}
                                                   meetingModalOpen={meetingModalOpen}
                                                   docModalOpen={docModalOpen}
                                                   embedUrlIsOpen={embedUrlIsOpen}
                                                   recordingStatus={recordingStatus}
                                                   setRecordingStatus={setRecordingStatus}
                                                   recordingDurationMins={recordingDurationMins}
                                                   setRecordingDurationMins={setRecordingDurationMins}
                                                   recordingDurationSeconds={recordingDurationSeconds}
                                                   setRecordingDurationSeconds={setRecordingDurationSeconds}
                                                   recordingDevices={recordingDevices}
                                                   setRecordingDevices={setRecordingDevices}
                                                   mediaRecorder={mediaRecorder}
                                                   mediaStream={mediaStream}
                                                   mediaDevices={mediaDevices}
                                                   setADevID={setADevID}
                                                   aDevID={aDevID}
                                                   changingDevice={changingDevice}
                                                   setChangingDevice={setChangingDevice}
                                                   localStream={localStream}
                                                   videoBitsPerSecond={videoBitsPerSecond}
                                                   setVDevID={setVDevID}
                                                   vDevID={vDevID}
                                                   recordedFileFormat={recordedFileFormat}
                                                   setRecordedFileFormat={setRecordedFileFormat}
                                                   setSelectedFile={setSelectedFile}
                                                   setShowPostButton={setShowPostButton}
                                                   embedUrlKey={embedUrlKey}
                                                   is_me={props.is_me}
                                                   handleAudioDeviceChange={handleAudioDeviceChange}
                                                   handleVideoDeviceChange={handleVideoDeviceChange}
                                                   saveFile={saveFile}
                                                   handleFullscreenOpen={handleFullscreenOpen}
                                                   processEmbedUrlSet={processEmbedUrlSet}
                                                   embedUrlType={embedUrlType}
                                                   setEmbedVideoUrlThread={setEmbedVideoUrlThread}
                                                   setEmbedVideoUrlComment={setEmbedVideoUrlComment}
                                                   setEmbedVideoUrlEdit={setEmbedVideoUrlEdit}
                                                   snackbarMsgEmbed={snackbarMsgEmbed}
                                                   snackbarEmbed={snackbarEmbed}
                                                   embedVideoUrlThread={embedVideoUrlThread}
                                                   embedVideoUrlComment={embedVideoUrlComment}
                                                   embedVideoUrlEdit={embedVideoUrlEdit}
                                                   docType={docType}
                                                   activeDocFileType={activeDocFileType}
                                                   setActiveDocFileType={setActiveDocFileType}
                                                   setFileTypeThread={setFileTypeThread}
                                                   setDocLinkThread={setDocLinkThread}
                                                   setFileTypeComment={setFileTypeComment}
                                                   setDocLinkComment={setDocLinkComment}
                                                   setDocLinkEdit={setDocLinkEdit}
                                                   setFileTypeEdit={setFileTypeEdit}
                                          		   snackbarMsgDoc={snackbarMsgDoc}
                                                   snackbarOpenDoc={snackbarOpenDoc}
                                                   fileTypeThread={fileTypeThread}
                                                   fileTypeComment={fileTypeComment}
                                                   fileTypeEdit={fileTypeEdit}
                                                   docLinkThread={docLinkThread}
                                                   docLinkComment={docLinkComment}
                                                   docLinkEdit={docLinkEdit}
                                                   checkDocLink={checkDocLink}
                                                   meetingType={meetingType}
                                                   setMeetingLinkThread={setMeetingLinkThread}
                                                   setMeetingLinkComment={setMeetingLinkComment}
                                                   setMeetingLinkEdit={setMeetingLinkEdit}
                                                   meetingLinkThread={meetingLinkThread}
                                                   meetingLinkComment={meetingLinkComment}
                                                   meetingLinkEdit={meetingLinkEdit}
                                                   snackbarMsgMeeting={snackbarMsgMeeting}
                                                   snackbarOpenMeeting={snackbarOpenMeeting}
                                                   snackbarSeverityMeeting={snackbarSeverityMeeting}
                                                   snackbarColorMeeting={snackbarColorMeeting}
                                                   checkMeetingLink={checkMeetingLink}
                                                   currentTabInfo={currentTabInfo}
                                                   uploadPostTags={uploadPostTags}
                                                   setUploadPostTags={setUploadPostTags}
                                                   addCommunityCategory={addCommunityCategory}
                                                   editCategoryModalOpen={editCategoryModalOpen}
                                                   onEditCategory={(cat) => {
                                                      setEditingCategory(cat);
                                                      setEditCategoryName(cat);
                                                      setEditCategoryModalOpen(true);
                                                      setTimeout(() => {
                                                         setEditCategoryModalAnimating(true);
                                                      }, 10);
                                                   }}
                                                   setMeetingImageEdit={setMeetingImageEdit}
                                                   setMeetingImageFileNameEdit={setMeetingImageFileNameEdit}
                                                   meetingImageEdit={meetingImageEdit}
                                                   meetingImageFileNameEdit={meetingImageFileNameEdit}
                                                   setSelectedFileEdit={setSelectedFileEdit}
                                                   pendingCategories={pendingCategories}
                                                   setPendingCategories={setPendingCategories}
                                             />
                                          </div>
                                       </Grid>
                                       <div style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>{categoriesSection}</div>
                                       {pinned_post_block}

                                       <div style={{ minWidth: '100%' }}>


                                          <div
                                             ref={postsContainerRef}
                                             style={{ minWidth: '100%' }}
                                          >
                                             {community_post_blocks}
                                             {/* Sentinel element for Intersection Observer */}
                                             {scrollHasMore && (
                                                <div 
                                                   ref={scrollSentinelRef}
                                                   style={{
                                                      height: '1px',
                                                      width: '100%',
                                                      marginTop: '20px',
                                                   }}
                                                />
                                             )}
                                             {/* Loading indicator */}
                                             {scrollLoading && scrollHasMore && (
                                                <Box sx={{ display: 'flex', width: '100%', marginTop: '20px' }}>
                                                   <CircularProgress sx={{ margin: 'auto', color: 'var(--brand-color)' }} />
                                                </Box>
                                             )}
                                             {/* End message */}
                                             {!scrollHasMore && nPosts > 0 && (
                                                <p style={{
                                                   fontSize: '14px',
                                                   fontWeight: '500',
                                                   margin: '40px auto 40px auto',
                                                   color: '#6E89AF',
                                                   textAlign: 'center',
                                                   width: '90%',
                                                }}>
                                                   {t('community.reachedEnd')}</p>
                                             )}
                                          </div>
                                       </div>


                                    </Grid>
                                    )}
                                 </div>

                                 {/* Right sidebar - hidden when library tab is active */}
                                 <div style={{
                                    flex: '1',
                                    minWidth: '300px',
                                    maxWidth: '350px',
                                    display: selectedFilter === 'library' ? 'none' : 'flex',
                                    flexDirection: 'column',
                                    gap: '20px'
                                 }}>
                                    {/* Community Info Card */}
                                    <div style={{
                                       backgroundColor: '#fff',
                                       border: '1px solid #E4EBF6',
                                       borderRadius: '12px',
                                       overflow: 'hidden',
                                       boxShadow: '0 1px 4px rgba(0, 0, 0, 0.01)',
                                    }}>
                                       {/* Community Thumbnail */}
                                       {(() => {
                                          const thumb = currentTabInfo?.product_thumbnail || currentTabInfo?.thumbnail || currentTabInfo?.thumbnail_url || currentTabInfo?.thumbnailUrl;
                                          const thumbSrc = thumb && thumb !== 'null' && thumb !== 'null/' && !thumb.endsWith('/fitwith/')
                                             ? (thumb.startsWith('null') ? getImageUrl(thumb) : thumb)
                                             : null;
                                          return thumbSrc ? (
                                             <div style={{ width: '100%', aspectRatio: '1', overflow: 'hidden' }}>
                                                <img src={thumbSrc} alt={currentTabInfo?.name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                             </div>
                                          ) : (
                                             <div style={{
                                                width: '100%',
                                                paddingBottom: '100%',
                                                position: 'relative',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                             }}>
                                             <div style={{
                                                position: 'absolute',
                                                top: 0, left: 0, right: 0, bottom: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '48px',
                                                fontWeight: 'bold',
                                                color: '#fff'
                                             }}>
                                                {currentTabInfo?.name ? currentTabInfo.name.substring(0, 2).toUpperCase() : 'CM'}
                                             </div>
                                             </div>
                                          );
                                       })()}

                                       <div style={{ padding: '16px 20px 0 20px' }}>
                                       {/* Community Title */}
                                       <h3 style={{
                                          fontSize: '18px',
                                          fontWeight: '600',
                                          color: '#1D1D1D',
                                          margin: '0 0 4px 0',
                                          fontFamily: 'Roboto, sans-serif'
                                       }}>
                                          {currentTabInfo?.name || 'Community'}
                                       </h3>

                                       {/* Community URL */}
                                       <p style={{
                                          fontSize: '13px',
                                          color: '#9CA3AF',
                                          margin: '0 0 12px 0',
                                          fontFamily: 'Roboto, sans-serif'
                                       }}>
                                          withme.so/{props.influencer}
                                       </p>

                                       {/* Community Description */}
                                       <CommunityDescription description={currentTabInfo?.description || t('community.preview.defaultDescription')} />

                                       {/* Member Stats */}
                                       <div style={{
                                          display: 'flex',
                                          justifyContent: 'space-around',
                                          alignItems: 'center',
                                          padding: '10px 0 4px 0',
                                          margin: '0 0 2px 0',
                                          borderTop: '1px solid #E4EBF6',
                                       }}>
                                          <div style={{ textAlign: 'center' }}>
                                             <p style={{ fontSize: '14px', fontWeight: '700', color: '#1D1D1D', margin: '0', fontFamily: 'Roboto, sans-serif' }}>
                                                {influencerTotalMembers || 0}
                                             </p>
                                             <p style={{ fontSize: '11px', color: '#9CA3AF', margin: '2px 0 0 0', fontFamily: 'Roboto, sans-serif' }}>
                                                {influencerTotalMembers === 1 ? t('community.library.member', 'Member') : t('community.library.members', 'Members')}
                                             </p>
                                          </div>
                                       </div>

                                       {/* Share Button */}
                                       <Button
                                       onClick={() => {
                                          setShareLinkCopied(false);
                                          setShareModalOpen(true);
                                       }}
                                          variant="contained"
                                          startIcon={<TbShare />}
                                          style={{
                                             width: '100%',
                                             padding: '8px 24px',
                                             borderRadius: '8px',
                                             border: 'none',
                                             color: '#fff',
                                             fontSize: '13px',
                                             fontWeight: '600',
                                             textTransform: 'none',
                                             fontFamily: 'Roboto, sans-serif',
                                             background: getBrandColor() || 'var(--brand-color)',
                                             boxShadow: 'none',
                                             transition: 'all 0.2s ease',
                                             marginBottom: '15px',
                                          }}
                                       >
                                          {t('community.share')}
                                       </Button>
                                       </div>
                                    </div>
      
     {/* Upcoming Events Section */}
     <CommunityUpcomingEvents brandColor={getBrandColor()} events={
                           postData.length === 0 && !hasRealPosts && props.is_me && showExamplePosts && communityEvents.length === 0
                              ? [
                                 { id: 'mock-event-1', community_post_title: t('community.examplePosts.event1Title'), event_time: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000).toISOString(), content_type: 'mzm', link: 'https://zoom.us/j/example', is_example: true },
                                 { id: 'mock-event-2', community_post_title: t('community.examplePosts.event2Title'), event_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), content_type: 'mgm', link: 'https://meet.google.com/example', is_example: true },
                              ]
                              : communityEvents
                        } onAddEvent={scrollToComposerAndOpenMeeting} windowWidth={props.windowWidth} is_me={props.is_me} />

                                    {communitySidebarShareConfig ? (
                                       <div style={{
                                          backgroundColor: '#fff',
                                          border: '1px solid #E4EBF6',
                                          borderRadius: '12px',
                                          padding: '24px',
                                          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.01)',
                                          overflow: 'hidden'
                                       }}>
                                          <p style={{ fontSize: '16px', marginBottom: '12px', fontWeight: '600', color: '#1D1D1D', marginTop: '0px' }}>{t('community.sidebar.shareAndEarn')}</p>
                                          <p style={{
                                             width: '100%',
                                             margin: '0px 0px 16px 0px',
                                             fontWeight: '500',
                                             fontSize: '14px',
                                             color: '#6E89AF',
                                             lineHeight: '20px',
                                          }}>
                                             {t('community.sidebar.shareDescription')}
                                          </p>
                                          <div style={{ display: 'flex' }}>
                                             <ShareEarnButton
                                                productType={communitySidebarShareConfig.productType}
                                                productId={communitySidebarShareConfig.productId}
                                                productName={communitySidebarShareConfig.productName}
                                                influencer={communitySidebarShareConfig.influencer}
                                                hasAccess={true}
                                                affiliateEnabled={currentTabInfo?.affiliate_enabled === true}
                                                buttonVariant="full"
                                                size="small"
                                             />
                                          </div>
                                       </div>
                                    ) : null}

                                    {/* Guidelines Section */}
                                    {/* {!props.is_me && (
                                       <div style={{
                                          backgroundColor: '#fff',
                                          border: '1px solid #E4EBF6',
                                          borderRadius: '16px',
                                          padding: '24px',
                                          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.01)',
                                          overflow: 'hidden'
                                       }}>
                                          <p style={{ fontSize: '16px', marginBottom: '0px', fontWeight: '600', color: '#1D1D1D', marginTop: '0px' }}>{t('community.guidelines')}</p>
                                          <CommunityGuidelines
                                             guidelines={guidelines}
                                             padding_bottom={padding_bottom}
                                          />
                                       </div>
                                    )} */}

                                    {/* Active Posts Section */}
                                          {/* <div style={{
                                       backgroundColor: '#fff',
                                       border: '1px solid #E4EBF6',
                                       borderRadius: '12px',
                                       padding: '24px',
                                       boxShadow: '0 1px 4px rgba(0, 0, 0, 0.01)',
                                       overflow: 'hidden'
                                    }}>
                                       <p style={{ fontSize: '16px', marginBottom: '16px', fontWeight: '600', color: '#1D1D1D', marginTop: '0px' }}>{t('community.sidebar.activePosts')}</p>
                                                <CommunityActiveThreads
                                                   windowWidth={props.windowWidth}
                                                   posts={postData}
                                                   padding_bottom={padding_bottom}
                                                   mobileCallback={
                                                      (post_id) => {
                                                         if ('?page=community' in window.location.href) {
                                                            window.location.assign(
                                                               window.location.href.split('?')[0].split('#')[0] + '?page=community#post' + post_id
                                                            )
                                                         } else if ('?page=preview' in window.location.href) {
                                                            window.location.assign(
                                                               window.location.href.split('?')[0].split('#')[0] + '?page=preview#post' + post_id
                                                            )
                                                         } else {
                                                            window.location.assign(
                                                               window.location.href.split('?')[0].split('#')[0] + '#post' + post_id
                                                            )
                                                         }
                                                      }
                                                   }
                                                   changeTabs={(post_id) => {
                                                      setSelectedFilter('posts')
                                                      scrollToPost(post_id)
                                                   }}
                                                   handleEditingContentModal={(postId, postIndex, currentText) => handleEditingContentModal(postId, postIndex, currentText)}
                                                   current_user={props.current_user}
                                                   is_me={props.is_me}
                                                />
                                          </div> */}


                                          
                                             </div>
                              </motion.div>


                              {/* <NewEvent isOpen={newEventModalOpen} onClose={handleNewEventModalClose} onSave={handleSaveNewEvent} />
                              <EditGuidelines isOpen={editGuidelinesModalOpen} guidelines={guidelines} onClose={handleEditGuidelinesClose} onSave={handleSaveEditGuidelines} /> */}
                           </div>
                           : ''
                     }
                  </>}
                  {/* Share Community Modal */}
                  <Modal
                     open={shareModalOpen}
                     onClose={() => setShareModalOpen(false)}
                     style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                     <div style={{
                        backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
                        width: '90%', maxWidth: '400px', outline: 'none',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                     }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                           <div>
                              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#1D1D1D', fontFamily: 'Roboto, sans-serif' }}>
                                 {t('community.shareModal.title', 'Share community')}
                              </h3>
                              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280', fontFamily: 'Roboto, sans-serif' }}>
                                 {t('community.shareModal.subtitle', 'Invite people to')} {paths.URL}{props.influencer}
                              </p>
                           </div>
                           <span onClick={() => setShareModalOpen(false)} style={{ cursor: 'pointer', color: '#9CA3AF', display: 'flex' }}>
                              <IoClose style={{ fontSize: 20 }} />
                           </span>
                        </div>
                        <div style={{
                           display: 'flex', alignItems: 'center', gap: '8px',
                           backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB',
                           borderRadius: '10px', padding: '10px 14px',
                        }}>
                           <span style={{
                              flex: 1, fontSize: '14px', color: '#374151', fontFamily: 'Roboto, sans-serif',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                           }}>
                              {communityShareUrl}
                           </span>
                           <button
                              onClick={() => {
                                 navigator.clipboard.writeText(communityShareUrl);
                                 setShareLinkCopied(true);
                                 setTimeout(() => setShareLinkCopied(false), 2000);
                              }}
                              style={{
                                 display: 'flex', alignItems: 'center', gap: '6px',
                                 padding: '6px 14px', fontSize: '13px', fontWeight: 600,
                                 color: shareLinkCopied ? '#059669' : '#374151',
                                 backgroundColor: '#fff', border: '1px solid #D1D5DB',
                                 borderRadius: '8px', cursor: 'pointer',
                                 fontFamily: 'Roboto, sans-serif', whiteSpace: 'nowrap',
                                 transition: 'all 0.15s',
                              }}
                           >
                              <TbCopy size={15} />
                              {shareLinkCopied ? t('community.shareModal.copied', 'Copied!') : t('community.shareModal.copy', 'Copy')}
                           </button>
                        </div>
                     </div>
                  </Modal>

                  {/*  Edit Content Name Modal */}
                  <Modal
                     sx={{ backgroundColor: 'transparent', overflow: 'hidden', outline: 'none' }}
                     open={editingContentModal}
                     style={{ display: 'flex', marginRight: '10px', marginLeft: '10px', alignItems: 'center', justifyContent: 'center' }}
                  // closeAfterTransition
                  >
                     <Fade in={editingContentModal}>
                        <Grid
                           container
                           xs={12} md={9} lg={6}

                           alignItems="center"
                           justifyContent="center"
                           direction="column"
                           style={{
                              width: '100vw',
                              minHeight: '100vh',
                              paddingTop: 'calc(30px + 1vw)',
                              paddingBottom: 'calc(30px + 1vw)',
                              overflow: 'auto',
                              margin: 'auto',
                           }}
                        >

                           <Grid sx={{
                              minHeight: '290px', backgroundColor: '#fff', borderRadius: '10px', margin: '0px auto',
                              maxWidth: 'calc(100% - 20px)', width: '500px', overflow: 'auto',
                              boxShadow: '0 5px 25px 0 rgba(35, 40, 105, 0.04)', padding: '25px 20px', position: 'relative',
                           }}>


                              <IoClose
                                 onClick={() => { setEditingContentModal(false) }}
                                 style={{
                                    fontSize: '20px',
                                    fontWeight: '300',
                                    textAlign: 'right',
                                    display: 'flex',
                                    top: '15px',
                                    right: '15px',
                                    position: 'absolute',
                                    padding: '0px',
                                    cursor: 'pointer'
                                 }}
                                 color='#023648'
                              />

                              <Grid
                                 xs={12}
                                 alignItems='center'
                                 justifyContent='center'
                              >
                                 <Typography
                                    style={{
                                       color: 'black',
                                       fontSize: '16px',
                                       // paddingLeft: '10%',
                                       fontWeight: '600',
                                       // paddingTop: '20px',
                                       textTransform: 'none',
                                       textAlign: 'center',
                                       cursor: 'default',
                                       margin: '0px 0px 3px 0px',
                                       padding: '0px',
                                       backgroundColor: 'transparent',
                                       // backgroundColor: 'red',
                                       width: '100%',
                                    }}
                                 >
                                    {t('community.activeThreads.editPostTitle')}
                                 </Typography>
                              </Grid>

                              <Grid xs={12}
                                 container
                                 justifyContent={'center'}
                                 alignItems={'center'}
                              >
                                 <Grid xs={12}
                                    style={{ paddingBottom: '10px', paddingTop: '10px', backgroundColor: 'white', borderRadius: '10px' }}
                                 >
                                    <Suspense fallback={<></>}>
                                       <TextEditor
                                          modalPopupEditor={true}
                                          currentText={editingContentText !== null ? editingContentText : ''}
                                          parentCallback={(evt) => {
                                             if ((editingContentTextLength > 0) && (editingContentTextLength <= 40)) {
                                                setEditingContentText(decode(evt))
                                             }
                                          }}
                                          snackbarCallback={() => { }}
                                          toolbarOptions={['emoji', 'history']}
                                          textEditorTextLength={40}
                                          placeholder={t('common.updateContentName')}
                                          lengthCallback={(length) => setEditingContentTextLength(length)}
                                       /></Suspense>
                                 </Grid>
                              </Grid>

                              <Button
                                 fullWidth
                                 variant="contained"
                                 sx={{
                                    width: '100%', backgroundColor: 'var(--brand-color)', fontSize: '16px',
                                    textTransform: 'none', margin: '5px auto 0px auto', height: '40px',
                                    boxShadow: 'none', fontWeight: '600',
                                    ':hover': {
                                       bgcolor: 'var(--brand-color-hover)',
                                       color: 'white',
                                       boxShadow: 'none',
                                    },
                                 }}
                                 type="submit" value="submit"
                                 onClick={() => {
                                    if (editingContentTextLength > 40 || editingContentTextLength < 1) {
                                       setSnackbarSeverity('error')
                                       setSnackbarOpen(true)
                                       setSnackbarColor('var(--danger-color)')
                                       setSnackbarMsg(t('community.post.titleLengthError'))
                                    } else {
                                       editPostTitle(editingContentTextId, editingContentTextIndex, editingContentText);
                                    }
                                 }}
                              >{t('community.post.update')}</Button>
                           </Grid>
                        </Grid>
                     </Fade>
                  </Modal>

                  {/* Conditional rendered components for both mobile and desktop here */}
                  <SnackbarAlert
                     msg={snackbarMsg}
                     open={snackbarOpen}
                     severity={snackbarType}
                     backgroundColor={snackbarColor}
                     handleClose={() => {
                        setSnackbarOpen(false)
                     }}
                     autoHide={4000}
                  />
                  {
                     isUploading ?
                        <Modal open={isUploading} sx={{ zIndex: '9999', position: 'absolute', width: '100%', height: '100%', overflow: 'hidden' }}>

                           <Box style={modalStyle}
                              sx={{
                                 display: 'flex', width: '100%', height: '100%',
                                 margin: 'auto', zIndex: '9999'
                              }}>
                              <Grid
                                 container
                                 spacing={0}
                                 direction="column"
                                 alignItems="center"
                                 justifyContent="center"
                                 style={{ minHeight: '90vh' }}
                              >
                                 <CircularProgress sx={{
                                    color: 'var(--brand-color)',
                                 }} />

                                 <p style={{
                                    color: 'var(--primary-text-color)', fontSize: 'var(--medium-text)',
                                    fontWeight: '600',
                                 }}>Submitting {uploadingTitle}</p>
                                 <p style={{
                                    color: 'var(--primary-text-color)',
                                    fontWeight: '600',
                                    fontSize: '12px'
                                 }}>
                                    {Math.round(uploadPercent)}%
                                 </p>

                                 <div className="upload_toast onboarding_upload" style={{ marginTop: '160px', display: 'contents' }}>
                                    <LinearProgress style={{ width: '80vw' }} variant="determinate" defaultValue={0}
                                       value={
                                          uploadPercent
                                          //  50
                                       } />
                                    <p style={{ color: '#fff', fontSize: '12px' }}>
                                       {uploadStep} {uploadMsg}
                                    </p>
                                    {uploadMsg !== 'Finished' && timeRemaining < 60 ?
                                       <p style={{ color: '#fff', fontSize: '12px', position: 'absolute', bottom: '30px' }}>
                                          {timeRemaining !== null && uploadPercent !== 0 ?
                                             t('dashboard.main.estimatedTimeSeconds', { seconds: timeRemaining }) : t('dashboard.main.calculatingTime')
                                          }
                                       </p>
                                       : uploadMsg !== 'Finished' &&
                                       <p style={{ color: '#fff', fontSize: '12px', position: 'absolute', bottom: '30px' }}>
                                          {timeRemaining !== null && uploadPercent !== 0 ?
                                             t('dashboard.main.estimatedTimeMinutes', { minutes: Math.floor(timeRemaining / 60), seconds: timeRemaining % 60 }) : t('dashboard.main.calculatingTime')
                                          }
                                       </p>
                                    }
                                 </div>
                              </Grid>
                           </Box>
                        </Modal>
                        :
                        <div>

                        </div>
                  }
               </div>
         }

         <CommunityPostModal
            onEditCategory={(cat) => {
               // If cat is null or 'new', open modal for creating new category
               if (!cat || cat === 'new') {
                  setEditingCategory(null);
                  setEditCategoryName('');
               } else {
                  setEditingCategory(cat);
                  setEditCategoryName(cat);
               }
               setEditCategoryModalOpen(true);
               setTimeout(() => {
                  setEditCategoryModalAnimating(true);
               }, 10);
            }}
            is_me={props.is_me}
            late_payment={props.late_payment}
            stripe_customer_id2={props.stripe_customer_id2}
            open={modalOpen}
            onClose={handleModalClose}
            postData={selectedPostData}
            postIndex={selectedPostIndex}
            openInEditMode={openModalInEditMode}
            windowWidth={props.windowWidth}
            current_username={props.current_user}
            current_user_icon={props.current_user_icon}
            current_subscription_tier={props.influencer_info['current_tier']}
            influencer_tier_names={props.influencer_tiers}
            influencer_name={props.influencer}
            loadAdditionalThreads={loadAdditionalThreads}
            deletePost={deletePost}
            submitThread={submitThread}
            deleteThread={deleteThread}
            submitComment={submitComment}
            deleteComment={deleteComment}
            editPost={editPost}
            editThread={editThread}
            editComment={editComment}
            loadAdditionalComments={loadAdditionalComments}
            postLikeChange={postLikeChange}
            threadLikeChange={threadLikeChange}
            commentLikeChange={commentLikeChange}
            influencer_info={props.influencer_info}
            OpenSubscribePopup={props.OpenSubscribePopup}
            OpenWaitingListPopup={props.OpenWaitingListPopup}
            CloseSubscribePopup={props.CloseSubscribePopup}
            CloseWaitingListPopup={props.CloseWaitingListPopup}
            redirectCallback={(post_id) => window.location.assign(`/login?influencer=${props.influencer}&open_community=${true}&post_id=${post_id}`)}
            resetThreadTextId={resetThreadTextId}
            resetCommentTextId={resetCommentTextId}
            setResetThreadTextId={setResetThreadTextId}
            setResetCommentTextId={setResetCommentTextId}
            resetPostEditTextId={resetPostEditTextId}
            setResetPostEditTextId={setResetPostEditTextId}
            resetThreadEditTextId={resetThreadEditTextId}
            setResetThreadEditTextId={setResetThreadEditTextId}
            resetCommentEditTextId={resetCommentEditTextId}
            setResetCommentEditTextId={setResetCommentEditTextId}
            snackbarMsgOuter={snackbarMsg}
            snackbarOpenOuter={snackbarOpen}
            snackbarColorOuter={snackbarColor}
            setSnackbarOpenOuter={setSnackbarOpen}
            snackbarTypeOuter={snackbarType}
            setSelectedFileThread={setSelectedFileThread}
            setSelectedPreviewUrlThread={setSelectedPreviewUrlThread}
            setSelectedPreviewIdThread={setSelectedPreviewIdThread}
            selectedPreviewIdThread={selectedPreviewIdThread}
            setSelectedPreviewIdComment={setSelectedPreviewIdComment}
            selectedPreviewIdComment={selectedPreviewIdComment}
            setFileTypeThread={setFileTypeThread}
            setShowPostButtonThread={setShowPostButtonThread}
            setIsMediaAttachedThread={setIsMediaAttachedThread}
            setSelectedFileComment={setSelectedFileComment}
            setSelectedPreviewUrlComment={setSelectedPreviewUrlComment}
            setFileTypeComment={setFileTypeComment}
            setShowPostButtonComment={setShowPostButtonComment}
            setIsMediaAttachedComment={setIsMediaAttachedComment}
            isMediaAttachedThread={isMediaAttachedThread}
            selectedFileThread={selectedFileThread}
            selectedPreviewUrlThread={selectedPreviewUrlThread}
            fileTypeThread={fileTypeThread}
            showPostButtonThread={showPostButtonThread}
            isMediaAttachedComment={isMediaAttachedComment}
            selectedFileComment={selectedFileComment}
            selectedPreviewUrlComment={selectedPreviewUrlComment}
            fileTypeComment={fileTypeComment}
            showPostButtonComment={showPostButtonComment}
            embedUrlKeyThread={embedUrlKeyThread}
            embedUrlKeyComment={embedUrlKeyComment}
            isMediaAttachedEdit={isMediaAttachedEdit}
            selectedFileEdit={selectedFileEdit}
            selectedPreviewUrlEdit={selectedPreviewUrlEdit}
            fileTypeEdit={fileTypeEdit}
            setSelectedFileEdit={setSelectedFileEdit}
            setSelectedPreviewUrlEdit={setSelectedPreviewUrlEdit}
            setFileTypeEdit={setFileTypeEdit}
            setIsMediaAttachedEdit={setIsMediaAttachedEdit}
            // Embed modal controls
            setEmbedUrlType={setEmbedUrlType}
            setEmbedUrlIsOpen={setEmbedUrlIsOpen}
            setSnackbarEmbed={setSnackbarEmbed}
            // Doc modal controls
            setDocModalOpen={setDocModalOpen}
            setDocType={setDocType}
            setSnackbarOpenDoc={setSnackbarOpenDoc}
            setActiveDocFileType={setActiveDocFileType}
            // Meeting modal controls
            setMeetingModalOpen={setMeetingModalOpen}
            setMeetingType={setMeetingType}
            setSnackbarOpenMeeting={setSnackbarOpenMeeting}
            setMeetingTimeComment={setMeetingTimeComment}
            snackbarSeverityOuter={snackbarSeverity}
            snackbarPreMsgClick={snackbarPreMsgClick}
            snackbarPostMsgClick={snackbarPostMsgClick}
            snackbarTextClick={snackbarTextClick}
            saveFile={saveFile}
            recordedFileFormat={recordedFileFormat}
            snackbarOpenClick={snackbarOpenClick}
            snackbarSeverityClick={snackbarSeverityClick}
            snackbarColorClick={snackbarColorClick}
            setSnackbarOpenClick={setSnackbarOpenClick}
             setMeetingTimeEditPost={setMeetingTimeEditPost}
             setPostMeetingTime={setPostMeetingTime}
             setUpdateEditPostData={setUpdateEditPostData}
             handleUpdatePinnedPost={handleUpdatePinnedPost}
             handleRemovePinnedPost={handleRemovePinnedPost}
             isPinnedPost={isPinnedPost}
             getBrandColor={getBrandColor}
             editPostTags={editPostTags}
             setEditPostTags={setEditPostTags}
             currentTabInfo={currentTabInfo}
             meetingImageEdit={meetingImageEdit}
             setMeetingImageEdit={setMeetingImageEdit}
             meetingImageFileNameEdit={meetingImageFileNameEdit}
             setMeetingImageFileNameEdit={setMeetingImageFileNameEdit}
             meetingType={meetingType}
           />
         
         {/* Delete Category Confirmation Modal */}
         <Modal
            open={deleteConfirmModalOpen}
            onClose={() => setDeleteConfirmModalOpen(false)}
         >
            <Box sx={{
               position: 'absolute', top: '50%', left: '50%',
               transform: 'translate(-50%, -50%)',
               backgroundColor: '#fff', borderRadius: '16px',
               padding: '32px', maxWidth: '440px', width: '90%',
               boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
               outline: 'none',
            }}>
               <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1D1D1D', mb: 1 }}>
                  {t('community.category.deleteTitle') || 'Delete Category'}
               </Typography>
               <Typography sx={{ fontSize: '14px', color: '#6B7280', mb: 3, lineHeight: 1.6 }}>
                  {t('community.category.deleteWarning', { count: deleteCategoryPostCount, category: categoryToDelete }) || `This category has ${deleteCategoryPostCount} posts. Move them to another category before deleting.`}
               </Typography>
               <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#374151', mb: 1 }}>
                  {t('community.category.moveTo') || 'Move posts to:'}
               </Typography>
               <select
                  value={moveTargetCategory}
                  onChange={(e) => setMoveTargetCategory(e.target.value)}
                  style={{
                     width: '100%', padding: '10px 14px', borderRadius: '10px',
                     border: '1px solid #E4EBF6', fontSize: '14px',
                     fontFamily: 'Roboto, sans-serif', marginBottom: '24px',
                     outline: 'none', color: '#1D1D1D', backgroundColor: '#fff',
                  }}
               >
                  <option value="">{t('community.category.selectCategory') || 'Select a category...'}</option>
                  {(() => {
                     // Combine tags from all sources to get full category list
                     const allCats = new Set();
                     (currentTabInfo?.tags || []).forEach(t => { if (t) allCats.add(t); });
                     (orderedCategories || []).forEach(c => { if (c?.name) allCats.add(c.name); });
                     (currentTabInfo?.category_order || []).forEach(c => { if (c) allCats.add(c); });
                     return [...allCats]
                        .filter(tag => tag !== categoryToDelete)
                        .map(tag => (
                           <option key={tag} value={tag}>{tag.charAt(0).toUpperCase() + tag.slice(1)}</option>
                        ));
                  })()}
               </select>
               <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                     onClick={() => setDeleteConfirmModalOpen(false)}
                     sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 600 }}
                  >
                     {t('common.cancel')}
                  </Button>
                  <Button
                     disabled={!moveTargetCategory}
                     onClick={async () => {
                        try {
                           const form_data = new FormData();
                           form_data.append('tab_id', tabId);
                           form_data.append('source_tag', categoryToDelete);
                           form_data.append('target_tag', moveTargetCategory);
                           await axios({
                              method: 'post',
                              url: backend_url + 'api/influencer/community/move_tag_posts',
                              data: form_data,
                              headers: { 'Content-type': 'application/json' },
                              withCredentials: true,
                           });
                           // Update UI
                           const updated = (currentTabInfo?.tags || []).filter(t => t !== categoryToDelete);
                           setCurrentTabInfo(prev => ({ ...prev, tags: updated }));
                           const updatedOrder = (orderedCategories || []).filter(c => c.name?.toLowerCase() !== categoryToDelete.toLowerCase());
                           setOrderedCategories(updatedOrder);
                           if (selectedCategory === categoryToDelete) {
                              setSelectedCategory('all');
                           }
                           setDeleteConfirmModalOpen(false);
                           handleSnackbar('var(--brand-color)', 'success', t('community.category.deleteSuccess'));
                           // Reload posts
                           changeCategory(selectedCategory === categoryToDelete ? 'all' : selectedCategory);
                        } catch (err) {
                           handleSnackbar('var(--danger-color)', 'error', t('community.category.deleteError') || 'Error moving posts');
                        }
                     }}
                     variant="contained"
                     sx={{
                        textTransform: 'none', fontWeight: 600,
                        backgroundColor: '#EF4444', borderRadius: '10px',
                        '&:hover': { backgroundColor: '#DC2626' },
                        '&:disabled': { backgroundColor: '#FCA5A5', color: '#fff' },
                     }}
                  >
                     {t('community.category.moveAndDelete') || 'Move & Delete'}
                  </Button>
               </Box>
            </Box>
         </Modal>

         {/* Edit Category Modal */}
         {editCategoryModalOpen && createPortal(
            <div
               style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  display: 'flex',
                  alignItems: props.windowWidth < 900 ? 'flex-end' : 'center',
                  justifyContent: 'center',
                  zIndex: 9999,
                  padding: props.windowWidth < 900 ? '0px' : '20px',
                  opacity: editCategoryModalAnimating ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out'
               }}
               onClick={() => {
                  setEditCategoryModalAnimating(false);
                  setTimeout(() => {
                     setEditCategoryModalOpen(false);
                     setEditingCategory(null);
                     setEditCategoryName('');
                  }, 300);
               }}
            >
               <div
                  style={{
                     maxWidth: props.windowWidth < 600 ? '100%' : '500px',
                     width: '100%',
                     height: 'auto',
                     maxHeight: '85vh',
                     backgroundColor: '#fff',
                     borderRadius: props.windowWidth < 900 ? '20px 20px 0px 0px' : '16px',
                     position: 'relative',
                     boxShadow: props.windowWidth < 900 ? '0 -10px 40px rgba(0, 0, 0, 0.15)' : '0 20px 60px rgba(0, 0, 0, 0.15)',
                     overflow: 'hidden',
                     display: 'flex',
                     flexDirection: 'column',
                     transform: editCategoryModalAnimating 
                        ? 'translateY(0)' 
                        : props.windowWidth < 900 
                           ? 'translateY(100%)' 
                           : 'scale(0.8) translateY(50px)',
                     opacity: editCategoryModalAnimating ? 1 : 0,
                     transition: 'all 0.3s ease-out',
                     outline: 'none'
                  }}
                  onClick={(e) => e.stopPropagation()}
               >
                  {/* Close button */}
                  <div
                     style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        cursor: 'pointer',
                        zIndex: 10001,
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        opacity: editCategoryModalAnimating ? 1 : 0,
                        transform: editCategoryModalAnimating ? 'scale(1)' : 'scale(0.8)',
                        transitionDelay: editCategoryModalAnimating ? '0.2s' : '0s'
                     }}
                     onClick={() => {
                        setEditCategoryModalAnimating(false);
                        setTimeout(() => {
                           setEditCategoryModalOpen(false);
                           setEditingCategory(null);
                           setEditCategoryName('');
                        }, 300);
                     }}
                  >
                     <IoClose
                        style={{
                           fontSize: '18px',
                           color: '#666',
                           transition: 'color 0.2s ease'
                        }}
                     />
                  </div>

                  {/* Header section */}
                  <div
                     style={{
                        padding: '25px 25px 20px 25px',
                        background: 'linear-gradient(135deg, rgba(247,249,252,0.8) 0%, rgba(255,255,255,0.4) 100%)',
                        borderBottom: '1px solid #E4EBF6',
                        opacity: editCategoryModalAnimating ? 1 : 0,
                        transform: editCategoryModalAnimating ? 'translateY(0)' : 'translateY(20px)',
                        transition: 'all 0.4s ease-out',
                        transitionDelay: editCategoryModalAnimating ? '0.1s' : '0s'
                     }}
                  >
                     <Typography
                        variant="h5"
                        style={{
                           color: '#1D1D1D',
                           fontWeight: '700',
                           fontSize: props.windowWidth < 600 ? '18px' : '20px',
                           textAlign: 'center',
                           fontFamily: 'Roboto, sans-serif',
                           letterSpacing: '-0.5px',
                           margin: 0
                        }}
                     >
                        {editingCategory ? t('community.category.edit') : t('community.category.createNew')}
                     </Typography>
                  </div>

                  {/* Content section */}
                  <div
                     style={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        padding: '20px 25px 20px 25px',
                        paddingRight: '25px',
                        opacity: editCategoryModalAnimating ? 1 : 0,
                        transform: editCategoryModalAnimating ? 'translateY(0)' : 'translateY(20px)',
                        transition: 'all 0.4s ease-out',
                        transitionDelay: editCategoryModalAnimating ? '0.2s' : '0s',
                        maxHeight: props.windowWidth < 900 ? 'calc(80vh - 90px - 80px)' : 'calc(80vh - 90px - 80px)',
                        position: 'relative',
                        zIndex: 1,
                     }}
                     onClick={(e) => e.stopPropagation()}
                  >
                     <div style={{ marginBottom: '20px' }}>
                        <Typography style={{
                           color: '#333',
                           fontSize: '14px',
                           fontWeight: '600',
                           marginBottom: '8px',
                           fontFamily: 'Roboto, sans-serif',
                           textAlign: 'left'
                        }}>
                           {t('community.category.name')}
                        </Typography>
                        <input
                           type="text"
                           className={'text-input'}
                           style={{
                              display: 'block',
                              fontWeight: '500',
                              fontSize: '15px',
                              padding: '12px 15px',
                              color: '#1D1D1D',
                              borderRadius: '8px',
                              border: '1px solid #C7D1DE',
                              width: '100%',
                              fontFamily: 'Roboto',
                              outline: 'none',
                              boxSizing: 'border-box',
                              position: 'relative',
                              zIndex: 10,
                           }}
                           placeholder={t('community.category.namePlaceholder')} 
                           maxLength={50}
                           value={editCategoryName} 
                           onChange={(event) => {
                              setEditCategoryName(event.target.value);
                           }} 
                           onClick={(e) => e.stopPropagation()}
                           onFocus={(e) => e.stopPropagation()}
                        />
                     </div>

                     <Typography style={{
                        color: '#6D89AF',
                        fontSize: '12px',
                        fontWeight: '600',
                        marginTop: '10px',
                        fontFamily: 'Roboto, sans-serif',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                     }}>
                        <BiInfoCircle style={{ fontSize: '16px' }} />
                        {t('community.category.nameHint')}
                     </Typography>
                  </div>

                  {/* Action button section */}
                  <div
                     style={{
                        padding: '20px 25px 25px 25px',
                        borderTop: '1px solid #E4EBF6',
                        flexShrink: 0,
                        backgroundColor: '#fff',
                        opacity: editCategoryModalAnimating ? 1 : 0,
                        transform: editCategoryModalAnimating ? 'translateY(0)' : 'translateY(20px)',
                        transition: 'all 0.4s ease-out',
                        transitionDelay: editCategoryModalAnimating ? '0.3s' : '0s',
                        position: props.windowWidth < 900 ? 'sticky' : 'relative',
                        bottom: props.windowWidth < 900 ? '0' : 'auto',
                        left: 0,
                        right: 0,
                        zIndex: 2,
                     }}
                  >
                     <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'center' }}>
                        {editingCategory ? (
                           <>
                              <Button
                                 disableRipple
                                 variant="contained"
                                 disabled={!editCategoryName || editCategoryName.trim() === '' || editCategoryName.trim() === editingCategory}
                                 sx={{
                                    backgroundColor: (editCategoryName && editCategoryName.trim() !== '' && editCategoryName.trim() !== editingCategory) ? 'var(--brand-color)' : '#E4EBF6',
                                    color: (editCategoryName && editCategoryName.trim() !== '' && editCategoryName.trim() !== editingCategory) ? 'white' : '#9CA3B4',
                                    fontSize: '14px',
                                    textTransform: 'none',
                                    borderRadius: '12px',
                                    boxShadow: 'none',
                                    fontWeight: '600',
                                    padding: '12px 20px',
                                    border: (editCategoryName && editCategoryName.trim() !== '' && editCategoryName.trim() !== editingCategory) ? '2px solid var(--brand-color)' : '2px solid #E4EBF6',
                                    transition: 'all 0.2s ease-in-out',
                                    cursor: (editCategoryName && editCategoryName.trim() !== '' && editCategoryName.trim() !== editingCategory) ? 'pointer' : 'not-allowed',
                                    flex: 1,
                                    '&:hover': (editCategoryName && editCategoryName.trim() !== '' && editCategoryName.trim() !== editingCategory) ? {
                                       backgroundColor: 'var(--brand-color)',
                                       border: '2px solid var(--brand-color)',
                                       opacity: 0.8,
                                       transform: 'translateY(-1px)',
                                       boxShadow: 'none',
                                    } : {
                                       backgroundColor: '#E4EBF6',
                                       border: '2px solid #E4EBF6',
                                       transform: 'none',
                                       boxShadow: 'none',
                                    },
                                 }}
                                 onClick={async () => {
                                    const trimmedName = editCategoryName.trim();
                                    if (!trimmedName) {
                                       handleSnackbar('var(--danger-color)', 'error', t('community.category.validationError'));
                                       return;
                                    }
                                    
                                    if (trimmedName === editingCategory) {
                                       handleSnackbar('var(--danger-color)', 'error', 'Category name unchanged');
                                       return;
                                    }
                                    
                                    // Check if category already exists
                                    const existingTags = currentTabInfo?.tags || [];
                                    if (existingTags.includes(trimmedName) && trimmedName !== editingCategory) {
                                       handleSnackbar('var(--danger-color)', 'error', t('community.category.alreadyExists'));
                                       return;
                                    }
                                    
                                    editCommunityCategory(editingCategory, trimmedName);
                                    setEditCategoryModalAnimating(false);
                                    setTimeout(() => {
                                       setEditCategoryModalOpen(false);
                                       setEditingCategory(null);
                                       setEditCategoryName('');
                                       handleSnackbar('var(--brand-color)', 'success', t('community.category.updateSuccess'));
                                    }, 300);
                                 }}
                              >{t('community.category.saveChanges')}</Button>
                              
                              <Button
                                 disableRipple
                                 variant="outlined"
                                 sx={{
                                    borderColor: 'var(--danger-color)', 
                                    color: 'var(--danger-color)', 
                                    fontSize: '14px',
                                    textTransform: 'none', 
                                    borderRadius: '12px',
                                    fontWeight: '600',
                                    padding: '12px 20px',
                                    flex: 1,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                       backgroundColor: 'var(--danger-color)',
                                       color: 'white',
                                       borderColor: 'var(--danger-color)',
                                       transform: 'translateY(-1px)',
                                    },
                                 }}
                                 onClick={() => {
                                    deleteCommunityCategory(editingCategory);
                                    setEditCategoryModalAnimating(false);
                                    setTimeout(() => {
                                       setEditCategoryModalOpen(false);
                                       setEditingCategory(null);
                                       setEditCategoryName('');
                                    }, 300);
                                 }}
                              >{t('community.category.delete')}</Button>
                           </>
                        ) : (
                           <Button
                              disableRipple
                              fullWidth
                              variant="contained"
                              disabled={!editCategoryName || editCategoryName.trim() === ''}
                              sx={{
                                 backgroundColor: (editCategoryName && editCategoryName.trim() !== '') ? 'var(--brand-color)' : '#E4EBF6',
                                 color: (editCategoryName && editCategoryName.trim() !== '') ? 'white' : '#9CA3B4',
                                 fontSize: '14px',
                                 textTransform: 'none',
                                 borderRadius: '12px',
                                 boxShadow: 'none',
                                 fontWeight: '600',
                                 padding: '12px 20px',
                                 border: (editCategoryName && editCategoryName.trim() !== '') ? '2px solid var(--brand-color)' : '2px solid #E4EBF6',
                                 transition: 'all 0.2s ease-in-out',
                                 cursor: (editCategoryName && editCategoryName.trim() !== '') ? 'pointer' : 'not-allowed',
                                 '&:hover': (editCategoryName && editCategoryName.trim() !== '') ? {
                                    backgroundColor: 'var(--brand-color)',
                                    border: '2px solid var(--brand-color)',
                                    opacity: 0.8,
                                    transform: 'translateY(-1px)',
                                    boxShadow: 'none',
                                 } : {
                                    backgroundColor: '#E4EBF6',
                                    border: '2px solid #E4EBF6',
                                    transform: 'none',
                                    boxShadow: 'none',
                                 },
                              }}
                              onClick={async () => {
                                 const trimmedName = editCategoryName.trim();
                                 if (!trimmedName) {
                                    handleSnackbar('var(--danger-color)', 'error', t('community.category.validationError'));
                                    return;
                                 }
                                 
                                 // Check if category already exists or is pending
                                 const existingTags = currentTabInfo?.tags || [];
                                 const isPending = pendingEditCategories && pendingEditCategories.length > 0 && pendingEditCategories[0] === trimmedName;
                                 if (existingTags.includes(trimmedName) || isPending) {
                                    handleSnackbar('var(--danger-color)', 'error', t('community.category.alreadyExists'));
                                    return;
                                 }
                                 
                                 // If opened from the "+" button (no editing context), create the category directly via API
                                 if (!editingCategory) {
                                    try {
                                       await addCommunityCategory(trimmedName);
                                       handleSnackbar('var(--brand-color)', 'success', t('community.category.created') || 'Category created');
                                    } catch (err) {
                                       handleSnackbar('var(--danger-color)', 'error', 'Failed to create category');
                                       return;
                                    }
                                 } else {
                                    // Add to pending edit categories (will be created when post edit is submitted)
                                    setPendingEditCategories([trimmedName]);
                                    // Automatically select the new category for the edit post
                                    setEditPostTags([trimmedName]);
                                 }

                                 setEditCategoryModalAnimating(false);
                                 setTimeout(() => {
                                    setEditCategoryModalOpen(false);
                                    setEditingCategory(null);
                                    setEditCategoryName('');
                                 }, 300);
                              }}
                           >
                              {t('community.category.create')}
                           </Button>
                        )}
                     </div>
                  </div>
               </div>
            </div>,
            document.body
         )}
      </div>
      </>
   );
}

export default Community;
